/**
 * Ads Tools dashboard.
 *
 * Operator-facing view for Google Ads keyword research, campaign creation,
 * weekly management, monthly recaps, budget reallocation, cluster refresh,
 * and competitive diagnostics.
 *
 * Tenant context comes from useWorkspaceStore.currentTenant per the
 * project's bridge pattern (workspace.website == tenant_id).
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Activity,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clipboard,
  Clock,
  ExternalLink,
  Hammer,
  Loader2,
  Megaphone,
  Play,
  RotateCcw,
  Search,
  Sparkles,
  Target,
  Trash2,
  TriangleAlert,
  XCircle,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { stickyToast } from '@/features/ads/stickyToast';
import { SettingRow } from '@/features/ads/campaign-builder/SettingRow';
import {
  BIDDING_STRATEGY_COPY,
  BIDDING_STRATEGY_INTRO,
  BUDGET_ALLOCATION_COPY,
  BUDGET_ALLOCATION_INTRO,
  CAMPAIGN_GOAL_COPY,
  CAMPAIGN_GOAL_INTRO,
  CAMPAIGN_STRUCTURE_COPY,
  CAMPAIGN_STRUCTURE_INTRO,
  CAMPAIGN_TYPE_COPY,
  CAMPAIGN_TYPE_INTRO,
  CLUSTER_DEFINITION,
} from '@/features/ads/campaign-builder/campaignBuilderCopy';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  FilterBuilder,
  SortableTh,
  TablePagination,
  applyFilterRules,
  useColumnResize,
  usePagination,
  useTableSort,
  type FilterColumn,
  type FilterConjunction,
  type FilterRule,
  type SortAccessors,
} from '@/components/ui/data-table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useBrandBible } from '@/hooks/useBrandBible';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { useTenantStore } from '@/stores/useTenantStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useAdsViewStore } from '@/stores/useAdsViewStore';
import { useClientCapabilities } from '@/hooks/useClientCapabilities';
import { resolveGoogleAdsGate } from '@/components/google-ads/googleAdsGate';
import { NoGoogleAdsAccountState } from '@/components/google-ads/NoGoogleAdsAccountState';
import { GoogleAdsCheckingState } from '@/components/google-ads/GoogleAdsCheckingState';
import { apiService } from '@/services/api';
import AdsReportsTab from '@/components/google-ads/AdsReportsTab';
import { detectAccessibleGoogleAdsCustomers } from '@/services/googleAdsOAuthService';
import {
  applyApprovedRequest,
  getRun,
  getTenantWeeklyMode,
  listRuns,
  listWeeklyApprovals,
  resolveApproval,
  sendApprovalToSlack,
  setTenantWeeklyMode,
  triggerRun,
  type TenantWeeklyMode,
  type WeeklyApprovalRow,
  type WeeklyMode,
  type WeeklyRunDetail,
  type WeeklyRunStatus,
  type WeeklyRunSummary,
} from '@/services/googleAdsWeeklyService';
import { operatorQueueCountsQueryKey } from '@/services/googleAdsOperatorQueueService';
import {
  approveCampaignRun,
  cancelCampaignRun,
  dailyBudgetMicros,
  deleteLiveCampaign,
  describeCampaignRunError,
  describeCampaignSetupError,
  getCampaignRun,
  getCampaignRunAuditEvidence,
  keywordResearchCampaignHandoffKey,
  listAdGroupPerformance,
  listAdPerformance,
  listKeywordPerformance,
  listCampaignRuns,
  listLiveCampaigns,
  regenerateCampaignRunAd,
  rejectCampaignRun,
  refreshCampaignRunReadback,
  setupCampaign,
  submitCampaignRunAds,
  updateLiveCampaignBudget,
  updateLiveCampaignStatus,
  updateAdStatus,
  type AdsMetricsWindow,
  type AdsPerformanceMetrics,
  type AdPerformanceItem,
  type KeywordPerformanceItem,
  type CampaignBiddingStrategy,
  type CampaignAuditEvidenceResponse,
  type CampaignGoal,
  type CampaignPolicyCheck,
  type CampaignRunCluster,
  type CampaignGoogleAdsReadback,
  type CampaignRunKeyword,
  type KeywordResearchCampaignHandoff,
  type KeywordResearchCampaignHandoffKeyword,
  type CampaignRunResponse,
  type CampaignRunState,
  type LiveCampaignInventoryItem,
  type RsaDraft,
  getPmaxImageReadiness,
} from '@/services/googleAdsCampaignsService';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MonthlyRecapsTab } from '@/components/google-ads/MonthlyRecapsTab';
import { BudgetReallocationTab } from '@/components/google-ads/BudgetReallocationTab';
import { ClusterRefreshTab } from '@/components/google-ads/ClusterRefreshTab';
import { CompetitiveTab } from '@/components/google-ads/CompetitiveTab';
import { AdsManagementTab } from '@/components/google-ads/AdsManagementTab';
import { AccountAuditTab } from '@/components/google-ads/AccountAuditTab';
import { CampaignQaTab } from '@/components/google-ads/CampaignQaTab';
import { WeeklyFleetBoard } from '@/components/google-ads/WeeklyFleetBoard';
import { KpiTargetsCard } from '@/components/google-ads/KpiTargetsCard';
import { KpiDiagnosticsTab } from '@/components/google-ads/KpiDiagnosticsTab';
import { RsaEditor } from '@/components/google-ads/RsaEditor';
import { SearchTermsNegativesTab } from '@/components/google-ads/SearchTermsNegativesTab';
import { BudgetPushQueueCard } from '@/components/google-ads/BudgetPushQueueCard';
import {
  ManagementCategoryBar,
  useManagementTabFromUrl,
} from '@/features/ads/management/ManagementCategoryBar';
import { ManagementTabBody } from '@/features/ads/management/ManagementTabBody';
import { ManagementAccountCaveats } from '@/features/ads/management/ManagementAccountCaveats';
import { CampaignDrilldown } from '@/features/ads/management/CampaignDrilldown';
import { CampaignSettingsPage } from '@/features/ads/campaign-settings/CampaignSettingsPage';
import { PmaxPage } from '@/features/ads/pmax/PmaxPage';
import { PerformancePage } from '@/features/ads/performance/PerformancePage';
import { UnderspendFlagBanner } from '@/features/ads/client-dashboard/UnderspendFlagBanner';
import { AdsWaitState } from '@/components/google-ads/AdsWaitState';
import { PlacementReviewTab } from '@/components/google-ads/PlacementReviewTab';
import { useResearchRun } from '@/hooks/useKeywordResearchHistory';
import {
  CampaignBuildingLoader,
  CampaignBuildingOverlay,
} from '@/components/google-ads/CampaignBuildingLoader';
import {
  AdCampaignClusterResultsPanel,
  AdCampaignKeywordsPanel,
  AdCampaignNegativeKeywordsPanel,
  SponsoredAdsLookupPanel,
} from '@/components/keyword-research/AdCampaignKeywordsPanel';
import { KeywordResearchHistoryPanel } from '@/components/keyword-research/KeywordResearchHistoryPanel';
import { ACTIVE_ADS_PLATFORM } from '@/lib/adsPlatform';
import type { BrandBible } from '@/types/tenant';

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: WeeklyRunStatus | string }) {
  const variants: Record<string, { className: string; icon: JSX.Element }> = {
    success: {
      className: 'bg-teal-light text-teal-dark border-teal/30',
      icon: <CheckCircle2 className="h-3 w-3" />,
    },
    partial: {
      className: 'bg-coral-light text-coral-dark border-coral/30',
      icon: <TriangleAlert className="h-3 w-3" />,
    },
    failed: {
      className: 'bg-destructive/10 text-destructive border-destructive/30',
      icon: <TriangleAlert className="h-3 w-3" />,
    },
    running: {
      className: 'bg-warm-bg text-foreground border-border',
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
    },
    skipped: {
      className: 'bg-warm-bg text-muted-foreground border-border',
      icon: <Clock className="h-3 w-3" />,
    },
    pending: {
      className: 'bg-warm-bg text-muted-foreground border-border',
      icon: <Clock className="h-3 w-3" />,
    },
    approved: {
      className: 'bg-teal-light text-teal-dark border-teal/30',
      icon: <CheckCircle2 className="h-3 w-3" />,
    },
  };
  const v = variants[status] ?? variants.pending;
  return (
    <Badge variant="outline" className={`gap-1 ${v.className}`}>
      {v.icon}
      {status}
    </Badge>
  );
}

function ModeBadge({ mode }: { mode: string }) {
  const colorByMode: Record<string, string> = {
    warn: 'bg-warm-bg text-foreground',
    soft_auto: 'bg-coral-light text-coral-dark',
    hard_auto: 'bg-teal-light text-teal-dark',
  };
  return (
    <Badge variant="outline" className={colorByMode[mode] ?? ''}>
      {mode}
    </Badge>
  );
}

const CAMPAIGN_POLICY_PREVIEW: CampaignPolicyCheck[] = [
  {
    id: 'launch_paused',
    label: 'Campaign launches paused',
    status: 'pass',
    severity: 'info',
    evidence: 'Launch status is locked to PAUSED.',
  },
  {
    id: 'bidding_strategy',
    label: 'Bidding strategy',
    status: 'pass',
    severity: 'info',
    evidence: 'Defaults to Maximize Conversions with operator override.',
  },
  {
    id: 'conversion_tracking_gate',
    label: 'Lead-gen conversion tracking',
    status: 'warning',
    severity: 'warning',
    evidence:
      'Lead-gen and ROI goals require a qualified primary conversion action before campaign creation.',
  },
  {
    id: 'smart_bidding_history',
    label: 'Smart bidding conversion history',
    status: 'warning',
    severity: 'warning',
    evidence: 'Maximize Conversions requires 30 conversions in the last 30 days.',
  },
  {
    id: 'presence_targeting',
    label: 'Presence-only location targeting',
    status: 'pass',
    severity: 'info',
    evidence: 'Campaign criteria use presence-based local targeting.',
  },
  {
    id: 'phrase_match',
    label: 'Phrase-match keyword criteria',
    status: 'pass',
    severity: 'info',
    evidence: 'Generated ad-group keywords default to PHRASE match.',
  },
  {
    id: 'safe_recommendations',
    label: 'Safe recommendation policy',
    status: 'pass',
    severity: 'info',
    evidence: 'Only housekeeping recommendations are eligible.',
  },
  {
    id: 'budget_floor',
    label: 'Budget supports expected click volume',
    status: 'warning',
    severity: 'warning',
    evidence: 'Validated after keyword CPC data is available.',
  },
];

const CAMPAIGN_PACING_DAYS = 30.4;
const CAMPAIGN_MIN_CLICKS_PER_DAY = 10;
const CAMPAIGN_DAILY_SPEND_CAP_MULTIPLIER = 2;
const MICROS_PER_USD = 1_000_000;

// Budget unit overlay: `monthly_budget_usd` stays the canonical monthly string
// used by all downstream code. These helpers derive the daily display value and
// convert a daily entry back to the canonical monthly value (Google's 30.4-day
// average — the same divisor used in dailyBudgetMicros).
function monthlyToDailyDisplay(monthlyStr: string): string {
  if (monthlyStr.trim() === '') return '';
  const monthly = Number.parseFloat(monthlyStr);
  if (!Number.isFinite(monthly)) return '';
  const daily = monthly / CAMPAIGN_PACING_DAYS;
  // Trimmed 2-decimal string; whole numbers drop the ".00" (1520 -> "50").
  return String(Math.round(daily * 100) / 100);
}

/** Money hint for the budget derived-value line: thousands separators, cents
 *  only when non-round, em-dash for non-positive. */
function budgetHintUsd(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '—';
  const rounded = Math.round(value * 100) / 100;
  return `$${rounded.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

function dailyToMonthlyCanonical(dailyStr: string): string {
  if (dailyStr.trim() === '') return '';
  const daily = Number.parseFloat(dailyStr);
  if (!Number.isFinite(daily)) return '';
  return String(Math.round(daily * CAMPAIGN_PACING_DAYS * 100) / 100);
}
const SMART_BIDDING_OVERRIDE_ACK = 'I ACCEPT SMART BIDDING RISK';
const SMART_BIDDING_THRESHOLDS: Partial<Record<CampaignBiddingStrategy, number>> = {
  MAXIMIZE_CONVERSIONS: 30,
  TARGET_CPA: 50,
  TARGET_ROAS: 100,
};

type CampaignBudgetAllocationStrategy =
  | 'EVEN_BY_AD_GROUP'
  | 'WEIGHTED_BY_CPC_VOLUME';

type CampaignStructure =
  | 'SINGLE_CAMPAIGN'
  | 'CAMPAIGN_PER_CLUSTER';

/**
 * Collapsible section wrapper for long tables/lists (default expanded).
 * The header stays visible when collapsed; `actions` (badges, selects)
 * render outside the toggle button so they remain interactive.
 *
 * Pass `open` + `onOpenChange` to drive it from the parent — the campaign
 * accordion (#954) needs to know when a row opens so it can fetch that
 * campaign's ad groups only then. Omit them and behaviour is exactly what it
 * always was: uncontrolled and expanded by default.
 */
function CollapsibleSection({
  header,
  actions,
  children,
  className,
  contentClassName,
  actionsClassName,
  defaultOpen = true,
  open,
  onOpenChange,
}: {
  header: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  /** Escape hatch for the default `shrink-0` on the actions cluster — a row
   *  with three buttons has to be allowed to wrap on a narrow viewport
   *  instead of pushing the whole card wider than the page. */
  actionsClassName?: string;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  // Never hand Radix both `open` and `defaultOpen` — one or the other decides
  // whether the section is controlled.
  const openProps = open === undefined ? { defaultOpen } : { open };
  return (
    <Collapsible {...openProps} onOpenChange={onOpenChange} className={className}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="group flex min-w-0 flex-1 items-start gap-2 text-left"
          >
            <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=closed]:-rotate-90" />
            <div className="min-w-0 flex-1">{header}</div>
          </button>
        </CollapsibleTrigger>
        {actions ? (
          <div className={cn('flex shrink-0 flex-wrap items-center gap-2', actionsClassName)}>
            {actions}
          </div>
        ) : null}
      </div>
      <CollapsibleContent className={contentClassName}>{children}</CollapsibleContent>
    </Collapsible>
  );
}

function PolicyChecklist({
  checks,
  title = 'Setup Criteria',
}: {
  checks: CampaignPolicyCheck[];
  title?: string;
}) {
  if (!checks.length) return null;

  return (
    <div className="rounded border border-border bg-warm-bg p-4">
      <CollapsibleSection
        header={<div className="text-sm font-semibold">{title}</div>}
        actions={
          <Badge variant="outline" className="text-[10px]">
            {checks.length} checks
          </Badge>
        }
        contentClassName="pt-3"
      >
      <div className="grid gap-2 md:grid-cols-2">
        {checks.map((check) => {
          const isFail = check.status === 'fail';
          const isWarning = check.status === 'warning';
          const Icon = isFail || isWarning ? TriangleAlert : CheckCircle2;
          const className = isFail
            ? 'border-destructive/30 bg-destructive/5 text-destructive'
            : isWarning
              ? 'border-coral/30 bg-coral-light text-coral-dark'
              : 'border-teal/30 bg-teal-light text-teal-dark';
          return (
            <div key={check.id} className={`overflow-hidden rounded border p-3 ${className}`}>
              <div className="flex items-start gap-2">
                <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{check.label}</div>
                  <div className="mt-1 text-xs opacity-90 [overflow-wrap:anywhere]">
                    {check.evidence}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </CollapsibleSection>
    </div>
  );
}

function GoogleAdsReadbackPanel({
  readback,
  isRefreshing = false,
  onRefresh,
}: {
  readback?: CampaignGoogleAdsReadback | null;
  isRefreshing?: boolean;
  onRefresh?: () => void;
}) {
  if (!readback) return null;

  const status = String(readback.status || 'warning').toLowerCase();
  const checks = Array.isArray(readback.checks) ? readback.checks : [];
  const summary = readback.summary ?? {};
  const statusClass = googleAdsReadbackStatusClassName(status);

  return (
    <div className={`rounded border p-4 text-sm ${statusClass}`}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-semibold">Google Ads API read-back</div>
          <div className="mt-0.5 text-xs opacity-90">
            Checked {formatCampaignRunDate(readback.checked_at)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onRefresh ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-card"
              disabled={isRefreshing}
              onClick={onRefresh}
            >
              {isRefreshing ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="mr-1 h-3.5 w-3.5" />
              )}
              Refresh
            </Button>
          ) : null}
          <Badge variant="outline" className="bg-card">
            {googleAdsReadbackStatusLabel(status)}
          </Badge>
        </div>
      </div>
      <div className="grid gap-2 text-xs sm:grid-cols-3 lg:grid-cols-6">
        <GoogleAdsReadbackCount label="Campaigns" value={summary.campaigns} />
        <GoogleAdsReadbackCount label="Ad groups" value={summary.ad_groups} />
        <GoogleAdsReadbackCount label="Keywords" value={summary.keywords} />
        <GoogleAdsReadbackCount label="RSAs" value={summary.rsas} />
        <GoogleAdsReadbackCount
          label="Negative lists"
          value={summary.negative_shared_sets}
        />
        <GoogleAdsReadbackCount
          label="Conversions"
          value={summary.conversion_actions}
        />
      </div>
      {checks.length > 0 ? (
        <div className="mt-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide opacity-75">
            Read-back checks
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {checks.map((check, index) => {
              const checkStatus = String(check.status || 'warning').toLowerCase();
              return (
                <div
                  key={`${check.id ?? 'readback'}-${index}`}
                  className="rounded border border-current/20 bg-card/70 px-3 py-2"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-medium">{check.label || check.id || 'Read-back check'}</div>
                    <Badge variant="outline" className="bg-card text-[10px] uppercase">
                      {googleAdsReadbackStatusLabel(checkStatus)}
                    </Badge>
                  </div>
                  {check.evidence ? (
                    <div className="mt-0.5 text-xs opacity-90 [overflow-wrap:anywhere]">
                      {check.evidence}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded border border-current/20 bg-card/70 px-3 py-2 text-xs">
          All read-back checks passed.
        </div>
      )}
    </div>
  );
}

function CampaignAuditEvidencePanel({
  runId,
  tenantId,
  enabled,
}: {
  runId: string;
  tenantId: string;
  enabled: boolean;
}) {
  const audit = useQuery({
    queryKey: ['google-ads-campaign-run-audit-evidence', runId, tenantId],
    queryFn: () => getCampaignRunAuditEvidence({ runId, tenantId }),
    enabled,
    refetchOnWindowFocus: false,
  });
  if (!enabled) return null;

  const data = audit.data;
  const statusEntries = data ? Object.entries(data.by_status ?? {}) : [];
  const operationEntries = data
    ? Object.entries(data.by_operation_status ?? {})
    : [];
  const errorRowCount = data?.by_status?.error ?? 0;
  const visibleRows = data?.rows?.slice(0, 10) ?? [];
  const copyEvidence = async (evidence: CampaignAuditEvidenceResponse) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(evidence, null, 2));
      toast.success('Audit evidence copied');
    } catch {
      toast.error('Could not copy audit evidence');
    }
  };

  return (
    <div className="rounded border border-border bg-warm-bg p-4 text-sm">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-semibold">Audit evidence</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Sanitized Google Ads write rows for this campaign run window —
            window-scoped, not run-scoped: a row from a different, genuinely
            concurrent write on this tenant can appear here too.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-card"
            disabled={audit.isFetching}
            onClick={() => audit.refetch()}
          >
            {audit.isFetching ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
            )}
            Refresh
          </Button>
          {data ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-card"
              onClick={() => copyEvidence(data)}
            >
              <Clipboard className="mr-1 h-3.5 w-3.5" />
              Copy JSON
            </Button>
          ) : null}
        </div>
      </div>

      {audit.isLoading ? (
        <div className="flex items-center gap-2 rounded border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading audit evidence...
        </div>
      ) : audit.isError ? (
        <div className="rounded border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          Audit evidence unavailable: {describeCampaignSetupError(audit.error)}
        </div>
      ) : data ? (
        <div className="space-y-3">
          <div className="grid gap-2 text-xs md:grid-cols-4">
            <GoogleAdsReadbackCount label="Audit rows" value={data.audit_total} />
            <GoogleAdsReadbackCount
              label="Error rows"
              value={errorRowCount}
            />
            <div className="rounded border border-current/20 bg-card/70 px-3 py-2">
              <div className="font-medium opacity-80">Max CPC micros</div>
              <div className="mt-1 text-lg font-semibold tabular-nums">
                {typeof data.max_cpc_bid_ceiling_micros === 'number'
                  ? data.max_cpc_bid_ceiling_micros.toLocaleString()
                  : '—'}
              </div>
            </div>
            <div className="rounded border border-current/20 bg-card/70 px-3 py-2">
              <div className="font-medium opacity-80">Customer</div>
              <div className="mt-1 text-lg font-semibold tabular-nums">
                {data.customer_id_ending ? `…${data.customer_id_ending}` : '—'}
              </div>
            </div>
          </div>
          <div className="grid gap-2 text-xs md:grid-cols-2">
            <div className="rounded border border-border bg-card px-3 py-2">
              <div className="font-medium">Status rollup</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {statusEntries.length > 0 ? (
                  statusEntries.map(([status, count]) => (
                    <Badge key={status} variant="outline">
                      {status}: {count}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground">No rows found</span>
                )}
              </div>
            </div>
            <div className="rounded border border-border bg-card px-3 py-2">
              <div className="font-medium">Operation rollup</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {operationEntries.length > 0 ? (
                  operationEntries.map(([operation, count]) => (
                    <Badge key={operation} variant="outline">
                      {operation}: {count}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground">No rows found</span>
                )}
              </div>
            </div>
          </div>
          {visibleRows.length > 0 ? (
            <div className="overflow-x-auto rounded border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Operation</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Audit row</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{row.operation}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell className="max-w-[220px] truncate font-mono text-xs">
                        {row.actor || '—'}
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate text-xs">
                        {row.resource_type || '—'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {formatCampaignRunDate(row.created_at)}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{row.id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function GoogleAdsReadbackCount({
  label,
  value,
}: {
  label: string;
  value: number | null | undefined;
}) {
  return (
    <div className="rounded border border-current/20 bg-card/70 px-3 py-2">
      <div className="font-medium opacity-80">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">
        {typeof value === 'number' ? value : '—'}
      </div>
    </div>
  );
}

function googleAdsReadbackNeedsRefresh(
  readback?: CampaignGoogleAdsReadback | null,
): boolean {
  if (!readback) return true;
  const status = String(readback.status || '').toLowerCase();
  if (status === 'pass') return false;
  if (status === 'warning' || status === 'error') return true;
  const checks = Array.isArray(readback.checks) ? readback.checks : [];
  return checks.some((check) => {
    const checkStatus = String(check.status || '').toLowerCase();
    return checkStatus && checkStatus !== 'pass';
  });
}

function googleAdsReadbackStatusLabel(status: string): string {
  if (status === 'pass') return 'Pass';
  if (status === 'error') return 'Error';
  if (status === 'warning') return 'Review';
  return status || 'Review';
}

function googleAdsReadbackStatusClassName(status: string): string {
  if (status === 'pass') return 'border-teal/30 bg-teal-light text-teal-dark';
  if (status === 'error') return 'border-destructive/30 bg-destructive/5 text-destructive';
  return 'border-coral/30 bg-coral-light/30 text-coral-dark';
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const absDiffMs = Math.abs(diffMs);
  const suffix = diffMs >= 0 ? 'ago' : 'from now';
  if (absDiffMs < 60_000) return diffMs >= 0 ? 'just now' : 'in <1m';
  if (absDiffMs < 3_600_000) {
    const minutes = Math.round(absDiffMs / 60_000);
    return diffMs >= 0 ? `${minutes}m ago` : `in ${minutes}m`;
  }
  if (absDiffMs < 86_400_000) {
    const hours = Math.round(absDiffMs / 3_600_000);
    return diffMs >= 0 ? `${hours}h ago` : `in ${hours}h`;
  }
  if (absDiffMs < 604_800_000) {
    const days = Math.round(absDiffMs / 86_400_000);
    return diffMs >= 0 ? `${days}d ${suffix}` : `in ${days}d`;
  }
  return d.toLocaleDateString();
}

function normalizeGoogleAdsCustomerId(value: string | null | undefined): string {
  return String(value || '').replace(/\D/g, '');
}

function formatGoogleAdsCustomerId(value: string | null | undefined): string {
  const digits = normalizeGoogleAdsCustomerId(value);
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits || 'unknown';
}

function weeklyRunDetailRefetchInterval(status?: WeeklyRunStatus): number | false {
  return status === undefined || status === 'running' ? 5_000 : false;
}

// ---------------------------------------------------------------------------
// Weekly sweep mode control (GET/PATCH /google-ads/weekly/tenant-mode)
// ---------------------------------------------------------------------------

const WEEKLY_SWEEP_MODES: ReadonlyArray<{
  value: TenantWeeklyMode;
  hint: string;
}> = [
  { value: 'off', hint: 'cron skips this tenant' },
  { value: 'warn', hint: 'report-only, no changes' },
  { value: 'soft_auto', hint: 'auto-apply low-risk only' },
  { value: 'hard_auto', hint: 'auto-apply incl. pauses (guard-gated)' },
];

function WeeklySweepModeControl({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [pendingAutoMode, setPendingAutoMode] = useState<Extract<
    TenantWeeklyMode,
    'soft_auto' | 'hard_auto'
  > | null>(null);

  const modeQuery = useQuery({
    queryKey: ['google-ads-weekly-tenant-mode', tenantId],
    queryFn: () => getTenantWeeklyMode(tenantId),
    retry: (failureCount, err: unknown) => {
      // 404 = no Google Ads integration row; 503 = feature flag off.
      // Neither heals by retrying.
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404 || status === 503) return false;
      return failureCount < 2;
    },
  });

  const save = useMutation({
    mutationFn: (mode: TenantWeeklyMode) => setTenantWeeklyMode(tenantId, mode),
    onSuccess: (state) => {
      queryClient.setQueryData(
        ['google-ads-weekly-tenant-mode', tenantId],
        state,
      );
      toast.success(
        state.weekly_mode === 'off'
          ? `Weekly sweep turned off — the Monday cron will skip ${tenantId}.`
          : `Weekly sweep set to ${state.weekly_mode} for ${tenantId}.`,
      );
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Could not save weekly sweep mode: ${detail}`);
    },
  });

  const currentMode = modeQuery.data?.weekly_mode;
  const queryErrorStatus = (
    modeQuery.error as { response?: { status?: number } } | null
  )?.response?.status;

  const handleSelect = (value: TenantWeeklyMode) => {
    if (value === currentMode) return;
    if (value === 'soft_auto' || value === 'hard_auto') {
      // Auto modes let the cron make real changes — confirm first.
      setPendingAutoMode(value);
      return;
    }
    save.mutate(value);
  };

  const confirmPendingAutoMode = () => {
    if (!pendingAutoMode) return;
    save.mutate(pendingAutoMode, { onSettled: () => setPendingAutoMode(null) });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-teal-dark" />
          Weekly sweep
          {currentMode && (
            <Badge
              variant="outline"
              className={cn(
                currentMode === 'off' &&
                  'border-border bg-warm-bg text-muted-foreground',
                currentMode === 'warn' &&
                  'border-teal/40 bg-teal-light text-teal-dark',
                (currentMode === 'soft_auto' || currentMode === 'hard_auto') &&
                  'border-coral/40 bg-coral-light text-coral-dark',
              )}
            >
              {currentMode}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Whether the Monday 06:00 cron includes this tenant, and how much it
          may change on its own. The manual trigger below ignores this — it
          always runs with the mode you pick there.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {modeQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading current mode…
          </div>
        ) : queryErrorStatus === 404 ? (
          <div className="rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
            No Google Ads integration for <strong>{tenantId}</strong> yet —
            connect Google Ads first, then set the weekly sweep mode here.
          </div>
        ) : modeQuery.isError ? (
          <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Could not load the weekly sweep mode
            {queryErrorStatus === 503
              ? ' — the Client Dashboard cron is disabled on this deploy.'
              : '.'}
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                Cron mode
              </label>
              <Select
                value={currentMode}
                onValueChange={(v) => handleSelect(v as TenantWeeklyMode)}
                disabled={save.isPending}
              >
                <SelectTrigger className="w-[320px]">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  {WEEKLY_SWEEP_MODES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      <span className="font-medium">{m.value}</span>
                      <span className="text-muted-foreground">
                        {' '}
                        — {m.hint}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {save.isPending && (
              <div className="flex items-center gap-2 pb-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </div>
            )}
          </div>
        )}
        {modeQuery.data &&
          modeQuery.data.integration_status !== 'connected' && (
            <div className="rounded border border-coral/30 bg-coral-light p-3 text-sm text-coral-dark">
              This tenant's Google Ads integration status is{' '}
              <strong>{modeQuery.data.integration_status ?? 'unknown'}</strong>
              . The cron only runs <em>connected</em> integrations, so the
              sweep stays inactive until it's reconnected.
            </div>
          )}
      </CardContent>

      <Dialog
        open={pendingAutoMode !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAutoMode(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Enable {pendingAutoMode} weekly sweep for {tenantId}?
            </DialogTitle>
            <DialogDescription>
              {pendingAutoMode === 'hard_auto' ? (
                <>
                  Every Monday the cron will make real changes to this
                  tenant's Google Ads account on its own — including pausing
                  ads and keywords (blast-radius guards still apply). No
                  operator approval step.
                </>
              ) : (
                <>
                  Every Monday the cron will auto-apply low-risk changes
                  (negative keywords) to this tenant's Google Ads account and
                  queue riskier ones — like pauses — as approval requests.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingAutoMode(null)}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmPendingAutoMode}
              disabled={save.isPending}
              className="bg-coral hover:bg-coral-hover"
            >
              {save.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                <>Enable {pendingAutoMode}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Manual trigger panel (POST /google-ads/weekly/trigger)
// ---------------------------------------------------------------------------

function ManualTriggerPanel({ tenantId }: { tenantId: string }) {
  const [mode, setMode] = useState<WeeklyMode>('warn');
  const [missingCustomerIdError, setMissingCustomerIdError] = useState<string | null>(null);
  const [applyingCustomerId, setApplyingCustomerId] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const autoApplyDetectedCustomerId = async () => {
    setApplyingCustomerId(true);
    try {
      const result = await detectAccessibleGoogleAdsCustomers(tenantId);
      const recommendedCustomerId = normalizeGoogleAdsCustomerId(
        result.recommended_customer_id,
      );

      if (!recommendedCustomerId) {
        const candidateCount = result.customers?.length ?? 0;
        if (candidateCount > 0) {
          toast.warning(
            `Google Ads returned ${candidateCount} customer IDs. Choose the correct Park City account in Workspace Settings.`,
          );
        } else {
          toast.error(
            'Google Ads OAuth is connected, but no accessible customer IDs were returned.',
          );
        }
        navigate('/workspace-settings?tab=integrations');
        return;
      }

      const saved = await apiService.updateTenant(tenantId, {
        google_ad_customer_id: recommendedCustomerId,
      });
      await queryClient.invalidateQueries({ queryKey: ['tenantSettings', tenantId] });
      if (saved.website && saved.website !== tenantId) {
        await queryClient.invalidateQueries({ queryKey: ['tenantSettings', saved.website] });
      }
      setMissingCustomerIdError(null);
      toast.success(
        `Google Ads customer ID ${formatGoogleAdsCustomerId(recommendedCustomerId)} applied. Run weekly again.`,
      );
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ??
        (err as { message?: string })?.message ??
        String(err);
      toast.error(`Could not apply Google Ads customer ID: ${detail}`);
    } finally {
      setApplyingCustomerId(false);
    }
  };

  const trigger = useMutation({
    mutationFn: () => triggerRun(tenantId, mode),
    onMutate: () => {
      setMissingCustomerIdError(null);
    },
    onSuccess: (result) => {
      setMissingCustomerIdError(null);
      toast.success(
        `Run completed: ${result.status} — flagged ${result.search_terms_flagged}, ` +
          `paused ${result.ads_paused} ads, ${result.keywords_paused} kws, ` +
          `created ${result.approval_items_created} approvals`,
      );
      queryClient.invalidateQueries({
        queryKey: ['google-ads-weekly-runs', tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ['google-ads-weekly-approvals', tenantId],
      });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      const isTimeout =
        (err as { code?: string })?.code === 'ECONNABORTED' ||
        /timeout of \d+ms exceeded|timed out/i.test(detail);
      if (isTimeout) {
        toast.warning(
          'Weekly trigger is taking longer than the browser wait window. The backend may still be running; recent runs will refresh automatically.',
        );
        queryClient.invalidateQueries({
          queryKey: ['google-ads-weekly-runs', tenantId],
        });
        queryClient.invalidateQueries({
          queryKey: ['google-ads-weekly-approvals', tenantId],
        });
        return;
      }
      if (/no google_ad_customer_id|manager\/MCC account|needs the client account customer ID/i.test(detail)) {
        setMissingCustomerIdError(detail);
      }
      toast.error(`Trigger failed: ${detail}`);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Play className="h-4 w-4 text-coral" />
          Manual trigger
        </CardTitle>
        <CardDescription>
          Run the weekly cron for this tenant right now. The cron normally
          fires Monday 06:00 — use this for staging smoke or ad-hoc reruns.
          Live Google Ads checks can take up to a few minutes; recent runs
          continue auto-refreshing while it completes.
          Three modes:{' '}
          <strong>warn</strong> (evaluate-only, no writes),{' '}
          <strong>soft_auto</strong> (creates approval requests for the
          operator), <strong>hard_auto</strong> (writes directly).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Tenant
            </label>
            <div className="rounded border border-border bg-warm-bg px-3 py-2 text-sm">
              {tenantId}
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Mode
            </label>
            <Select
              value={mode}
              onValueChange={(v) => setMode(v as WeeklyMode)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="warn">warn (read-only)</SelectItem>
                <SelectItem value="soft_auto">
                  soft_auto (queue approvals)
                </SelectItem>
                <SelectItem value="hard_auto">
                  hard_auto (write directly)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => trigger.mutate()}
            disabled={trigger.isPending}
            className="bg-coral hover:bg-coral-hover"
          >
            {trigger.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running…
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" /> Run now
              </>
            )}
          </Button>
        </div>
        {mode === 'hard_auto' && (
          <div className="rounded border border-coral/30 bg-coral-light p-3 text-sm text-coral-dark">
            <strong>hard_auto writes directly to Google Ads.</strong> Only use
            after warn + soft_auto runs look right, and confirm{' '}
            <code>GOOGLE_ADS_DRY_RUN=true</code> on staging if you don't want
            real writes yet.
          </div>
        )}
        {missingCustomerIdError && (
          <div className="rounded border border-coral/30 bg-coral-light p-3 text-sm text-coral-dark">
            <div className="font-medium">Google Ads customer ID is missing for this workspace.</div>
            <p className="mt-1">
              Client Dashboard needs <code>tenant_settings.google_ad_customer_id</code> for{' '}
              <code>{tenantId}</code>. Open Workspace Settings, go to Google Ads,
              detect the accessible customer IDs, then apply the correct client account.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                className="bg-coral hover:bg-coral-hover"
                disabled={applyingCustomerId}
                onClick={autoApplyDetectedCustomerId}
              >
                {applyingCustomerId ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Detecting…
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Detect and apply customer ID
                  </>
                )}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="bg-card"
                onClick={() => navigate('/workspace-settings?tab=integrations')}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Google Ads settings
              </Button>
            </div>
            <p className="mt-2 text-xs opacity-80">{missingCustomerIdError}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Recent runs panel (GET /google-ads/weekly/runs)
// ---------------------------------------------------------------------------

const RUN_SORT_ACCESSORS: SortAccessors<WeeklyRunSummary> = {
  started: (r) => r.run_started_at ?? null,
  mode: (r) => r.mode,
  status: (r) => r.status,
  terms: (r) => r.search_terms_flagged,
  ads: (r) => r.ads_paused,
  kw: (r) => r.keywords_paused,
  approvals: (r) => r.approval_items_created,
  errors: (r) => r.error_count,
};

const RUN_RESIZE_DEFAULTS: Record<string, number> = {
  started: 150,
  mode: 100,
  status: 110,
  terms: 110,
  ads: 120,
  kw: 120,
  approvals: 110,
  errors: 90,
};

function runUniqueOptions(values: (string | null | undefined)[]): { value: string; label: string }[] {
  const set = new Set<string>();
  for (const v of values) if (v) set.add(v);
  return [...set].sort().map((v) => ({ value: v, label: v }));
}

function RecentRunsPanel({ tenantId }: { tenantId: string }) {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const runs = useQuery({
    queryKey: ['google-ads-weekly-runs', tenantId],
    queryFn: () => listRuns(tenantId, 50),
    refetchInterval: 15_000,
  });
  const runsErrorMessage = runs.error instanceof Error
    ? runs.error.message
    : 'Unable to load recent weekly runs.';

  const runRows = useMemo<WeeklyRunSummary[]>(() => runs.data ?? [], [runs.data]);

  const { sortKey, sortDir, toggleSort, sortRows } = useTableSort<WeeklyRunSummary>('started', 'desc');
  const { widths, startResize } = useColumnResize(RUN_RESIZE_DEFAULTS);
  const [rules, setRules] = useState<FilterRule[]>([]);
  const [conjunction, setConjunction] = useState<FilterConjunction>('and');

  const filterColumns: FilterColumn<WeeklyRunSummary>[] = useMemo(
    () => [
      {
        key: 'mode', label: 'Mode', type: 'enum',
        accessor: (r) => r.mode,
        options: runUniqueOptions(runRows.map((r) => r.mode)),
      },
      {
        key: 'status', label: 'Status', type: 'enum',
        accessor: (r) => r.status,
        options: runUniqueOptions(runRows.map((r) => r.status)),
      },
      { key: 'terms', label: 'Search terms', type: 'number', accessor: (r) => r.search_terms_flagged },
      { key: 'ads', label: 'Ad candidates', type: 'number', accessor: (r) => r.ads_paused },
      { key: 'kw', label: 'Kw candidates', type: 'number', accessor: (r) => r.keywords_paused },
      { key: 'approvals', label: 'Approvals', type: 'number', accessor: (r) => r.approval_items_created },
      { key: 'errors', label: 'Errors', type: 'number', accessor: (r) => r.error_count },
    ],
    [runRows],
  );

  const filteredSorted = useMemo(() => {
    const filtered = applyFilterRules(runRows, rules, filterColumns, conjunction);
    return sortRows(filtered, RUN_SORT_ACCESSORS);
  }, [runRows, rules, filterColumns, conjunction, sortRows]);

  const { page, pageSize, setPage, setPageSize, pageCount, slice } = usePagination(
    filteredSorted.length,
    { defaultPageSize: 10 },
  );
  const pageRows = slice(filteredSorted);

  const th = (key: string, label: string, className?: string) => (
    <SortableTh
      sortKey={key}
      currentKey={sortKey}
      dir={sortDir}
      onSort={toggleSort}
      resizable
      width={widths[key]}
      onResize={startResize}
      className={className}
    >
      {label}
    </SortableTh>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-teal" />
          Recent runs
        </CardTitle>
        <CardDescription>
          Latest weekly cron runs for this tenant, newest first. Auto-refreshes
          every 15 seconds.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {runs.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : runs.isError ? (
          <div className="rounded border border-destructive/30 bg-destructive/5 p-4 text-sm">
            <div className="flex items-start gap-2">
              <TriangleAlert className="mt-0.5 h-4 w-4 text-destructive" />
              <div className="space-y-2">
                <div className="font-medium text-destructive">
                  Recent weekly runs failed to load.
                </div>
                <div className="text-muted-foreground">{runsErrorMessage}</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => runs.refetch()}
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        ) : runRows.length === 0 ? (
          <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No runs yet. Trigger one above or wait for the Monday cron.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-end">
              <FilterBuilder
                columns={filterColumns}
                rules={rules}
                conjunction={conjunction}
                onRulesChange={setRules}
                onConjunctionChange={setConjunction}
              />
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {th('started', 'Started')}
                    {th('mode', 'Mode')}
                    {th('status', 'Status')}
                    {th('terms', 'Search terms', 'text-right')}
                    {th('ads', 'Ad candidates', 'text-right')}
                    {th('kw', 'Kw candidates', 'text-right')}
                    {th('approvals', 'Approvals', 'text-right')}
                    {th('errors', 'Errors', 'text-right')}
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((r) => (
                    <RunRow
                      key={r.id}
                      run={r}
                      onView={() => setSelectedRunId(r.id)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
            <TablePagination
              page={page}
              pageCount={pageCount}
              pageSize={pageSize}
              totalRows={filteredSorted.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </CardContent>
      <RunDetailDialog
        runId={selectedRunId}
        onClose={() => setSelectedRunId(null)}
      />
    </Card>
  );
}

function RunRow({
  run,
  onView,
}: {
  run: WeeklyRunSummary;
  onView: () => void;
}) {
  return (
    <TableRow>
      <TableCell className="text-sm">
        {relativeTime(run.run_started_at)}
      </TableCell>
      <TableCell>
        <ModeBadge mode={run.mode} />
      </TableCell>
      <TableCell>
        <StatusBadge status={run.status} />
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {run.search_terms_flagged}
      </TableCell>
      <TableCell className="text-right tabular-nums">{run.ads_paused}</TableCell>
      <TableCell className="text-right tabular-nums">
        {run.keywords_paused}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {run.approval_items_created}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {run.error_count > 0 ? (
          <span className="text-destructive">{run.error_count}</span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="sm"
          onClick={onView}
          aria-label="View run detail"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.map(asRecord).filter((item) => Object.keys(item).length > 0)
    : [];
}

function textValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function numericValue(value: unknown): number | null {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function moneyFromMicros(value: unknown): string {
  const micros = numericValue(value);
  if (micros === null) return '—';
  return `$${(micros / 1_000_000).toFixed(2)}`;
}

function metricText(metrics: Record<string, unknown>): string {
  const clicks = numericValue(metrics.clicks);
  const conversions = numericValue(metrics.conversions);
  const cost = numericValue(metrics.cost);
  const cpa = numericValue(metrics.costPerConversion);
  return [
    clicks !== null ? `${clicks.toLocaleString()} clicks` : null,
    conversions !== null ? `${conversions.toLocaleString()} conv` : null,
    cost !== null ? `$${cost.toFixed(2)} cost` : null,
    cpa !== null ? `$${cpa.toFixed(2)} CPA` : null,
  ].filter(Boolean).join(' · ') || '—';
}

function WeeklyRunFindings({ run }: { run: WeeklyRunDetail }) {
  const findings = asRecord(run.findings_json);
  const summary = asRecord(findings.summary);
  const searchTerms = asRecord(findings.search_terms);
  const keywordFindings = asRecord(findings.keywords);
  const adFindings = asRecord(findings.ads);
  const searchTermCandidates = asArray(searchTerms.candidates);
  const keywordCandidates = asArray(keywordFindings.candidates);
  const keywordWatch = asArray(keywordFindings.watch);
  const adGroups = asArray(adFindings.groups);
  const adRows = adGroups.flatMap((group) =>
    asArray(group.ads).map((ad) => ({ group, ad })),
  );
  const adWatch = asArray(adFindings.watch);
  // Monitor engines (budget pacing, KPI drift). review_steps carries per-section
  // success — run status only reflects the ACTION engines, so a green run can
  // still have a FAILED monitor step. undefined = the monitor didn't run (legacy).
  const reviewSteps = asRecord(summary.review_steps);
  const budgetPacing = asRecord(findings.budget_pacing);
  const kpiDrift = asRecord(findings.kpi_drift);
  const kpiPlan = asRecord(findings.kpi_plan);
  const pacingCandidates = asArray(budgetPacing.candidates);
  const kpiCandidates = asArray(kpiDrift.candidates);
  const kpiWins = asArray(kpiDrift.wins);
  const planCandidates = asArray(kpiPlan.candidates);
  const planWins = asArray(kpiPlan.wins);
  const planNoTargets = kpiPlan.no_targets === true;
  const urlReach = asRecord(findings.url_reachability);
  const reachCandidates = asArray(urlReach.candidates);
  const reachChecked = numericValue(urlReach.checked) ?? 0;
  const adPolicy = asRecord(findings.ad_policy);
  const policyCandidates = asArray(adPolicy.candidates);
  const convHealth = asRecord(findings.conversion_health);
  const convCandidates = asArray(convHealth.candidates);
  const convActions = numericValue(convHealth.conversion_actions) ?? 0;
  const adStrength = asRecord(findings.ad_strength);
  const strengthCandidates = asArray(adStrength.candidates);
  const strengthChecked = numericValue(adStrength.checked) ?? 0;
  const assetCov = asRecord(findings.asset_coverage);
  const assetCandidates = asArray(assetCov.candidates);
  const assetChecked = numericValue(assetCov.checked) ?? 0;
  const convDrop = asRecord(findings.conversion_drop);
  const convDropCandidates = asArray(convDrop.candidates);
  const convDropPrimaries = numericValue(convDrop.primary_actions) ?? 0;
  const bidFloor = asRecord(findings.bid_floor);
  const bidFloorCandidates = asArray(bidFloor.candidates);
  const bidFloorChecked = numericValue(bidFloor.checked) ?? 0;
  const budgetSync = asRecord(findings.budget_sync);
  const budgetSyncCandidates = asArray(budgetSync.candidates);
  const budgetSyncNoPlanner = budgetSync.no_planner === true;
  const resetWindow = asRecord(findings.reset_window);
  const resetWindowCandidates = asArray(resetWindow.candidates);
  const cannibalization = asRecord(findings.cannibalization);
  const cannibalizationCandidates = asArray(cannibalization.candidates);
  // Post-write reconciliation (SC-D1 #1404): only present once the delayed sweep
  // has re-read a hard_auto run's writes (~15 min after it applied them).
  const reconciliation = asRecord(findings.reconciliation);
  const reconciliationCandidates = asArray(reconciliation.candidates);
  const hasReconciliation = Object.keys(reconciliation).length > 0;
  // Blast-radius circuit breaker: only present when a hard_auto run HELD writes
  // because they exceeded the configured caps (fail-closed safety, SC-D3).
  const blastRadius = asRecord(findings.blast_radius);
  const blastRadiusCandidates = asArray(blastRadius.candidates);
  const hasBlastRadius = Object.keys(blastRadius).length > 0;

  return (
    <Tabs defaultValue="summary" className="space-y-3">
      <TabsList className="flex h-auto flex-wrap justify-start">
        <TabsTrigger value="summary">Summary</TabsTrigger>
        <TabsTrigger value="search-terms">Search Terms</TabsTrigger>
        <TabsTrigger value="keywords">Keyword Pauses</TabsTrigger>
        <TabsTrigger value="ads">Ad Review</TabsTrigger>
        <TabsTrigger value="monitors">Monitors</TabsTrigger>
        <TabsTrigger value="approvals">Approvals</TabsTrigger>
      </TabsList>

      <TabsContent value="summary" className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="rounded border border-border bg-warm-bg p-3">
            <div className="text-xs text-muted-foreground">Search terms</div>
            <div className="text-lg font-semibold tabular-nums">{run.search_terms_flagged}</div>
          </div>
          <div className="rounded border border-border bg-warm-bg p-3">
            <div className="text-xs text-muted-foreground">Ad candidates</div>
            <div className="text-lg font-semibold tabular-nums">{run.ads_paused}</div>
          </div>
          <div className="rounded border border-border bg-warm-bg p-3">
            <div className="text-xs text-muted-foreground">Keyword candidates</div>
            <div className="text-lg font-semibold tabular-nums">{run.keywords_paused}</div>
          </div>
          <div className="rounded border border-border bg-warm-bg p-3">
            <div className="text-xs text-muted-foreground">Approvals</div>
            <div className="text-lg font-semibold tabular-nums">{run.approval_items_created}</div>
          </div>
        </div>
        <div className="rounded border border-teal/20 bg-teal-light/40 p-3 text-sm text-teal-dark">
          <strong>Run posture:</strong> {textValue(summary.write_policy)}. In warn mode, these are candidates only; no Google Ads writes are applied.
        </div>
        <div className="rounded border border-border bg-warm-bg p-3 text-xs text-muted-foreground">
          Pause candidates stay in watch when conversion lag, impressions, or spend are too thin
          to make the pause signal trustworthy. That keeps zero-conversion or low-data rows from
          being treated as automatic removals.
        </div>
      </TabsContent>

      <TabsContent value="search-terms">
        {searchTermCandidates.length === 0 ? (
          <EmptyWeeklyFinding message="No search-term negative candidates were stored for this run." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Negative keyword</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Evidence</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {searchTermCandidates.map((candidate, index) => {
                  const evidence = asArray(candidate.evidence);
                  return (
                    <TableRow key={`${textValue(candidate.negative_keyword)}-${index}`}>
                      <TableCell className="font-medium">{textValue(candidate.negative_keyword)}</TableCell>
                      <TableCell>{textValue(candidate.scope)}</TableCell>
                      <TableCell className="max-w-[320px] text-xs text-muted-foreground">
                        {evidence.length
                          ? evidence.map((item) => {
                              const clicks = numericValue(item.clicks);
                              const cost = moneyFromMicros(item.cost_micros);
                              return `${textValue(item.search_term)} (${clicks ?? 0} clicks, ${cost})`;
                            }).join(', ')
                          : 'No matching search-term row was returned in the stored evidence.'}
                      </TableCell>
                      <TableCell className="text-xs">{textValue(candidate.remediation_hint)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </TabsContent>

      <TabsContent value="keywords" className="space-y-3">
        {keywordCandidates.length === 0 ? (
          <EmptyWeeklyFinding message="No keyword pause candidates passed the weekly guardrails." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Keyword</TableHead>
                  <TableHead>Campaign / ad group</TableHead>
                  <TableHead>Metrics</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keywordCandidates.map((candidate, index) => {
                  const metrics = asRecord(candidate.metrics);
                  return (
                    <TableRow key={`${textValue(candidate.resourceName)}-${index}`}>
                      <TableCell>
                        <div className="font-medium">{textValue(candidate.keyword)}</div>
                        <div className="text-xs text-muted-foreground">{textValue(candidate.matchType)}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{textValue(candidate.campaignName)}</div>
                        <div className="text-muted-foreground">{textValue(candidate.adGroupName)}</div>
                      </TableCell>
                      <TableCell className="text-xs">{metricText(metrics)}</TableCell>
                      <TableCell className="max-w-[320px] text-xs">{textValue(candidate.reason)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {keywordWatch.length > 0 && (
          <WatchList title="Keyword watchlist" items={keywordWatch} />
        )}
      </TabsContent>

      <TabsContent value="ads" className="space-y-3">
        {adRows.length === 0 ? (
          <EmptyWeeklyFinding message="No ad pause candidates passed the weekly guardrails." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ad</TableHead>
                  <TableHead>Ad group</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Replacement</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adRows.map(({ group, ad }, index) => {
                  const replacement = asRecord(ad.replacement);
                  const headlines = asArray(replacement.headlines).map((item) => textValue(item.text));
                  const descriptions = asArray(replacement.descriptions).map((item) => textValue(item.text));
                  return (
                    <TableRow key={`${textValue(ad.adResourceName)}-${index}`}>
                      <TableCell className="text-xs">{textValue(ad.adId)}</TableCell>
                      <TableCell className="text-xs">{textValue(group.ad_group_name)}</TableCell>
                      <TableCell className="max-w-[300px] text-xs">{textValue(ad.reason)}</TableCell>
                      <TableCell className="max-w-[360px] text-xs text-muted-foreground">
                        {headlines.length || descriptions.length ? (
                          <div className="space-y-1">
                            {headlines.length > 0 && (
                              <div>
                                <span className="font-medium text-foreground">Headlines:</span>{' '}
                                {headlines.join(' | ')}
                              </div>
                            )}
                            {descriptions.length > 0 && (
                              <div>
                                <span className="font-medium text-foreground">Descriptions:</span>{' '}
                                {descriptions.join(' ')}
                              </div>
                            )}
                          </div>
                        ) : (
                          'No replacement RSA stored.'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {adWatch.length > 0 && (
          <WatchList title="Ad watchlist" items={adWatch} />
        )}
      </TabsContent>

      <TabsContent value="monitors" className="space-y-3">
        <MonitorSection
          title="Budget pacing"
          ran={reviewSteps.budget_pacing}
          accountStatus={textValue(budgetPacing.account_pacing_status)}
          candidates={pacingCandidates}
          renderCandidate={(c) => {
            const m = asRecord(c.metrics);
            return (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{textValue(c.campaign_name)}</span>
                  <SeverityChip value={textValue(c.severity)} />
                  <span className="text-xs text-muted-foreground">{textValue(c.pacing_status)}</span>
                </div>
                <div className="mt-1 text-xs">{textValue(c.reason)}</div>
                <div className="mt-1 text-xs tabular-nums text-muted-foreground">
                  spent {moneyUsd(m.spend_usd)} · budget {moneyUsd(m.cycle_budget_usd)} · projected {moneyUsd(m.projected_cycle_spend_usd)} (Δ {moneyUsd(m.projected_cycle_delta_usd)})
                </div>
              </>
            );
          }}
        />
        <MonitorSection
          title="KPI drift & impression share"
          ran={reviewSteps.kpi_drift}
          candidates={kpiCandidates}
          wins={kpiWins}
          renderCandidate={(c) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{textValue(c.label)}</span>
                <SeverityChip value={textValue(c.severity)} />
                <span className="text-xs text-muted-foreground">{textValue(c.direction)}</span>
              </div>
              <div className="mt-1 text-xs">{textValue(c.reason)}</div>
            </>
          )}
        />
        <MonitorSection
          title="Below plan (vs operator goals)"
          ran={reviewSteps.kpi_plan}
          candidates={planCandidates}
          wins={planWins}
          emptyMessage={
            planNoTargets
              ? 'No KPI goals set for this client — set target CPA / CPC / conversion-rate / impression-share below to enable below-plan flagging.'
              : undefined
          }
          renderCandidate={(c) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{textValue(c.label)}</span>
                <SeverityChip value={textValue(c.severity)} />
                <span className="text-xs text-muted-foreground">vs goal</span>
              </div>
              <div className="mt-1 text-xs">{textValue(c.reason)}</div>
            </>
          )}
        />
        <MonitorSection
          title="Final-URL reachability"
          ran={reviewSteps.url_reachability}
          candidates={reachCandidates}
          emptyMessage={
            urlReach.no_urls === true
              ? 'No absolute final URLs on enabled ads to check.'
              : `Ran — checked ${reachChecked} final URL(s), all reachable.`
          }
          renderCandidate={(c) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <SeverityChip value={textValue(c.severity)} />
                <span className="text-xs text-muted-foreground">
                  HTTP {c.status === null || c.status === undefined ? 'timeout/error' : textValue(c.status)}
                </span>
              </div>
              <div className="mt-1 break-all text-xs">{textValue(c.reason)}</div>
            </>
          )}
        />
        <MonitorSection
          title="Disapproval / policy residue"
          ran={reviewSteps.ad_policy}
          candidates={policyCandidates}
          emptyMessage="Ran — no disapproved or limited-serving ads."
          renderCandidate={(c) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{textValue(c.label)}</span>
                <SeverityChip value={textValue(c.severity)} />
                <span className="text-xs text-muted-foreground">{textValue(c.approval_status)}</span>
              </div>
              <div className="mt-1 text-xs">{textValue(c.reason)}</div>
            </>
          )}
        />
        <MonitorSection
          title="Conversion-tracking health"
          ran={reviewSteps.conversion_health}
          candidates={convCandidates}
          emptyMessage={`Ran — ${convActions} conversion action(s), tracking looks healthy.`}
          renderCandidate={(c) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{textValue(c.label)}</span>
                <SeverityChip value={textValue(c.severity)} />
              </div>
              <div className="mt-1 text-xs">{textValue(c.reason)}</div>
            </>
          )}
        />
        <MonitorSection
          title="RSA ad strength"
          ran={reviewSteps.ad_strength}
          candidates={strengthCandidates}
          emptyMessage={`Ran — checked ${strengthChecked} RSA(s), none POOR.`}
          renderCandidate={(c) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{textValue(c.label)}</span>
                <SeverityChip value={textValue(c.severity)} />
                {c.ad_group ? (
                  <span className="text-xs text-muted-foreground">{textValue(c.ad_group)}</span>
                ) : null}
              </div>
              <div className="mt-1 text-xs">{textValue(c.reason)}</div>
            </>
          )}
        />
        <MonitorSection
          title="Asset / extension coverage"
          ran={reviewSteps.asset_coverage}
          candidates={assetCandidates}
          emptyMessage={`Ran — ${assetChecked} campaign(s), all have ≥4 sitelinks & callouts.`}
          renderCandidate={(c) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{textValue(c.label)}</span>
                <SeverityChip value={textValue(c.severity)} />
                <span className="text-xs text-muted-foreground">
                  {textValue(c.sitelinks)} sitelinks · {textValue(c.callouts)} callouts
                </span>
              </div>
              <div className="mt-1 text-xs">{textValue(c.reason)}</div>
            </>
          )}
        />
        <MonitorSection
          title="Conversion volume drop (w/w)"
          ran={reviewSteps.conversion_drop}
          candidates={convDropCandidates}
          emptyMessage={`Ran — ${convDropPrimaries} primary action(s), none down >30% w/w.`}
          renderCandidate={(c) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{textValue(c.label)}</span>
                <SeverityChip value={textValue(c.severity)} />
              </div>
              <div className="mt-1 text-xs">{textValue(c.reason)}</div>
            </>
          )}
        />
        <MonitorSection
          title="Bid-strategy learning floor"
          ran={reviewSteps.bid_floor}
          candidates={bidFloorCandidates}
          emptyMessage={`Ran — ${bidFloorChecked} smart-bid campaign(s), all above their learning floor.`}
          renderCandidate={(c) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{textValue(c.label)}</span>
                <SeverityChip value={textValue(c.severity)} />
                <span className="text-xs text-muted-foreground">{textValue(c.strategy)}</span>
              </div>
              <div className="mt-1 text-xs">{textValue(c.reason)}</div>
            </>
          )}
        />
        <MonitorSection
          title="Daily budget sync (planned vs live)"
          ran={reviewSteps.budget_sync}
          candidates={budgetSyncCandidates}
          emptyMessage={
            budgetSyncNoPlanner
              ? 'No Planner budget mapped for this client — set planner_client_id to enable drift checks.'
              : 'Ran — live daily budget matches the planned budget.'
          }
          renderCandidate={(c) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <SeverityChip value={textValue(c.severity)} />
                <span className="text-xs text-muted-foreground">
                  planned ${textValue(c.planned_daily_usd)} · live ${textValue(c.live_daily_usd)}
                </span>
              </div>
              <div className="mt-1 text-xs">{textValue(c.reason)}</div>
            </>
          )}
        />
        <MonitorSection
          title="Budget reset window (relearn risk)"
          ran={reviewSteps.reset_window}
          candidates={resetWindowCandidates}
          emptyMessage="Ran — no campaign moved its budget enough in the last 7 days to reset learning."
          renderCandidate={(c) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <SeverityChip value={textValue(c.severity)} />
                <span className="font-medium">{textValue(c.label)}</span>
                <span className="text-xs text-muted-foreground">
                  {textValue(c.changes)} change(s) · {Math.round((numericValue(c.cumulative_pct) ?? 0) * 100)}% cumulative
                </span>
              </div>
              <div className="mt-1 text-xs">{textValue(c.reason)}</div>
            </>
          )}
        />
        <MonitorSection
          title="Cross-campaign cannibalization"
          ran={reviewSteps.cannibalization}
          candidates={cannibalizationCandidates}
          emptyMessage="Ran — no search term is served by more than one campaign."
          renderCandidate={(c) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <SeverityChip value={textValue(c.severity)} />
                <span className="font-medium">{textValue(c.label)}</span>
                <span className="text-xs text-muted-foreground">
                  {textValue(c.campaign_count)} campaigns · ${textValue(c.overlap_cost_usd)} / 30d
                </span>
              </div>
              <div className="mt-1 text-xs">{textValue(c.reason)}</div>
            </>
          )}
        />
        {hasReconciliation && (
          <MonitorSection
            title="Write reconciliation (post-apply)"
            ran={true}
            candidates={reconciliationCandidates}
            emptyMessage={`Verified — all ${textValue(reconciliation.checked)} applied write(s) landed as intended.`}
            renderCandidate={(c) => (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <SeverityChip value={textValue(c.severity)} />
                  <span className="font-medium">{textValue(c.label)}</span>
                  <span className="text-xs text-muted-foreground">{textValue(c.kind)}</span>
                </div>
                <div className="mt-1 text-xs">{textValue(c.reason)}</div>
              </>
            )}
          />
        )}
        {hasBlastRadius && (
          <MonitorSection
            title="Blast-radius holds (writes not auto-applied)"
            ran={true}
            candidates={blastRadiusCandidates}
            emptyMessage="No writes were held this run."
            renderCandidate={(c) => (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <SeverityChip value={textValue(c.severity)} />
                  <span className="font-medium">{textValue(c.label)}</span>
                  <span className="text-xs text-muted-foreground">held for review</span>
                </div>
                <div className="mt-1 text-xs">{textValue(c.reason)}</div>
              </>
            )}
          />
        )}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href="/ads-tools/budgets?platform=google-ads">Open Budget Pacing tool →</a>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href="/ads-tools/performance?platform=google-ads">Open Performance tool →</a>
          </Button>
        </div>
      </TabsContent>

      <TabsContent value="approvals">
        <div className="rounded border border-border bg-warm-bg p-4 text-sm">
          <div className="font-medium">{run.approval_items_created} approval request(s) created</div>
          <p className="mt-1 text-muted-foreground">
            Warn mode creates no approvals. Use soft_auto only after the warn findings look correct.
          </p>
        </div>
      </TabsContent>
    </Tabs>
  );
}

function EmptyWeeklyFinding({ message }: { message: string }) {
  return (
    <div className="rounded border border-dashed border-border p-4 text-sm text-muted-foreground">
      {message}
    </div>
  );
}

function moneyUsd(value: unknown): string {
  const n = numericValue(value);
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `$${Math.round(n).toLocaleString()}`;
}

function SeverityChip({ value }: { value: string }) {
  const v = (value || '').toLowerCase();
  const cls =
    v === 'high'
      ? 'bg-destructive/10 text-destructive'
      : v === 'medium' || v === 'warning'
        ? 'bg-coral-light/40 text-coral-dark'
        : 'bg-teal-light/40 text-teal-dark';
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${cls}`}>
      {value || 'info'}
    </span>
  );
}

/**
 * One monitor engine's findings (budget pacing / KPI drift). ``ran`` comes from
 * summary.review_steps — true = ran, false = the check FAILED (distinct from
 * "no issues"), undefined = the monitor didn't run for this run (legacy).
 */
function MonitorSection({
  title,
  ran,
  accountStatus,
  candidates,
  renderCandidate,
  wins,
  emptyMessage,
}: {
  title: string;
  ran: unknown;
  accountStatus?: string;
  candidates: Record<string, unknown>[];
  renderCandidate: (c: Record<string, unknown>) => JSX.Element;
  wins?: Record<string, unknown>[];
  emptyMessage?: string;
}) {
  const failed = ran === false;
  const notRun = ran === undefined || ran === null;
  const hasWins = !!wins && wins.length > 0;
  return (
    <div className="rounded border border-border bg-warm-bg p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{title}</span>
        {accountStatus && accountStatus !== '—' && (
          <span className="text-xs text-muted-foreground">account: {accountStatus}</span>
        )}
        {failed && (
          <span className="rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-destructive">
            check failed
          </span>
        )}
        {notRun && (
          <span className="text-[10px] uppercase text-muted-foreground">not run</span>
        )}
      </div>
      {notRun ? (
        <div className="text-xs text-muted-foreground">
          This monitor didn’t run for this weekly run (it predates the monitor engines).
        </div>
      ) : failed ? (
        <div className="text-xs text-destructive">
          The check errored this run — see Errors above. A blank result here does not mean “no issues”.
        </div>
      ) : candidates.length === 0 && !hasWins ? (
        <div className="text-xs text-teal-dark">
          {emptyMessage || 'Ran — nothing flagged this week.'}
        </div>
      ) : (
        <div className="space-y-2">
          {candidates.map((c, i) => (
            <div key={i} className="rounded bg-background p-2">
              {renderCandidate(c)}
            </div>
          ))}
          {hasWins && (
            <div className="space-y-1 pt-1">
              {wins!.map((w, i) => (
                <div key={`win-${i}`} className="text-xs text-teal-dark">
                  ✓ {textValue(w.reason)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WatchList({
  title,
  items,
}: {
  title: string;
  items: Record<string, unknown>[];
}) {
  return (
    <div className="rounded border border-coral/20 bg-coral-light/20 p-3">
      <div className="mb-2 text-sm font-medium text-coral-dark">{title}</div>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="rounded bg-background p-2 text-xs">
            <div>{textValue(item.keyword ?? item.ad_id ?? item.adId ?? item.ad_resource_name)}</div>
            <div className="text-muted-foreground">{textValue(item.watch_reason ?? item.watchReason)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RunDetailDialog({
  runId,
  onClose,
}: {
  runId: string | null;
  onClose: () => void;
}) {
  const detail = useQuery({
    queryKey: ['google-ads-weekly-run', runId],
    queryFn: () => getRun(runId!),
    enabled: !!runId,
    refetchInterval: (query) =>
      weeklyRunDetailRefetchInterval(
        (query.state.data as WeeklyRunDetail | undefined)?.status,
      ),
    refetchIntervalInBackground: true,
  });
  return (
    <Dialog open={!!runId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Run detail</DialogTitle>
          <DialogDescription>
            {runId ? `ID: ${runId} · auto-updates while running` : ''}
          </DialogDescription>
        </DialogHeader>
        {detail.isLoading ? (
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : detail.data ? (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-muted-foreground">Mode</div>
                <ModeBadge mode={detail.data.mode} />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Status</div>
                <StatusBadge status={detail.data.status} />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Started</div>
                <div>{detail.data.run_started_at ?? '—'}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Completed</div>
                <div>{detail.data.run_completed_at ?? 'still running'}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">LLM cost</div>
                <div className="tabular-nums">
                  ${detail.data.llm_cost_usd.toFixed(4)}
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Errors</div>
              {Object.keys(detail.data.errors_json).length === 0 ? (
                <div className="text-sm text-muted-foreground">None</div>
              ) : (
                <div className="max-h-64 space-y-1 overflow-auto">
                  {Object.entries(detail.data.errors_json).map(([step, message]) => (
                    <div key={step} className="rounded bg-warm-bg p-2 text-xs">
                      <span className="font-medium text-destructive">{step}</span>
                      <span className="text-muted-foreground"> — {String(message)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <WeeklyRunFindings run={detail.data} />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Approval queue panel (GET /approvals + POST /weekly/approvals/{id}/apply)
// ---------------------------------------------------------------------------

const FLOW_LABELS: Record<string, { label: string; description: string }> = {
  p2_negative_keywords: {
    label: 'Negative keywords',
    description: 'Search terms the LLM wants to add as account-wide negatives',
  },
  p2_ad_pauses: {
    label: 'Ad pauses',
    description: 'Underperforming ads to pause + replacement RSAs',
  },
  p2_keyword_pauses: {
    label: 'Keyword pauses',
    description: 'Underperforming keywords to pause',
  },
};

function ApprovalQueuePanel({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const operatorEmail = user?.email ?? 'ui-operator';

  const approvals = useQuery({
    queryKey: ['google-ads-weekly-approvals', tenantId],
    queryFn: () => listWeeklyApprovals(tenantId),
    refetchInterval: 15_000,
  });

  const invalidateApprovalsAndRuns = () => {
    queryClient.invalidateQueries({
      queryKey: ['google-ads-weekly-approvals', tenantId],
    });
    queryClient.invalidateQueries({
      queryKey: ['google-ads-weekly-runs', tenantId],
    });
    queryClient.invalidateQueries({
      queryKey: operatorQueueCountsQueryKey(tenantId),
    });
  };

  const applyMutation = useMutation({
    mutationFn: (requestId: string) => applyApprovedRequest(requestId),
    onSuccess: (result) => {
      toast.success(
        `Applied ${result.applied} entries (${result.skipped} skipped, ` +
          `${result.errors_recorded} errors)`,
      );
      invalidateApprovalsAndRuns();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Apply failed: ${detail}`);
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (vars: { requestId: string; action: 'approve' | 'reject' }) =>
      resolveApproval({
        requestId: vars.requestId,
        action: vars.action,
        tenantId,
        resolvedBy: operatorEmail,
      }),
    onSuccess: (result, vars) => {
      toast.success(
        `${vars.action === 'approve' ? 'Approved' : 'Rejected'} — status=${result.status}`,
      );
      invalidateApprovalsAndRuns();
    },
    onError: (err: unknown, vars) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`${vars.action} failed: ${detail}`);
    },
  });

  const sendSlackMutation = useMutation({
    mutationFn: (requestId: string) => sendApprovalToSlack(requestId, tenantId),
    onSuccess: (result) => {
      const slackResult = result.results.slack ?? 'sent';
      toast.success(`Slack dispatch: ${slackResult}`);
      invalidateApprovalsAndRuns();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Slack dispatch failed: ${detail}`);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Hammer className="h-4 w-4 text-coral" />
          Approval queue
        </CardTitle>
        <CardDescription>
          Soft_auto runs queue approval requests here. Approve in the UI, send
          to Slack when operator review should happen there, then click{' '}
          <strong>Apply</strong> after approval to push the writes through
          Google Ads.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {approvals.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : approvals.data?.length === 0 ? (
          <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No weekly approvals — run a soft_auto trigger to populate this.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Flow</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead className="w-56" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(approvals.data ?? []).map((row) => (
                  <ApprovalRow
                    key={row.id}
                    row={row}
                    onSendSlack={() => sendSlackMutation.mutate(row.id)}
                    sendSlackDisabled={
                      row.status !== 'pending' || sendSlackMutation.isPending
                    }
                    onApply={() => applyMutation.mutate(row.id)}
                    applyDisabled={
                      row.status !== 'approved' || applyMutation.isPending
                    }
                    onApprove={() =>
                      resolveMutation.mutate({
                        requestId: row.id,
                        action: 'approve',
                      })
                    }
                    onReject={() =>
                      resolveMutation.mutate({
                        requestId: row.id,
                        action: 'reject',
                      })
                    }
                    resolveDisabled={resolveMutation.isPending}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ApprovalRow({
  row,
  onSendSlack,
  sendSlackDisabled,
  onApply,
  applyDisabled,
  onApprove,
  onReject,
  resolveDisabled,
}: {
  row: WeeklyApprovalRow;
  onSendSlack: () => void;
  sendSlackDisabled: boolean;
  onApply: () => void;
  applyDisabled: boolean;
  onApprove: () => void;
  onReject: () => void;
  resolveDisabled: boolean;
}) {
  const flowMeta = FLOW_LABELS[row.flow] ?? {
    label: row.flow,
    description: '',
  };
  const itemCount = countItemsInPayload(row);
  const watchItems = watchItemsInPayload(row);
  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{flowMeta.label}</div>
        <div className="text-xs text-muted-foreground">
          {flowMeta.description}
        </div>
        {watchItems.length > 0 && (
          <div className="mt-2 rounded-md border border-coral/30 bg-coral-light px-3 py-2 text-xs text-coral-dark">
            <div className="flex items-center gap-1.5 font-medium">
              <Clock className="h-3.5 w-3.5" />
              {watchItems.length} watch item{watchItems.length === 1 ? '' : 's'}
            </div>
            <div className="mt-1 space-y-1">
              {watchItems.slice(0, 3).map((item, index) => (
                <div key={weeklyWatchItemKey(item, index)}>
                  <span className="font-medium">
                    {weeklyWatchItemLabel(item)}
                  </span>
                  <span className="text-coral-dark/80">
                    {' '}
                    - {weeklyWatchItemReason(item)}
                  </span>
                </div>
              ))}
              {watchItems.length > 3 && (
                <div className="text-coral-dark/70">
                  +{watchItems.length - 3} more watch item
                  {watchItems.length - 3 === 1 ? '' : 's'}
                </div>
              )}
            </div>
          </div>
        )}
      </TableCell>
      <TableCell>
        <StatusBadge status={row.status} />
      </TableCell>
      <TableCell className="text-sm">
        {relativeTime(row.requested_at)}
      </TableCell>
      <TableCell className="text-sm">{relativeTime(row.expires_at)}</TableCell>
      <TableCell className="text-right tabular-nums">
        <div>{itemCount}</div>
        {watchItems.length > 0 && (
          <div className="text-xs text-coral-dark">
            {watchItems.length} watch
          </div>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-2">
          {row.status === 'pending' && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={sendSlackDisabled}
                onClick={onSendSlack}
              >
                Send to Slack
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={resolveDisabled}
                onClick={onApprove}
                className="border-teal/40 text-teal-dark hover:bg-teal-light"
              >
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={resolveDisabled}
                onClick={onReject}
                className="border-destructive/30 text-destructive hover:bg-destructive/5"
              >
                Reject
              </Button>
            </>
          )}
          {row.status === 'approved' && (
            <Button
              variant="outline"
              size="sm"
              disabled={applyDisabled}
              onClick={onApply}
              className="border-coral/40 text-coral-dark hover:bg-coral-light"
            >
              Apply
            </Button>
          )}
          {row.status !== 'pending' && row.status !== 'approved' && (
            <span className="text-xs text-muted-foreground">
              {row.status}
            </span>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function countItemsInPayload(row: WeeklyApprovalRow): number {
  const p = row.payload as {
    candidates?: unknown[];
    keywords?: unknown[];
    groups?: { ads?: unknown[] }[];
  };
  if (Array.isArray(p.candidates)) return p.candidates.length;
  if (Array.isArray(p.keywords)) return p.keywords.length;
  if (Array.isArray(p.groups))
    return p.groups.reduce((n, g) => n + (g.ads?.length ?? 0), 0);
  return 0;
}

// ---------------------------------------------------------------------------
function watchItemsInPayload(row: WeeklyApprovalRow): Record<string, unknown>[] {
  const p = row.payload as { watch?: unknown[] };
  if (!Array.isArray(p.watch)) return [];
  return p.watch.filter(isPlainRecord);
}

function weeklyWatchItemKey(item: Record<string, unknown>, index: number): string {
  return (
    nullableTextValue(item.ad_resource_name)
    ?? nullableTextValue(item.resource_name)
    ?? nullableTextValue(item.ad_id)
    ?? nullableTextValue(item.keyword)
    ?? String(index)
  );
}

function weeklyWatchItemLabel(item: Record<string, unknown>): string {
  const keyword = nullableTextValue(item.keyword);
  if (keyword) return keyword;
  const adGroup = nullableTextValue(item.ad_group_name);
  const adId = nullableTextValue(item.ad_id);
  if (adGroup && adId) return `${adGroup} ad ${adId}`;
  if (adGroup) return adGroup;
  return nullableTextValue(item.ad_resource_name) ?? 'Watch item';
}

function weeklyWatchItemReason(item: Record<string, unknown>): string {
  return (
    nullableTextValue(item.watch_reason)
    ?? nullableTextValue(item.original_reason)
    ?? nullableTextValue(item.reason)
    ?? 'Pause guard kept this item in watch status.'
  );
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nullableTextValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

// Campaign creation form (P1)
// ---------------------------------------------------------------------------

interface CampaignFormState {
  business_name: string;
  /** Search (keyword campaign) vs Performance Max (asset-group campaign). */
  campaign_type: 'SEARCH' | 'PERFORMANCE_MAX';
  monthly_budget_usd: string;
  active_service_days_per_month: string;
  campaign_goal: CampaignGoal;
  bidding_strategy: CampaignBiddingStrategy;
  target_cpa_usd: string;
  target_roas_percent: string;
  smart_bidding_override_ack: string;
  budget_allocation_strategy: CampaignBudgetAllocationStrategy;
  campaign_structure: CampaignStructure;
  target_search_network: boolean;
  target_content_network: boolean;
  target_address: string;
  gbp_location_resource_name: string;
  primary_phone: string;
  company_information: string;
  services_offered: string;
  value_proposition: string;
  call_to_action: string;
  promotions_specials: string;
  existing_target_keywords: string;
  pages: string;
  status_on_launch: 'PAUSED';
  approval_expires_in_hours: string;
  ad_review: boolean;
  /** RSAs per ad group to draft/create (2–3) for A/B testing. */
  rsa_variations: number;
}

const EMPTY_FORM: CampaignFormState = {
  business_name: '',
  campaign_type: 'SEARCH',
  monthly_budget_usd: '',
  active_service_days_per_month: '',
  campaign_goal: 'ROI_HOME_SERVICES',
  bidding_strategy: 'MAXIMIZE_CONVERSIONS',
  target_cpa_usd: '',
  target_roas_percent: '',
  smart_bidding_override_ack: '',
  budget_allocation_strategy: 'EVEN_BY_AD_GROUP',
  campaign_structure: 'SINGLE_CAMPAIGN',
  target_search_network: false,
  target_content_network: false,
  target_address: '',
  gbp_location_resource_name: '',
  primary_phone: '',
  company_information: '',
  services_offered: '',
  value_proposition: '',
  call_to_action: '',
  promotions_specials: '',
  existing_target_keywords: '',
  pages: '',
  status_on_launch: 'PAUSED',
  approval_expires_in_hours: '48',
  ad_review: false,
  rsa_variations: 2,
};

function recommendedBiddingStrategyForGoal(goal: CampaignGoal): CampaignBiddingStrategy {
  if (goal === 'BRAND_AWARENESS' || goal === 'TRAFFIC_CLICKS') {
    return 'MAXIMIZE_CLICKS';
  }
  return 'MAXIMIZE_CONVERSIONS';
}

function smartBiddingThreshold(strategy: CampaignBiddingStrategy): number | null {
  return SMART_BIDDING_THRESHOLDS[strategy] ?? null;
}

function campaignBiddingStrategyLabel(strategy: CampaignBiddingStrategy): string {
  if (strategy === 'MAXIMIZE_CLICKS') return 'Maximize clicks';
  if (strategy === 'TARGET_CPA') return 'Target CPA';
  if (strategy === 'TARGET_ROAS') return 'Target ROAS';
  return 'Maximize conversions';
}

function campaignGoalRequiresConversionTracking(goal: CampaignGoal): boolean {
  return goal === 'ROI_HOME_SERVICES' || goal === 'LEAD_GENERATION';
}

interface CampaignFieldSuggestion {
  label: string;
  value: string;
  source: string;
}

interface CampaignSetupLogEntry {
  id: number;
  status: 'info' | 'success' | 'error';
  stage: string;
  message: string;
  detail?: string;
  createdAt: string;
}

type CampaignFieldSuggestions = Partial<
  Record<keyof CampaignFormState, CampaignFieldSuggestion[]>
>;

function compactSuggestion(
  label: string,
  value: string | null | undefined,
  source: string,
): CampaignFieldSuggestion | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return { label, value: trimmed, source };
}

function uniqueCampaignSuggestions(
  suggestions: Array<CampaignFieldSuggestion | null>,
): CampaignFieldSuggestion[] {
  const seen = new Set<string>();
  return suggestions.filter((suggestion): suggestion is CampaignFieldSuggestion => {
    if (!suggestion) return false;
    const key = suggestion.value.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeMonthlyBudgetInput(value: string | null | undefined): string {
  const parsed = Number.parseFloat(String(value ?? '').replace(/[$,]/g, '').trim());
  return Number.isFinite(parsed) && parsed > 0
    ? String(Math.round(parsed * 100) / 100)
    : '';
}

function buildCampaignPlaceholders({
  brandBible,
  tenantId,
}: {
  brandBible?: BrandBible;
  tenantId: string;
}) {
  const domain = workspaceDomain(tenantId);
  const serviceExamples = (brandBible?.services_offered ?? [])
    .map(cleanCampaignExample)
    .filter(Boolean)
    .slice(0, 2);
  const firstService = serviceExamples[0] ?? 'primary service';
  const secondService = serviceExamples[1] ?? 'consultation';
  // Guard non-string types: Brand Bible rows are loosely typed and can carry an
  // object or array through the service. `??` only falls through on null/undefined,
  // so a raw `.trim()` here throws. Same class of bug as the Current Campaigns
  // crash fixed in PR #2038.
  const location = String(
    brandBible?.contact_info?.address
    ?? brandBible?.service_locations?.[0]
    ?? ''
  ).trim();

  return {
    businessName: brandBible?.business_name || cleanBusinessLabel(tenantId),
    targetAddress: location || 'Primary service address or market',
    primaryPhone: brandBible?.contact_info?.phone || '+1...',
    companyInformation:
      brandBible?.keyword_research_brief
      || brandBible?.description
      || `Describe ${brandBible?.business_name || cleanBusinessLabel(tenantId)}, its services, customers, and market.`,
    servicesOffered:
      serviceExamples.length > 0
        ? serviceExamples.join(', ')
        : `${firstService}, ${secondService}`,
    valueProposition:
      brandBible?.value_proposition
      || brandBible?.marketing_focus
      || 'What makes this workspace the best choice?',
    callToAction: brandBible?.call_to_action || 'Book a consultation',
    promotion: brandBible?.promotions_specials || 'Current offer or seasonal special',
    seedKeywords: [firstService, secondService].join('\n'),
    pages: domain
      ? `https://${domain}/services\nhttps://${domain}/contact`
      : 'https://your-domain.com/services\nhttps://your-domain.com/contact',
  };
}


/**
 * Values safe to WRITE INTO the form, as opposed to values safe to SHOW as a hint.
 *
 * ⛔ WHY THIS IS SEPARATE FROM buildCampaignPlaceholders. That function mixes two
 * different things: real Brand Bible data when it exists, and instruction text
 * when it does not — "What makes this workspace the best choice?",
 * "Current offer or seasonal special", "https://your-domain.com/services",
 * "Describe <business>, its services, customers, and market."
 *
 * Autofill used it as a fallback, so when a Brand Bible was thin the form was
 * filled with the guidance telling the operator what to write, and that text was
 * then SUBMITTED and fed into keyword generation. Observed live on
 * mikehammermoving.com 2026-08-16: company_information arrived at the backend as
 * "Describe who the company serves, the main services offered, and the
 * measurable problem it helps solve." The run produced a single one-keyword
 * cluster, which is what generating keywords from an instruction looks like.
 *
 * A prompt that becomes an answer is worse than a blank field: blank is visibly
 * incomplete, whereas this looks filled in and passes validation.
 *
 * So: hints stay in buildCampaignPlaceholders and are only ever rendered as the
 * placeholder attribute. Anything autofill WRITES comes from here, and every
 * value here is real data or absent.
 */
function buildCampaignAutofillValues({
  brandBible,
  tenantId,
}: {
  brandBible?: BrandBible;
  tenantId: string;
}) {
  const domain = workspaceDomain(tenantId);
  const services = (brandBible?.services_offered ?? [])
    .map(cleanCampaignExample)
    .filter(Boolean);
  const location = (
    brandBible?.contact_info?.address
    ?? brandBible?.service_locations?.[0]
    ?? ''
  ).trim();

  // Every entry is Brand Bible data or '' — never a hint, never a template.
  return {
    businessName: (brandBible?.business_name ?? '').trim(),
    targetAddress: location,
    primaryPhone: (brandBible?.contact_info?.phone ?? '').trim(),
    companyInformation: (
      brandBible?.keyword_research_brief
      || brandBible?.description
      || ''
    ).trim(),
    servicesOffered: services.join(', '),
    valueProposition: (
      brandBible?.value_proposition
      || brandBible?.marketing_focus
      || ''
    ).trim(),
    callToAction: (brandBible?.call_to_action ?? '').trim(),
    seedKeywords: services.slice(0, 2).join('\n'),
    // The real site, not the your-domain.com example.
    pages: domain ? `https://${domain}/services\nhttps://${domain}/contact` : '',
  };
}


function cleanCampaignExample(value: string): string {
  return value
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}


function cleanBusinessLabel(value: string): string {
  return value
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '')
    .replace(/\.[a-z]{2,}$/i, '')
    .replace(/[-_]+/g, ' ')
    .trim();
}


function workspaceDomain(tenantId: string): string {
  return tenantId
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '')
    .trim();
}

function normalizeHandoffList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item).trim()).filter(Boolean);
}

function campaignKeywordKey(value: string): string {
  return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

function normalizeHandoffKeywordDetails(
  value: unknown,
  selectedKeywords: string[],
): KeywordResearchCampaignHandoffKeyword[] {
  if (!Array.isArray(value)) return [];
  const selectedKeys = new Set(selectedKeywords.map(campaignKeywordKey));
  return value
    .map((item) => item as Partial<KeywordResearchCampaignHandoffKeyword>)
    .filter((item) => {
      const keyword = typeof item.keyword === 'string' ? item.keyword.trim() : '';
      return keyword && selectedKeys.has(campaignKeywordKey(keyword));
    })
    .map((item) => ({
      keyword: String(item.keyword).trim(),
      cpc: Number.isFinite(item.cpc) ? Number(item.cpc) : 0,
      volume: Number.isFinite(item.volume) ? Number(item.volume) : 0,
      kp_avg_monthly_searches:
        typeof item.kp_avg_monthly_searches === 'number'
          ? item.kp_avg_monthly_searches
          : null,
      kp_competition:
        typeof item.kp_competition === 'string' ? item.kp_competition : null,
      kp_competition_index:
        typeof item.kp_competition_index === 'number'
          ? item.kp_competition_index
          : null,
      kp_low_top_of_page_bid_micros:
        typeof item.kp_low_top_of_page_bid_micros === 'number'
          ? item.kp_low_top_of_page_bid_micros
          : null,
      kp_high_top_of_page_bid_micros:
        typeof item.kp_high_top_of_page_bid_micros === 'number'
          ? item.kp_high_top_of_page_bid_micros
          : null,
      kp_monthly_search_volumes: Array.isArray(item.kp_monthly_search_volumes)
        ? item.kp_monthly_search_volumes
        : null,
      ad_impression_share:
        typeof item.ad_impression_share === 'number'
          ? item.ad_impression_share
          : null,
      organic_impression_share:
        typeof item.organic_impression_share === 'number'
          ? item.organic_impression_share
          : null,
      organic_average_position:
        typeof item.organic_average_position === 'number'
          ? item.organic_average_position
          : null,
      in_account:
        typeof item.in_account === 'boolean' ? item.in_account : null,
      in_plan: typeof item.in_plan === 'boolean' ? item.in_plan : null,
      selected_for_campaign:
        typeof item.selected_for_campaign === 'boolean'
          ? item.selected_for_campaign
          : null,
      source:
        item.source === 'keyword_planner' || item.source === 'brainstorm'
          ? item.source
          : null,
      cluster_name:
        typeof item.cluster_name === 'string' && item.cluster_name.trim()
          ? item.cluster_name.trim()
          : 'Selected keywords',
      landing_page:
        typeof item.landing_page === 'string' && item.landing_page.trim()
          ? item.landing_page.trim()
          : null,
      low_hanging_fruit: item.low_hanging_fruit ?? null,
    }));
}

function parseKeywordResearchHandoff(
  raw: string | null,
  tenantId: string,
): KeywordResearchCampaignHandoff | null {
  if (!raw) return null;
  const parsed = JSON.parse(raw) as Partial<KeywordResearchCampaignHandoff>;
  const selectedKeywords = normalizeHandoffList(parsed.selected_keywords);
  if (
    parsed.source !== 'keyword_research' ||
    parsed.tenant_id !== tenantId ||
    selectedKeywords.length === 0
  ) {
    return null;
  }
  return {
    source: 'keyword_research',
    tenant_id: tenantId,
    run_id: typeof parsed.run_id === 'string' ? parsed.run_id : null,
    selected_keywords: selectedKeywords,
    selected_keyword_details: normalizeHandoffKeywordDetails(
      parsed.selected_keyword_details,
      selectedKeywords,
    ),
    pages: normalizeHandoffList(parsed.pages),
    saved_at:
      typeof parsed.saved_at === 'string'
        ? parsed.saved_at
        : new Date().toISOString(),
    planner_language_label:
      typeof parsed.planner_language_label === 'string'
        ? parsed.planner_language_label
        : null,
    planner_geo_label:
      typeof parsed.planner_geo_label === 'string'
        ? parsed.planner_geo_label
        : null,
    site_filter_url:
      typeof parsed.site_filter_url === 'string' ? parsed.site_filter_url : null,
  };
}

function joinLines(values: string[]): string {
  return values.join('\n');
}

function handoffMatchedPages(handoff: KeywordResearchCampaignHandoff): string[] {
  const pages = [
    ...(handoff.pages ?? []),
    ...(handoff.selected_keyword_details ?? [])
      .map((keyword) => keyword.landing_page)
      .filter((value): value is string => Boolean(value?.trim())),
  ];
  const seen = new Set<string>();
  return pages.filter((page) => {
    const key = page.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function formContainsHandoff(
  form: CampaignFormState,
  handoff: KeywordResearchCampaignHandoff,
): boolean {
  const formKeywords = new Set(splitLines(form.existing_target_keywords));
  const formPages = new Set(splitLines(form.pages));
  const matchedPages = handoffMatchedPages(handoff);
  const keywordsLoaded = handoff.selected_keywords.every((keyword) =>
    formKeywords.has(keyword),
  );
  const pagesLoaded = matchedPages.every((page) => formPages.has(page));
  return keywordsLoaded && pagesLoaded;
}

function campaignPackageKeywordDetails(
  handoff: KeywordResearchCampaignHandoff,
): KeywordResearchCampaignHandoffKeyword[] {
  if (handoff.selected_keyword_details?.length) {
    return handoff.selected_keyword_details;
  }
  return handoff.selected_keywords.map((keyword) => ({
    keyword,
    cpc: 0,
    volume: 0,
    cluster_name: 'Selected keywords',
    landing_page: null,
    low_hanging_fruit: null,
    source: null,
  }));
}

function currency(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return '—';
  }
  return `$${value.toFixed(2)}`;
}

function clicksPerDay(value: number | null): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return '—';
  }
  return value >= 10 ? value.toFixed(1) : value.toFixed(2);
}

function keywordPlannerCpcUsd(
  keyword: KeywordResearchCampaignHandoffKeyword,
): number | null {
  const low = keyword.kp_low_top_of_page_bid_micros;
  const high = keyword.kp_high_top_of_page_bid_micros;
  const lowUsd =
    typeof low === 'number' && Number.isFinite(low) && low > 0
      ? low / MICROS_PER_USD
      : null;
  const highUsd =
    typeof high === 'number' && Number.isFinite(high) && high > 0
      ? high / MICROS_PER_USD
      : null;
  if (lowUsd && highUsd) return (lowUsd + highUsd) / 2;
  return highUsd ?? lowUsd;
}

function calculateBudgetPacing(
  monthlyBudgetUsd: number,
  keywordDetails: KeywordResearchCampaignHandoffKeyword[],
  activeServiceDaysPerMonth: number | null,
) {
  const dailyBudgetUsd = monthlyBudgetUsd > 0 ? monthlyBudgetUsd / CAMPAIGN_PACING_DAYS : 0;
  const cpcs = keywordDetails
    .map(keywordPlannerCpcUsd)
    .filter((cpc): cpc is number => Number.isFinite(cpc) && cpc > 0);
  const sortedCpcs = [...cpcs].sort((a, b) => a - b);
  const medianCpc = sortedCpcs.length
    ? sortedCpcs.length % 2
      ? sortedCpcs[Math.floor(sortedCpcs.length / 2)]
      : (
          sortedCpcs[(sortedCpcs.length / 2) - 1] +
          sortedCpcs[sortedCpcs.length / 2]
        ) / 2
    : null;
  const estimatedClicksPerDay = medianCpc ? dailyBudgetUsd / medianCpc : null;
  const requiredDailyBudgetUsd = medianCpc
    ? medianCpc * CAMPAIGN_MIN_CLICKS_PER_DAY
    : null;
  const requiredMonthlyBudgetUsd = requiredDailyBudgetUsd
    ? requiredDailyBudgetUsd * CAMPAIGN_PACING_DAYS
    : null;
  const monthlyBudgetGapUsd = requiredMonthlyBudgetUsd
    ? Math.max(0, requiredMonthlyBudgetUsd - monthlyBudgetUsd)
    : null;
  const activeDayAverageUsd =
    activeServiceDaysPerMonth && activeServiceDaysPerMonth > 0
      ? (dailyBudgetUsd * CAMPAIGN_PACING_DAYS) / activeServiceDaysPerMonth
      : null;
  const dailySpendCapUsd = dailyBudgetUsd * CAMPAIGN_DAILY_SPEND_CAP_MULTIPLIER;
  const status =
    !medianCpc
      ? 'unknown'
      : estimatedClicksPerDay && estimatedClicksPerDay >= CAMPAIGN_MIN_CLICKS_PER_DAY
        ? 'ready'
        : 'review';
  return {
    dailyBudgetUsd,
    cpcKeywordCount: cpcs.length,
    totalKeywordCount: keywordDetails.length,
    lowConfidence:
      cpcs.length > 0 && cpcs.length < 3 && cpcs.length < keywordDetails.length,
    averageCpc: medianCpc,
    estimatedClicksPerDay,
    requiredDailyBudgetUsd,
    requiredMonthlyBudgetUsd,
    monthlyBudgetGapUsd,
    activeDayAverageUsd,
    dailySpendCapUsd,
    status,
  };
}

function calculateBudgetAllocations(
  monthlyBudgetUsd: number,
  keywordDetails: KeywordResearchCampaignHandoffKeyword[],
  strategy: CampaignBudgetAllocationStrategy,
) {
  const groups = new Map<string, KeywordResearchCampaignHandoffKeyword[]>();
  keywordDetails.forEach((keyword) => {
    const group = keyword.cluster_name?.trim() || 'Selected keywords';
    groups.set(group, [...(groups.get(group) ?? []), keyword]);
  });
  if (!groups.size) {
    groups.set('Campaign budget', []);
  }

  const allCpcs = keywordDetails
    .map(keywordPlannerCpcUsd)
    .filter((cpc): cpc is number => Number.isFinite(cpc) && cpc > 0);
  const fallbackCpc = allCpcs.length
    ? allCpcs.reduce((sum, cpc) => sum + cpc, 0) / allCpcs.length
    : 1;

  const rows = [...groups.entries()].map(([clusterName, keywords]) => {
    const weight =
      strategy === 'WEIGHTED_BY_CPC_VOLUME'
        ? keywords.reduce((sum, keyword) => {
            const cpc = keywordPlannerCpcUsd(keyword) ?? fallbackCpc;
            const volume = Number.isFinite(keyword.volume) && keyword.volume > 0
              ? keyword.volume
              : 10;
            return sum + cpc * volume;
          }, 0) || 1
        : 1;
    return {
      clusterName,
      keywordCount: keywords.length,
      weight,
    };
  });
  const totalWeight = rows.reduce((sum, row) => sum + row.weight, 0) || 1;

  return rows
    .map((row) => {
      const ratio = row.weight / totalWeight;
      return {
        ...row,
        ratio,
        monthlyUsd: monthlyBudgetUsd * ratio,
        dailyUsd: (monthlyBudgetUsd * ratio) / CAMPAIGN_PACING_DAYS,
      };
    })
    .sort((a, b) => b.ratio - a.ratio);
}

function microsToCurrency(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return '—';
  }
  return `$${(value / 1_000_000).toFixed(2)}`;
}

function bidRange(keyword: KeywordResearchCampaignHandoffKeyword): string {
  const low = microsToCurrency(keyword.kp_low_top_of_page_bid_micros);
  const high = microsToCurrency(keyword.kp_high_top_of_page_bid_micros);
  if (low === '—' && high === '—') return '—';
  if (low === '—') return high;
  if (high === '—') return low;
  return `${low}-${high}`;
}

function campaignTrendPoints(keyword: KeywordResearchCampaignHandoffKeyword) {
  return (keyword.kp_monthly_search_volumes ?? [])
    .map((point) => ({
      key: `${point.year ?? ''}-${point.month ?? ''}`,
      label: String(point.month ?? '').slice(0, 3) || String(point.year ?? ''),
      searches:
        typeof point.monthly_searches === 'number' ? point.monthly_searches : 0,
    }))
    .filter((point) => point.searches >= 0);
}

function CampaignKeywordTrendSparkline({
  keyword,
}: {
  keyword: KeywordResearchCampaignHandoffKeyword;
}) {
  const data = campaignTrendPoints(keyword);
  if (data.length < 2) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const width = 92;
  const height = 28;
  const values = data.map((point) => point.searches);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(1, max - min);
  const points = data.map((point, index) => {
    const x = data.length === 1 ? width / 2 : (index / (data.length - 1)) * width;
    const y = height - ((point.searches - min) / range) * (height - 6) - 3;
    return { ...point, x, y };
  });
  const path = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(' ');
  const latest = data[data.length - 1];
  const peak = data.reduce(
    (maxPoint, point) => (point.searches > maxPoint.searches ? point : maxPoint),
    data[0],
  );
  return (
    <TooltipProvider delayDuration={120}>
      <UiTooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex h-8 w-[100px] items-center justify-end rounded border border-transparent px-1 hover:border-teal/30 hover:bg-card"
            aria-label={`${keyword.keyword} monthly search trend`}
          >
            <svg
              width={width}
              height={height}
              viewBox={`0 0 ${width} ${height}`}
              role="img"
              aria-hidden="true"
              className="overflow-visible"
            >
              <path d={path} fill="none" stroke="#0f766e" strokeWidth="2" />
              {points.map((point) => (
                <circle
                  key={point.key}
                  cx={point.x}
                  cy={point.y}
                  r={point.key === latest.key ? 2.6 : 1.6}
                  fill="#0f766e"
                />
              ))}
            </svg>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="left"
          align="center"
          className="w-[420px] max-w-[calc(100vw-2rem)] p-3 text-xs"
        >
          <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="font-medium text-foreground">{keyword.keyword}</div>
              <div className="text-[11px] text-muted-foreground">
                Keyword Planner monthly searches
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-1.5">
              <Badge variant="outline" className="bg-card text-[10px]">
                Latest {latest.searches.toLocaleString()}
              </Badge>
              <Badge variant="outline" className="bg-card text-[10px]">
                Peak {peak.searches.toLocaleString()}
              </Badge>
            </div>
          </div>
          <div className="h-44 min-h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 8, left: 2, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d6e7e4" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  width={48}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => Number(value).toLocaleString()}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  formatter={(value) => [
                    Number(value).toLocaleString(),
                    'Monthly searches',
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="searches"
                  stroke="#0f766e"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#0f766e', strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-x-4 gap-y-1">
            {data.map((point) => (
              <span key={point.key} className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">{point.label}</span>
                <span className="tabular-nums text-foreground">
                  {point.searches.toLocaleString()}
                </span>
              </span>
            ))}
          </div>
        </TooltipContent>
      </UiTooltip>
    </TooltipProvider>
  );
}

function CampaignKeywordDetailsTable({
  keywords,
}: {
  keywords: KeywordResearchCampaignHandoffKeyword[];
}) {
  return (
    <div className="overflow-x-auto rounded border border-border bg-card">
      <table className="w-full min-w-[1100px] text-sm">
        <thead className="bg-warm-bg text-[11px] uppercase text-muted-foreground">
          <tr>
            <th className="px-2 py-2 text-left font-medium">Keyword</th>
            <th className="px-2 py-2 text-left font-medium">Ad group</th>
            <th className="px-2 py-2 text-right font-medium">Volume</th>
            <th className="px-2 py-2 text-right font-medium">CPC</th>
            <th className="px-2 py-2 text-right font-medium">Trend</th>
            <th className="px-2 py-2 text-right font-medium">Top bid</th>
            <th className="px-2 py-2 text-right font-medium">Competition</th>
            <th className="px-2 py-2 text-right font-medium">Low fruit</th>
            <th className="px-2 py-2 text-right font-medium">Account</th>
            <th className="px-2 py-2 text-right font-medium">Source</th>
            <th className="px-2 py-2 text-left font-medium">Landing page</th>
          </tr>
        </thead>
        <tbody>
          {keywords.map((keyword) => (
            <tr
              key={`${keyword.cluster_name}-${keyword.keyword}`}
              className="border-t border-border/60"
            >
              <td className="px-2 py-2 font-medium">{keyword.keyword}</td>
              <td className="px-2 py-2 text-muted-foreground">
                {keyword.cluster_name}
              </td>
              <td className="px-2 py-2 text-right tabular-nums">
                {keyword.volume > 0 ? keyword.volume.toLocaleString() : '—'}
              </td>
              <td className="px-2 py-2 text-right tabular-nums">
                {currency(keyword.cpc)}
              </td>
              <td className="px-2 py-2 text-right">
                <CampaignKeywordTrendSparkline keyword={keyword} />
              </td>
              <td className="px-2 py-2 text-right tabular-nums">
                {bidRange(keyword)}
              </td>
              <td className="px-2 py-2 text-right">
                <CampaignCompetitionBadge value={keyword.kp_competition ?? null} />
              </td>
              <td className="px-2 py-2 text-right">
                <CampaignLowFruitBadge keyword={keyword} />
              </td>
              <td className="px-2 py-2 text-right">
                <CampaignAccountBadge value={keyword.in_account ?? null} />
              </td>
              <td className="px-2 py-2 text-right">
                <CampaignSourceBadge value={keyword.source ?? null} />
              </td>
              <td className="max-w-[240px] truncate px-2 py-2 text-muted-foreground">
                {keyword.landing_page ? (
                  <a
                    href={keyword.landing_page}
                    target="_blank"
                    rel="noreferrer"
                    className="text-teal-dark hover:underline"
                    title={keyword.landing_page}
                  >
                    {keyword.landing_page}
                  </a>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CampaignCompetitionBadge({ value }: { value: string | null }) {
  if (!value) return <Badge variant="outline">Unknown</Badge>;
  const normalized = value.toLowerCase();
  const className =
    normalized === 'low'
      ? 'border-teal/30 bg-teal-light text-teal-dark'
      : normalized === 'medium'
        ? 'border-coral/30 bg-coral-light/40 text-coral-dark'
        : 'border-destructive/30 bg-destructive/5 text-destructive';
  return (
    <Badge variant="outline" className={className}>
      {value}
    </Badge>
  );
}

function CampaignLowFruitBadge({
  keyword,
}: {
  keyword: KeywordResearchCampaignHandoffKeyword;
}) {
  const signal = keyword.low_hanging_fruit;
  if (!signal) return <Badge variant="outline">Review</Badge>;
  const className =
    signal.status === 'yes'
      ? 'border-teal/30 bg-teal-light text-teal-dark'
      : signal.status === 'review'
        ? 'border-coral/30 bg-coral-light/40 text-coral-dark'
        : 'border-border bg-warm-bg text-muted-foreground';
  return (
    <Badge variant="outline" className={className} title={signal.reason}>
      {signal.label}
    </Badge>
  );
}

function CampaignAccountBadge({ value }: { value: boolean | null }) {
  if (value === true) return <Badge variant="outline">Existing</Badge>;
  if (value === false) {
    return (
      <Badge variant="outline" className="border-teal/30 bg-teal-light text-teal-dark">
        New
      </Badge>
    );
  }
  return <Badge variant="outline">Unknown</Badge>;
}

function CampaignSourceBadge({ value }: { value: string | null }) {
  return (
    <Badge variant="outline" className="bg-warm-bg">
      {value === 'keyword_planner' ? 'Planner' : value === 'brainstorm' ? 'LLM' : 'Mixed'}
    </Badge>
  );
}

function CampaignBudgetPacingPanel({
  monthlyBudgetUsd,
  keywordDetails,
  activeServiceDaysPerMonth,
  campaignGoal,
  biddingStrategy,
  targetCpaUsd,
  targetRoasPercent,
  allocationStrategy,
  campaignStructure,
  onBiddingStrategyChange,
  onCampaignGoalChange,
  onTargetCpaChange,
  onTargetRoasChange,
  onActiveServiceDaysChange,
  onAllocationStrategyChange,
  onCampaignStructureChange,
}: {
  monthlyBudgetUsd: number;
  keywordDetails: KeywordResearchCampaignHandoffKeyword[];
  activeServiceDaysPerMonth: number | null;
  campaignGoal: CampaignGoal;
  biddingStrategy: CampaignBiddingStrategy;
  targetCpaUsd: string;
  targetRoasPercent: string;
  allocationStrategy: CampaignBudgetAllocationStrategy;
  campaignStructure: CampaignStructure;
  onBiddingStrategyChange: (value: CampaignBiddingStrategy) => void;
  onCampaignGoalChange: (value: CampaignGoal) => void;
  onTargetCpaChange: (value: string) => void;
  onTargetRoasChange: (value: string) => void;
  onActiveServiceDaysChange: (value: string) => void;
  onAllocationStrategyChange: (value: CampaignBudgetAllocationStrategy) => void;
  onCampaignStructureChange: (value: CampaignStructure) => void;
}) {
  const pacing = calculateBudgetPacing(
    monthlyBudgetUsd,
    keywordDetails,
    activeServiceDaysPerMonth,
  );
  const allocations = calculateBudgetAllocations(
    monthlyBudgetUsd,
    keywordDetails,
    allocationStrategy,
  );
  const statusBadge =
    pacing.status === 'ready'
      ? 'border-teal/30 bg-teal-light text-teal-dark'
      : pacing.status === 'review'
        ? 'border-coral/40 bg-coral-light text-coral-dark'
        : 'border-border bg-warm-bg text-muted-foreground';
  const statusLabel =
    pacing.status === 'ready'
      ? 'Budget looks OK'
      : pacing.status === 'review'
        ? 'Review budget'
        : 'No cost-per-click data yet';

  return (
    <div className="rounded-md border border-border bg-background p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Activity className="h-4 w-4 text-teal" />
            Bidding and budget pacing
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Checks whether the budget can pay for at least 10 clicks a day,
            using Google's cost-per-click estimates for these keywords.
          </p>
        </div>
        <Badge variant="outline" className={statusBadge}>
          {statusLabel}
        </Badge>
      </div>

      <div className="mt-4 space-y-4">
        <SettingRow
          label="Campaign goal"
          htmlFor="campaign-goal"
          control={
            <Select
              value={campaignGoal}
              onValueChange={(value) => onCampaignGoalChange(value as CampaignGoal)}
            >
              <SelectTrigger id="campaign-goal" className="bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CAMPAIGN_GOAL_COPY).map(([value, copy]) => (
                  <SelectItem key={value} value={value}>
                    {copy.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
          explanation={
            <>
              <p className="text-foreground">
                {CAMPAIGN_GOAL_COPY[campaignGoal].explanation}
              </p>
              <p>{CAMPAIGN_GOAL_INTRO}</p>
            </>
          }
        />
        <SettingRow
          label="Bidding strategy"
          htmlFor="campaign-bidding-strategy"
          explanation={
            <>
              <p className="text-foreground">
                {BIDDING_STRATEGY_COPY[biddingStrategy].explanation}
              </p>
              <p>{BIDDING_STRATEGY_INTRO}</p>
            </>
          }
          control={
        <>
          <Select
            value={biddingStrategy}
            onValueChange={(value) =>
              onBiddingStrategyChange(value as CampaignBiddingStrategy)
            }
          >
            <SelectTrigger id="campaign-bidding-strategy" className="bg-card">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(BIDDING_STRATEGY_COPY).map(([value, copy]) => (
                <SelectItem key={value} value={value}>
                  {copy.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {biddingStrategy === 'TARGET_CPA' && (
            <div className="mt-3 rounded-md border border-coral/30 bg-coral-light px-3 py-2">
              <Label
                htmlFor="campaign-target-cpa"
                className="text-xs font-medium text-coral-dark"
              >
                Target CPA
              </Label>
              <Input
                id="campaign-target-cpa"
                value={targetCpaUsd}
                onChange={(event) => onTargetCpaChange(event.target.value)}
                type="number"
                min="0"
                step="0.01"
                placeholder="75.00"
                className="mt-1 bg-card"
              />
              <div className="mt-1 text-xs text-coral-dark/80">
                Required. The cost per lead you want, in USD.
              </div>
            </div>
          )}
          {biddingStrategy === 'TARGET_ROAS' && (
            <div className="mt-3 rounded-md border border-coral/30 bg-coral-light px-3 py-2">
              <Label
                htmlFor="campaign-target-roas"
                className="text-xs font-medium text-coral-dark"
              >
                Target ROAS
              </Label>
              <Input
                id="campaign-target-roas"
                value={targetRoasPercent}
                onChange={(event) => onTargetRoasChange(event.target.value)}
                type="number"
                min="0"
                step="1"
                placeholder="300"
                className="mt-1 bg-card"
              />
              <div className="mt-1 text-xs text-coral-dark/80">
                Required. Enter 300 for 300% return on ad spend.
              </div>
            </div>
          )}
        </>
          }
        />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded border border-border bg-warm-bg px-3 py-2">
          <div className="text-xs text-muted-foreground">Daily budget</div>
          <div className="text-sm font-semibold">{currency(pacing.dailyBudgetUsd)}</div>
        </div>
        <div className="rounded border border-border bg-warm-bg px-3 py-2">
          <div className="text-xs text-muted-foreground">Typical cost per click</div>
          <div className="text-sm font-semibold">{currency(pacing.averageCpc)}</div>
          <div className="text-xs text-muted-foreground">
            {pacing.lowConfidence
              ? `${pacing.cpcKeywordCount}/${pacing.totalKeywordCount} keywords with bid data`
              : `${pacing.cpcKeywordCount} keyword${pacing.cpcKeywordCount === 1 ? '' : 's'}`}
          </div>
          {pacing.lowConfidence ? (
            <div className="mt-1 text-[11px] font-medium text-coral-dark">
              Rough estimate. Only a few keywords have cost data.
            </div>
          ) : null}
        </div>
        <div className="rounded border border-border bg-warm-bg px-3 py-2">
          <div className="text-xs text-muted-foreground">Estimated clicks/day</div>
          <div className="text-sm font-semibold">
            {clicksPerDay(pacing.estimatedClicksPerDay)}
          </div>
        </div>
        <div className="rounded border border-border bg-warm-bg px-3 py-2">
          <div className="text-xs text-muted-foreground">Budget for 10 clicks/day</div>
          <div className="text-sm font-semibold">
            {currency(pacing.requiredDailyBudgetUsd)}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-md border border-border bg-card p-3">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(180px,0.45fr)_repeat(2,minmax(120px,0.35fr))]">
          <div>
            <Label className="text-xs font-medium uppercase text-muted-foreground">
              Days ads run
            </Label>
            <p className="mt-1 text-xs text-muted-foreground">
              Google can spend up to twice the daily budget on a busy day, but
              never more than 30.4 days' worth in a month. Ads don't run on
              days that are turned off.
            </p>
          </div>
          <div>
            <Label htmlFor="campaign-active-service-days" className="text-xs text-muted-foreground">
              Active days / month
            </Label>
            <Input
              id="campaign-active-service-days"
              type="number"
              min={1}
              max={31}
              step={1}
              value={activeServiceDaysPerMonth ?? ''}
              onChange={(event) => {
                const raw = event.target.value;
                const value = raw ? Number.parseFloat(raw) : null;
                onActiveServiceDaysChange(
                  value && Number.isFinite(value) ? String(Math.min(31, Math.max(1, value))) : '',
                );
              }}
              placeholder="22"
              className="mt-1 bg-warm-bg"
            />
          </div>
          <div className="rounded border border-border bg-warm-bg px-3 py-2">
            <div className="text-xs text-muted-foreground">Average per active day</div>
            <div className="text-sm font-semibold">
              {currency(pacing.activeDayAverageUsd)}
            </div>
          </div>
          <div className="rounded border border-border bg-warm-bg px-3 py-2">
            <div className="text-xs text-muted-foreground">Most in one day</div>
            <div className="text-sm font-semibold">{currency(pacing.dailySpendCapUsd)}</div>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-md border border-border bg-card p-3">
        <CollapsibleSection
          header={
            <div>
              <Label className="text-xs font-medium uppercase text-muted-foreground">
                Budget allocation
              </Label>
              <p className="mt-1 text-xs text-muted-foreground">
                {CLUSTER_DEFINITION}
              </p>
            </div>
          }
          actions={
            <Badge variant="outline" className="bg-warm-bg text-[10px]">
              {allocations.length} cluster{allocations.length === 1 ? '' : 's'}
            </Badge>
          }
        >
        <div className="mt-3">
          <SettingRow
            label="How to split the budget"
            htmlFor="campaign-budget-allocation"
            control={
              <Select
                value={allocationStrategy}
                onValueChange={(value) =>
                  onAllocationStrategyChange(value as CampaignBudgetAllocationStrategy)
                }
              >
                <SelectTrigger id="campaign-budget-allocation" className="bg-warm-bg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(BUDGET_ALLOCATION_COPY).map(([value, copy]) => (
                    <SelectItem key={value} value={value}>
                      {copy.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
            explanation={
              <>
                <p className="text-foreground">
                  {BUDGET_ALLOCATION_COPY[allocationStrategy].explanation}
                </p>
                <p>{BUDGET_ALLOCATION_INTRO}</p>
              </>
            }
          />
        </div>
        <div className="mt-3 grid gap-2">
          {allocations.map((allocation) => (
            <div
              key={allocation.clusterName}
              className="grid gap-2 rounded border border-border bg-warm-bg px-3 py-2 text-xs sm:grid-cols-[minmax(0,1fr)_80px_90px_90px]"
            >
              <div className="min-w-0">
                <div className="truncate font-medium text-foreground">
                  {allocation.clusterName}
                </div>
                <div className="text-muted-foreground">
                  {allocation.keywordCount} keyword{allocation.keywordCount === 1 ? '' : 's'}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Ratio</div>
                <div className="font-semibold">{Math.round(allocation.ratio * 100)}%</div>
              </div>
              <div>
                <div className="text-muted-foreground">Monthly</div>
                <div className="font-semibold">{currency(allocation.monthlyUsd)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Daily</div>
                <div className="font-semibold">{currency(allocation.dailyUsd)}</div>
              </div>
            </div>
          ))}
        </div>
        </CollapsibleSection>
      </div>

      <div className="mt-4 rounded-md border border-border bg-card p-3">
        {/* The default is one campaign with ad groups (EMPTY_FORM and the
            backend both default to SINGLE_CAMPAIGN); comms 9/23 asked for
            that default and for the choice to be named in plain words. */}
        <SettingRow
          label="Campaign structure"
          htmlFor="campaign-structure"
          control={
            <Select
              value={campaignStructure}
              onValueChange={(value) =>
                onCampaignStructureChange(value as CampaignStructure)
              }
            >
              <SelectTrigger id="campaign-structure" className="bg-warm-bg text-left">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CAMPAIGN_STRUCTURE_COPY).map(([value, copy]) => (
                  <SelectItem key={value} value={value}>
                    {copy.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
          explanation={
            <>
              <p className="text-foreground">
                {CAMPAIGN_STRUCTURE_COPY[campaignStructure].explanation}
              </p>
              <p>{CAMPAIGN_STRUCTURE_INTRO}</p>
            </>
          }
        />
      </div>

      {pacing.status === 'review' ? (
        <div className="mt-3 rounded border border-coral/30 bg-coral-light/40 px-3 py-2 text-xs text-coral-dark">
          The monthly budget is about {currency(pacing.monthlyBudgetGapUsd)} short
          of what 10 clicks a day would cost for these keywords.
          {pacing.lowConfidence ? (
            <div className="mt-1 font-medium">
              Rough estimate. Only {pacing.cpcKeywordCount} of{' '}
              {pacing.totalKeywordCount} selected keywords have cost-per-click
              data, so one unusual keyword can throw it off.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function buildCampaignFieldSuggestions({
  brandBible,
  tenantSettingsBudget,
  tenantId,
  placeholders,
  handoff,
}: {
  brandBible?: BrandBible;
  tenantSettingsBudget?: string;
  tenantId: string;
  placeholders: ReturnType<typeof buildCampaignPlaceholders>;
  handoff: KeywordResearchCampaignHandoff | null;
}): CampaignFieldSuggestions {
  const keywordDetails = handoff ? campaignPackageKeywordDetails(handoff) : [];
  const plannerKeywords = keywordDetails
    .filter((keyword) => keyword.source === 'keyword_planner')
    .map((keyword) => keyword.keyword);
  const lowFruitKeywords = keywordDetails
    .filter((keyword) => keyword.low_hanging_fruit?.status === 'yes')
    .map((keyword) => keyword.keyword);
  const topVolumeKeywords = [...keywordDetails]
    .sort((a, b) => b.volume - a.volume)
    .slice(0, 20)
    .map((keyword) => keyword.keyword);
  const selectedKeywords = handoff?.selected_keywords ?? [];
  const pages = handoff ? handoffMatchedPages(handoff) : [];
  const serviceList = brandBible?.services_offered?.filter(Boolean).join(', ');
  const businessName = brandBible?.business_name || cleanBusinessLabel(tenantId);

  return {
    business_name: uniqueCampaignSuggestions([
      compactSuggestion('Brand Bible', brandBible?.business_name, 'workspace'),
      compactSuggestion('Workspace domain', cleanBusinessLabel(tenantId), 'workspace'),
    ]),
    monthly_budget_usd: uniqueCampaignSuggestions([
      compactSuggestion('Workspace budget', tenantSettingsBudget, 'workspace'),
    ]),
    target_address: uniqueCampaignSuggestions([
      compactSuggestion('Primary address', brandBible?.contact_info?.address, 'workspace'),
      compactSuggestion('Service market', brandBible?.service_locations?.[0], 'workspace'),
    ]),
    primary_phone: uniqueCampaignSuggestions([
      compactSuggestion('Brand Bible', brandBible?.contact_info?.phone, 'workspace'),
    ]),
    company_information: uniqueCampaignSuggestions([
      compactSuggestion('Keyword brief', brandBible?.keyword_research_brief, 'workspace'),
      compactSuggestion('Brand description', brandBible?.description, 'workspace'),
      compactSuggestion(
        'Service summary',
        serviceList
          ? `${businessName} provides ${serviceList}.`
          : placeholders.companyInformation,
        'generated',
      ),
    ]),
    services_offered: uniqueCampaignSuggestions([
      compactSuggestion('Brand Bible services', serviceList, 'workspace'),
      compactSuggestion('Keyword page services', selectedKeywords.slice(0, 8).join(', '), 'handoff'),
    ]),
    value_proposition: uniqueCampaignSuggestions([
      compactSuggestion('Value prop', brandBible?.value_proposition, 'workspace'),
      compactSuggestion('Marketing focus', brandBible?.marketing_focus, 'workspace'),
    ]),
    call_to_action: uniqueCampaignSuggestions([
      compactSuggestion('Brand Bible CTA', brandBible?.call_to_action, 'workspace'),
      compactSuggestion('Consultation CTA', 'Book a consultation', 'default'),
      compactSuggestion('Quote CTA', 'Request a quote', 'default'),
    ]),
    promotions_specials: uniqueCampaignSuggestions([
      compactSuggestion('Brand Bible promo', brandBible?.promotions_specials, 'workspace'),
    ]),
    existing_target_keywords: uniqueCampaignSuggestions([
      compactSuggestion('Selected research keywords', selectedKeywords.join('\n'), 'handoff'),
      compactSuggestion('Low fruit only', lowFruitKeywords.join('\n'), 'handoff'),
      compactSuggestion('Keyword Planner rows', plannerKeywords.join('\n'), 'handoff'),
      compactSuggestion('Highest volume selected', topVolumeKeywords.join('\n'), 'handoff'),
      compactSuggestion('Default service seeds', placeholders.seedKeywords, 'default'),
    ]),
    pages: uniqueCampaignSuggestions([
      compactSuggestion('Matched keyword landing pages', pages.join('\n'), 'handoff'),
      compactSuggestion('Default site pages', placeholders.pages, 'default'),
    ]),
  };
}


// CORE-M-11.b.2: `'manage'` was retired here. Current Campaigns folded
// into Management's under-tab CampaignDrilldown (Q-2 ruling). The
// post-DONE run-completion flow now navigates OUT to
// /ads-tools/management?campaign=<id> via `onOpenManagement` instead
// of switching an in-page tab; the type therefore no longer carries
// the value.
type CampaignWorkflowTab =
  | 'create'
  | 'ad-review'
  | 'approvals'
  | 'logs'
  | 'history';

function normalizeCampaignWorkflowTab(value: string | null): CampaignWorkflowTab | null {
  return value === 'create' ||
    value === 'ad-review' ||
    value === 'approvals' ||
    value === 'logs' ||
    value === 'history'
    ? value
    : null;
}

/** Extract the campaign_id (last path segment) from a `campaign_resource_name`
 *  of the shape `customers/<cid>/campaigns/<id>`. Returns null on empty
 *  input or an unexpected shape — callers should treat that as "no live
 *  campaign to link to." */
function campaignIdFromResourceName(
  resource: string | null | undefined,
): string | null {
  if (!resource) return null;
  const parts = resource.split('/');
  const last = parts[parts.length - 1];
  return last && last !== 'campaigns' ? last : null;
}

function campaignTabForRun(run: CampaignRunResponse): CampaignWorkflowTab {
  if (run.state === 'KW_APPROVAL') return 'approvals';
  // AD_REVIEW has its own tab with the RSA editor. Sending it to 'approvals'
  // stranded the operator on a list while the thing needing their attention
  // lived one tab over.
  if (run.state === 'AD_REVIEW') return 'ad-review';
  // CORE-M-11.b.2: DONE / CAMPAIGN_CREATE / ASSET_CREATE / APPLY_RECS /
  // FAILED-with-a-live-campaign used to land on the internal 'manage' tab.
  // That tab is retired — the run panel's "Open Ads Management" button
  // now navigates OUT to /ads-tools/management?campaign=<id> instead. The
  // fallback tab here is 'history' so the operator still has a place to
  // land inside Campaign Builder if they never click the button.
  return 'history';
}

/**
 * A run waiting at the keyword gate is invisible on every surface except the
 * Approvals tab, and nothing pointed you there.
 *
 * ⛔ REPORTED BY JACOB, 2026-08-20, verifying on staging: he started a build,
 * navigated back to the builder, and the run had "disappeared". It had not — the
 * management list filters `state !== 'KW_APPROVAL'` deliberately, and the
 * builder form shows no runs at all, so the only trace of a pending approval was
 * the Approvals tab he was no longer on. He found it via run history because he
 * wrote the history view. A client operator would simply conclude their campaign
 * had vanished, and would very likely start a second one.
 *
 * So: whenever a run is waiting on a human, say so on the surface they are
 * standing on, and give them the way back to it.
 */
function PendingApprovalBanner({
  runs,
  onOpenRun,
}: {
  runs: CampaignRunResponse[];
  onOpenRun: (runId: string) => void;
}) {
  const pending = runs.filter((run) => run.state === 'KW_APPROVAL');
  if (pending.length === 0) return null;
  const first = pending[0];
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-coral/40 bg-coral-light p-3">
      <div className="text-sm">
        <span className="font-semibold text-coral-dark">
          {pending.length === 1
            ? '1 campaign run is waiting for your approval'
            : `${pending.length} campaign runs are waiting for your approval`}
        </span>
        <span className="text-muted-foreground">
          {' '}— nothing is created in Google Ads until you approve.
        </span>
      </div>
      <Button type="button" size="sm" onClick={() => onOpenRun(first.id)}>
        Review approval
      </Button>
    </div>
  );
}

function CampaignsTab({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: brandBible } = useBrandBible(tenantId);
  const { data: tenantSettings } = useTenantSettings(tenantId);
  const placeholders = buildCampaignPlaceholders({ brandBible, tenantId });
  // Hints are for SHOWING (placeholder attribute); these are for WRITING.
  const autofillValues = buildCampaignAutofillValues({ brandBible, tenantId });
  const [form, setForm] = useState<CampaignFormState>(EMPTY_FORM);
  const activeRunId = searchParams.get('run');
  const runPanelRef = useRef<HTMLDivElement | null>(null);
  const [activeCampaignTab, setActiveCampaignTab] = useState<CampaignWorkflowTab>(
    normalizeCampaignWorkflowTab(searchParams.get('campaign_tab')) ??
      // CORE-M-11.b.2: 'manage' retired; the mount landing for an open
      // run is 'history' now (the run row is there; "Open Ads Management"
      // in the run panel navigates OUT to the drill-down).
      (activeRunId ? 'history' : 'create'),
  );
  const [keywordHandoff, setKeywordHandoff] =
    useState<KeywordResearchCampaignHandoff | null>(null);
  const [handoffApplied, setHandoffApplied] = useState(false);
  const [budgetConfirmed, setBudgetConfirmed] = useState(false);
  // Same queryKey as PmaxRequirementsPanel, so this shares one fetch.
  const pmaxReadiness = useQuery({
    queryKey: ['google-ads-pmax-image-readiness', tenantId],
    queryFn: () => getPmaxImageReadiness(tenantId),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
    retry: false,
  });
  // Default TRUE until we have an answer: hiding the option on a network blip
  // would silently remove a working channel, and the server gate rejects
  // PERFORMANCE_MAX by name if the flag really is off.
  const pmaxFeatureAvailable = pmaxReadiness.data?.feature_enabled !== false;
  const [blockersDialogOpen, setBlockersDialogOpen] = useState(false);
  const [budgetSource, setBudgetSource] = useState<'workspace' | 'manual' | null>(null);
  // Budget entry unit overlay. `monthly_budget_usd` remains canonical; this only
  // controls which unit the operator types in / sees in the input box.
  const [budgetUnit, setBudgetUnit] = useState<'monthly' | 'daily'>('monthly');
  const [dailyBudgetInput, setDailyBudgetInput] = useState('');
  const [setupLogs, setSetupLogs] = useState<CampaignSetupLogEntry[]>([]);
  const [sendSlackApproval, setSendSlackApproval] = useState(false);
  const [expandCampaignKeywords, setExpandCampaignKeywords] = useState(false);
  const queryClient = useQueryClient();
  // Shared runs query (same key as the sub-tabs → deduped) used only to badge
  // the Ad Review sub-tab with the count of runs currently awaiting review.
  const adReviewCountQuery = useQuery({
    queryKey: ['google-ads-campaign-runs', tenantId],
    queryFn: () => listCampaignRuns({ tenantId, limit: 100 }),
    refetchInterval: 5_000,
  });
  const adReviewCount = (adReviewCountQuery.data?.runs ?? []).filter(
    (run) => run.state === 'AD_REVIEW',
  ).length;
  // Same query, second use: a run parked at the keyword gate is not shown on the
  // builder or the management list, so without this the operator who just
  // started it has no way to see it exists. See PendingApprovalBanner.
  const pendingApprovalRuns = (adReviewCountQuery.data?.runs ?? []).filter(
    (run) => run.state === 'KW_APPROVAL',
  );

  // Follow the open run to the tab that can act on it. The pipeline advances
  // server-side, so without this the operator sits on Approvals watching a
  // loader while the ads that need reviewing appear on another tab.
  //
  // Guarded by autoFollowedRunRef so a manual tab change isn't yanked back on
  // the next 5s poll — we jump once per (run, state) pair.
  const autoFollowedRunRef = useRef<string | null>(null);
  const openRun = (adReviewCountQuery.data?.runs ?? []).find(
    (run) => run.id === activeRunId,
  );
  useEffect(() => {
    if (!openRun) return;
    const key = `${openRun.id}:${openRun.state}`;
    if (autoFollowedRunRef.current === key) return;
    const target = campaignTabForRun(openRun);
    // Only follow into a state that needs the operator. Terminal and mid-write
    // states would drag them off whatever they navigated to deliberately.
    if (openRun.state !== 'AD_REVIEW' && openRun.state !== 'KW_APPROVAL') return;
    autoFollowedRunRef.current = key;
    if (target === activeCampaignTab) return;
    setActiveCampaignTab(target);
    updateCampaignSearchParams({ campaignTab: target, runId: openRun.id });
    // Keyed on dataUpdatedAt, NOT openRun?.id: on the first renders the runs
    // query is still loading, so openRun is undefined — and a dep that stays
    // undefined never re-fires the effect when the run finally arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRunId, adReviewCountQuery.dataUpdatedAt]);

  const setupLogIdRef = useRef(0);
  const tenantSettingsBudget = normalizeMonthlyBudgetInput(
    tenantSettings?.google_ad_budget_amount,
  );
  const fieldSuggestions = buildCampaignFieldSuggestions({
    brandBible,
    tenantSettingsBudget,
    tenantId,
    placeholders,
    handoff: keywordHandoff,
  });
  const updateCampaignSearchParams = (
    next: {
      runId?: string | null;
      campaignTab?: CampaignWorkflowTab;
    },
  ) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('platform', ACTIVE_ADS_PLATFORM);
      if (next.campaignTab) {
        params.set('campaign_tab', next.campaignTab);
      }
      if (next.runId === null) {
        params.delete('run');
      } else if (next.runId) {
        params.set('run', next.runId);
      }
      return params;
    }, { replace: true });
  };

  useEffect(() => {
    if (!activeRunId) return;
    window.setTimeout(() => {
      runPanelRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  }, [activeRunId]);

  useEffect(() => {
    try {
      const handoff = parseKeywordResearchHandoff(
        window.localStorage.getItem(keywordResearchCampaignHandoffKey(tenantId)),
        tenantId,
      );
      if (!handoff) {
        return;
      }
      const matchedPages = handoffMatchedPages(handoff);
      setKeywordHandoff(handoff);
      setForm((prev) => {
        return {
          ...prev,
          existing_target_keywords: prev.existing_target_keywords.trim()
            ? prev.existing_target_keywords
            : joinLines(handoff.selected_keywords),
          pages: prev.pages.trim() ? prev.pages : joinLines(matchedPages),
        };
      });
    } catch {
      window.localStorage.removeItem(keywordResearchCampaignHandoffKey(tenantId));
    }
  }, [tenantId]);

  useEffect(() => {
    setHandoffApplied(
      keywordHandoff ? formContainsHandoff(form, keywordHandoff) : false,
    );
  }, [form, keywordHandoff]);

  // Prefill the monthly budget from workspace settings ONCE (when it first
  // loads empty). Guarded by a ref so clearing the field later — e.g. deleting
  // the last digit to type a new value — is respected instead of snapping back
  // to the workspace default.
  const budgetPrefilledRef = useRef(false);
  useEffect(() => {
    if (budgetPrefilledRef.current) return;
    if (!tenantSettingsBudget) return;
    // Field already has a value (user typed / keyword handoff) — lock out the
    // one-time prefill without overwriting it.
    if (form.monthly_budget_usd.trim()) {
      budgetPrefilledRef.current = true;
      return;
    }
    budgetPrefilledRef.current = true;
    setBudgetSource('workspace');
    setForm((prev) => ({
      ...prev,
      monthly_budget_usd: tenantSettingsBudget,
    }));
    // Keep the daily overlay in sync if the operator is already in daily mode.
    if (budgetUnit === 'daily') {
      setDailyBudgetInput(monthlyToDailyDisplay(tenantSettingsBudget));
    }
  }, [budgetUnit, form.monthly_budget_usd, tenantSettingsBudget]);

  // When the operator switches to the daily unit, derive the daily entry box
  // from the canonical monthly value. Keyed ONLY on budgetUnit so per-keystroke
  // monthly edits do not clobber in-progress daily typing (e.g. "33.").
  useEffect(() => {
    if (budgetUnit === 'daily') {
      setDailyBudgetInput(monthlyToDailyDisplay(form.monthly_budget_usd));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetUnit]);

  const applyKeywordHandoff = useCallback(
    (options: { overwrite: boolean }) => {
      if (!keywordHandoff) return;
      const matchedPages = handoffMatchedPages(keywordHandoff);

      // ⛔ THE TOAST USED TO LIE. It always said "N keywords applied to campaign
      // setup", including when Apply changed NOTHING because both fields were
      // already filled — Apply only writes into blank fields. The operator got
      // a success message for a no-op, which is why Apply and Replace felt
      // identical. Report what actually happened.
      const changed: string[] = [];
      const skipped: string[] = [];

      setForm((prev) => {
        const keywordsBlank = !prev.existing_target_keywords.trim();
        const pagesBlank = !prev.pages.trim();
        const writeKeywords = options.overwrite || keywordsBlank;
        const writePages = options.overwrite || pagesBlank;

        (writeKeywords ? changed : skipped).push('keywords');
        (writePages ? changed : skipped).push('landing pages');

        return {
          ...prev,
          existing_target_keywords: writeKeywords
            ? joinLines(keywordHandoff.selected_keywords)
            : prev.existing_target_keywords,
          pages: writePages ? joinLines(matchedPages) : prev.pages,
        };
      });

      const kwCount = keywordHandoff.selected_keywords.length;
      const plural = kwCount === 1 ? '' : 's';
      if (changed.length === 0) {
        // Not a success — nothing moved. Say so, and say why, and name the
        // button that WOULD do it.
        stickyToast.info(
          `Nothing changed — keywords and landing pages already have values. ` +
          `Use "Replace inputs" to overwrite them with the package.`,
        );
      } else if (skipped.length > 0) {
        stickyToast.success(
          `${changed.join(' and ')} filled from the package. ` +
          `${skipped.join(' and ')} left as you had ${skipped.length === 1 ? 'it' : 'them'} — ` +
          `"Replace inputs" overwrites everything.`,
        );
      } else if (options.overwrite) {
        stickyToast.success(
          `Keywords and landing pages REPLACED with the package (${kwCount} keyword${plural}, ${matchedPages.length} page${matchedPages.length === 1 ? '' : 's'}).`,
        );
      } else {
        stickyToast.success(
          `${kwCount} keyword${plural} and ${matchedPages.length} landing page${matchedPages.length === 1 ? '' : 's'} applied to campaign setup.`,
        );
      }
    },
    [keywordHandoff],
  );

  // The package is the only copy of these values — it is browser state, not a
  // server record, and Replace overwrites the form from it. So it has to be
  // editable HERE, or an operator who wants slightly different keywords has to
  // apply first and then edit, which the next Replace silently undoes.
  const editKeywordHandoff = useCallback(
    (patch: { selected_keywords?: string[]; pages?: string[] }) => {
      setKeywordHandoff((prev) => {
        if (!prev) return prev;
        const next = { ...prev, ...patch };
        try {
          window.localStorage.setItem(
            keywordResearchCampaignHandoffKey(tenantId),
            JSON.stringify(next),
          );
        } catch {
          // A full or blocked localStorage must not lose the in-memory edit —
          // the operator keeps working, the value just does not survive reload.
        }
        return next;
      });
    },
    [tenantId],
  );

  const clearKeywordHandoff = useCallback(() => {
    window.localStorage.removeItem(keywordResearchCampaignHandoffKey(tenantId));
    setKeywordHandoff(null);
    setHandoffApplied(false);
    stickyToast.success('Keywords from Keyword Research removed');
  }, [tenantId]);

  // ⛔ THE HANDOFF IS BROWSER STATE, NOT VERIFIED STATE. The Keyword Research
  // campaign package is read from localStorage and rendered as-is, so a package
  // can outlive the run it points at — the run has a 30-day TTL, and staging or
  // database changes can remove it sooner. Nothing checked, so "View run"
  // happily navigated to a dead id and the operator got
  // "Request failed with status code 404", which reads as an outage rather than
  // a stale link. Verify once, cheaply, and let the banner say so.
  const handoffRunQuery = useResearchRun(keywordHandoff?.run_id ?? null);
  const handoffRunMissing =
    !!keywordHandoff?.run_id && handoffRunQuery.isError;

  const viewKeywordResearchRun = useCallback(() => {
    if (!keywordHandoff?.run_id) return;
    navigate(
      `/ads-tools/keyword-research?platform=google-ads&research_tab=history&run=${keywordHandoff.run_id}`,
    );
  }, [keywordHandoff?.run_id, navigate]);

  const appendSetupLog = useCallback(
    (entry: Omit<CampaignSetupLogEntry, 'id' | 'createdAt'>) => {
      setupLogIdRef.current += 1;
      const createdAt = new Date().toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      });
      setSetupLogs((prev) => [
        {
          ...entry,
          id: setupLogIdRef.current,
          createdAt,
        },
        ...prev,
      ].slice(0, 12));
    },
    [],
  );

  // Rehydrate the create form from a prior run's run_input (reverse of the
  // setup mutation's form->request mapping). Used by "Edit & Retry" on a FAILED
  // run so the operator can adjust the inputs and submit a fresh run instead of
  // re-filling the whole builder. The failed run itself is left untouched.
  const hydrateFormFromRun = (runInput: Record<string, unknown>) => {
    const str = (value: unknown): string =>
      value === null || value === undefined ? '' : String(value);
    const num = (value: unknown): number | null => {
      const parsed =
        typeof value === 'number' ? value : Number.parseFloat(String(value));
      return Number.isFinite(parsed) ? parsed : null;
    };
    const list = (value: unknown): string =>
      Array.isArray(value) ? joinLines(value.map((item) => String(item))) : '';

    const dailyMicros = num(runInput.daily_budget_micros);
    const monthlyBudgetUsd =
      dailyMicros !== null
        ? String(
            Math.round(
              (dailyMicros / MICROS_PER_USD) * CAMPAIGN_PACING_DAYS * 100,
            ) / 100,
          )
        : '';
    const targetCpaMicros = num(runInput.target_cpa_micros);
    const targetRoas = num(runInput.target_roas);

    const next: CampaignFormState = {
      ...EMPTY_FORM,
      business_name: str(runInput.business_name),
      monthly_budget_usd: monthlyBudgetUsd,
      active_service_days_per_month: str(runInput.active_service_days_per_month),
      campaign_goal:
        (runInput.campaign_goal as CampaignGoal | undefined) ??
        EMPTY_FORM.campaign_goal,
      bidding_strategy:
        (runInput.bidding_strategy as CampaignBiddingStrategy | undefined) ??
        EMPTY_FORM.bidding_strategy,
      target_cpa_usd:
        targetCpaMicros !== null ? String(targetCpaMicros / MICROS_PER_USD) : '',
      target_roas_percent: targetRoas !== null ? String(targetRoas * 100) : '',
      smart_bidding_override_ack: str(runInput.smart_bidding_override_ack),
      budget_allocation_strategy:
        (runInput.budget_allocation_strategy as
          | CampaignBudgetAllocationStrategy
          | undefined) ?? EMPTY_FORM.budget_allocation_strategy,
      campaign_structure:
        (runInput.campaign_structure as CampaignStructure | undefined) ??
        EMPTY_FORM.campaign_structure,
      campaign_type:
        (runInput.campaign_type as 'SEARCH' | 'PERFORMANCE_MAX' | undefined) ??
        EMPTY_FORM.campaign_type,
      target_search_network: Boolean(runInput.target_search_network),
      target_content_network: Boolean(runInput.target_content_network),
      rsa_variations:
        Number(runInput.rsa_variations) || EMPTY_FORM.rsa_variations,
      target_address: str(runInput.target_address),
      gbp_location_resource_name: str(runInput.gbp_location_resource_name),
      primary_phone: str(runInput.primary_phone),
      company_information: str(runInput.company_information),
      services_offered: str(runInput.services_offered),
      value_proposition: str(runInput.value_proposition),
      call_to_action: str(runInput.call_to_action),
      promotions_specials: str(runInput.promotions_specials),
      existing_target_keywords: list(runInput.existing_target_keywords),
      pages: list(runInput.pages),
      status_on_launch: 'PAUSED',
      approval_expires_in_hours:
        runInput.approval_expires_in_hours === null ||
        runInput.approval_expires_in_hours === undefined
          ? EMPTY_FORM.approval_expires_in_hours
          : str(runInput.approval_expires_in_hours),
    };

    setForm(next);

    const approvalChannels = Array.isArray(runInput.approval_channels)
      ? (runInput.approval_channels as unknown[]).map((channel) => String(channel))
      : [];
    setSendSlackApproval(approvalChannels.includes('slack'));
    setExpandCampaignKeywords(Boolean(runInput.keyword_expansion_enabled));
    // We hydrate the canonical monthly budget, so pin the entry unit to monthly
    // and stop the workspace one-time prefill effect from clobbering it.
    setBudgetUnit('monthly');
    budgetPrefilledRef.current = true;
    // Force the operator to re-confirm the budget before submitting again.
    setBudgetConfirmed(false);

    setActiveCampaignTab('create');
    updateCampaignSearchParams({ campaignTab: 'create', runId: null });
    stickyToast.success("Loaded the failed run's settings so you can fix them and retry");
  };

  // Full-page building overlay while campaign setup is in flight.
  // "Continue in background" hides it for the current submission only —
  // it re-arms every time a new setup request starts.
  const [setupOverlayDismissed, setSetupOverlayDismissed] = useState(false);

  const setup = useMutation({
    mutationFn: () => {
      const monthly = Number.parseFloat(form.monthly_budget_usd) || 0;
      const expiresHours =
        Number.parseInt(form.approval_expires_in_hours, 10) || 48;
      const activeServiceDays = Number.parseFloat(form.active_service_days_per_month);
      const activeServiceDaysPerMonth =
        Number.isFinite(activeServiceDays) && activeServiceDays > 0
          ? activeServiceDays
          : null;
      const targetCpaUsd = Number.parseFloat(form.target_cpa_usd);
      const targetRoasPercent = Number.parseFloat(form.target_roas_percent);
      const keywords = splitLines(form.existing_target_keywords);
      const pages = splitLines(form.pages);
      const selectedKeywordSet = new Set(
        keywords.map((keyword) => keyword.toLowerCase()),
      );
      const existingTargetKeywordDetails = keywordHandoff
        ? campaignPackageKeywordDetails(keywordHandoff).filter((keyword) =>
            selectedKeywordSet.has(keyword.keyword.toLowerCase()),
          )
        : [];
      const approvalChannels = sendSlackApproval ? ['ui', 'slack'] : ['ui'];
      appendSetupLog({
        status: 'info',
        stage: 'start',
        message: 'Submitting campaign setup request.',
        detail: `${keywords.length} keyword${keywords.length === 1 ? '' : 's'}, ${pages.length} page${pages.length === 1 ? '' : 's'}, monthly budget $${monthly.toFixed(2)}.`,
      });
      appendSetupLog({
        status: 'info',
        stage: 'budget',
        message: 'Converted campaign budget to Google Ads daily micros.',
        detail: `Type: ${form.campaign_type}. Daily budget micros: ${dailyBudgetMicros(monthly).toLocaleString()}. Goal: ${form.campaign_goal}. Bidding: ${form.bidding_strategy}. Allocation: ${form.budget_allocation_strategy}. Structure: ${form.campaign_structure}. Active days/month: ${activeServiceDaysPerMonth ?? 'not set'}. Search partners: ${form.target_search_network ? 'on' : 'off'}. Display: ${form.target_content_network ? 'on' : 'off'}. Launch: ${form.status_on_launch}.`,
      });
      appendSetupLog({
        status: 'info',
        stage: 'approval',
        message: sendSlackApproval
          ? 'Approval will be sent to the webapp and #core-alerts.'
          : 'Approval will stay in the webapp only.',
        detail: `Approval channels: ${approvalChannels.join(', ')}.`,
      });
      appendSetupLog({
        status: 'info',
        stage: 'keywords',
        message: expandCampaignKeywords
          ? 'Related keyword expansion is enabled.'
          : 'Using only the selected campaign keywords.',
        detail: expandCampaignKeywords
          ? 'Keyword Planner may add related keyword ideas before clustering.'
          : 'Campaign Creator will not add Keyword Planner related ideas.',
      });
      return setupCampaign({
        tenant_id: tenantId,
        business_name: form.business_name.trim(),
        campaign_type: form.campaign_type,
        daily_budget_micros: dailyBudgetMicros(monthly),
        active_service_days_per_month: activeServiceDaysPerMonth,
        campaign_goal: form.campaign_goal,
        bidding_strategy: form.bidding_strategy,
        target_cpa_micros:
          form.bidding_strategy === 'TARGET_CPA' && targetCpaUsd > 0
            ? Math.round(targetCpaUsd * MICROS_PER_USD)
            : undefined,
        target_roas:
          form.bidding_strategy === 'TARGET_ROAS' && targetRoasPercent > 0
            ? targetRoasPercent / 100
            : undefined,
        smart_bidding_override_ack: form.smart_bidding_override_ack.trim(),
        budget_allocation_strategy: form.budget_allocation_strategy,
        campaign_structure: form.campaign_structure,
        target_search_network: form.target_search_network,
        target_content_network: form.target_content_network,
        rsa_variations: form.rsa_variations,
        target_address: form.target_address.trim(),
        gbp_location_resource_name: form.gbp_location_resource_name.trim(),
        primary_phone: form.primary_phone.trim(),
        company_information: form.company_information.trim(),
        existing_target_keywords: keywords,
        existing_target_keyword_details: existingTargetKeywordDetails,
        pages,
        keyword_expansion_enabled: expandCampaignKeywords,
        services_offered: form.services_offered.trim(),
        value_proposition: form.value_proposition.trim(),
        call_to_action: form.call_to_action.trim(),
        promotions_specials: form.promotions_specials.trim(),
        status_on_launch: form.status_on_launch,
        approval_channels: approvalChannels,
        approval_expires_in_hours: expiresHours,
        ad_review: form.ad_review,
      });
    },
    onSuccess: (run) => {
      // /setup returns immediately at CLIENT_BOOTSTRAP; the pipeline (geocode →
      // GBP → keyword research → approval gate) runs detached server-side.
      //
      // Move to the tab the run is heading for rather than leaving the operator
      // on the builder form watching a card appear beneath it. An in-progress
      // run is on its way to KW_APPROVAL, so 'approvals' is where it belongs —
      // the run-panel overlay there polls every 5s and offers Cancel.
      const isInProgress =
        run.state === 'CLIENT_BOOTSTRAP' || run.state === 'KEYWORD_RESEARCH';
      const nextCampaignTab = isInProgress
        ? 'approvals'
        : campaignTabForRun(run);
      setActiveCampaignTab(nextCampaignTab);
      updateCampaignSearchParams({
        runId: run.id,
        campaignTab: nextCampaignTab,
      });
      queryClient.invalidateQueries({
        queryKey: ['google-ads-campaign-runs', tenantId],
      });
      appendSetupLog({
        status: 'success',
        stage: 'created',
        message: 'Campaign setup run was created.',
        detail: `Run ${run.id}; state ${run.state}. Keyword research runs in the background. You can cancel from the run panel.`,
      });
      if (run.state === 'FAILED') {
        stickyToast.error(
          `The campaign run failed before approval. Open History to see run ${run.id.slice(0, 8)}.`,
        );
      } else if (run.state === 'KW_APPROVAL') {
        stickyToast.success(
          `Keywords are ready for your approval (run ${run.id.slice(0, 8)}).`,
        );
      } else {
        stickyToast.success(
          `Campaign run started. Researching keywords now, you can cancel from the run panel (run ${run.id.slice(0, 8)}).`,
        );
      }
    },
    onError: (err: unknown) => {
      const message = describeCampaignSetupError(err);
      setActiveCampaignTab('logs');
      updateCampaignSearchParams({ campaignTab: 'logs', runId: null });
      appendSetupLog({
        status: 'error',
        stage: 'error',
        message: 'Campaign setup stopped before returning a run.',
        detail: message,
      });
      stickyToast.error(`Setup failed: ${message}`);
    },
  });
  const setupIsPending = setup.isPending;

  // Re-arm the setup overlay each time a new setup submission starts.
  useEffect(() => {
    if (setupIsPending) setSetupOverlayDismissed(false);
  }, [setupIsPending]);

  const update =
    <K extends keyof CampaignFormState>(field: K) =>
    (value: CampaignFormState[K]) => {
      if (field === 'monthly_budget_usd') {
        setBudgetConfirmed(false);
        setBudgetSource('manual');
      }
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const applyFieldSuggestion = <K extends keyof CampaignFormState>(
    field: K,
    value: CampaignFormState[K],
  ) => {
    if (field === 'monthly_budget_usd') {
      setBudgetConfirmed(false);
      setBudgetSource('workspace');
      if (budgetUnit === 'daily') {
        setDailyBudgetInput(monthlyToDailyDisplay(String(value ?? '')));
      }
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    stickyToast.success('Suggestion applied');
  };

  const autofillCampaignFields = () => {
    const next: CampaignFormState = { ...form };
    let filledCount = 0;

    // ⛔ NEVER AUTOFILL INSTRUCTION TEXT. A campaign run went to Google with
    // target_address = "Primary service address or market" — the placeholder
    // itself — and died on Geocoding ZERO_RESULTS after the operator had
    // already approved it.
    //
    // The fallback path below was already guarded against exactly this. The
    // SUGGESTION LIST was not: target_address offers the placeholder as a
    // 'default'-source entry, so when the workspace has no address the real
    // entries drop out of uniqueCampaignSuggestions and the instruction text
    // becomes suggestion [0]. Same poison, a door the guard did not cover.
    //
    // 'default'-source suggestions stay VISIBLE as hints; they are just never
    // written into a field.
    const firstSuggestion = (field: keyof CampaignFormState, fallback = '') =>
      (fieldSuggestions[field] ?? [])
        .find((suggestion) => suggestion.source !== 'default')
        ?.value.trim() || fallback.trim();

    const fillBlank = <K extends keyof CampaignFormState>(
      field: K,
      value: CampaignFormState[K],
    ) => {
      const currentValue = String(next[field] ?? '').trim();
      const nextValue = String(value ?? '').trim();
      if (currentValue || !nextValue) return;
      next[field] = value;
      filledCount += 1;
    };

    // ⛔ FALLBACKS COME FROM autofillValues, NEVER FROM placeholders.
    // placeholders mixes real data with INSTRUCTION TEXT, so using it here
    // filled the form with the guidance telling the operator what to write —
    // which was then submitted and fed to keyword generation. See
    // buildCampaignAutofillValues. Every fallback below is data or absent.
    fillBlank('business_name', firstSuggestion('business_name', autofillValues.businessName));
    fillBlank('monthly_budget_usd', firstSuggestion('monthly_budget_usd'));
    fillBlank('target_address', firstSuggestion('target_address', autofillValues.targetAddress));
    fillBlank('primary_phone', firstSuggestion('primary_phone'));
    fillBlank(
      'company_information',
      firstSuggestion('company_information', autofillValues.companyInformation),
    );
    fillBlank('services_offered', firstSuggestion('services_offered', autofillValues.servicesOffered));
    fillBlank(
      'value_proposition',
      firstSuggestion('value_proposition', autofillValues.valueProposition),
    );
    fillBlank('call_to_action', firstSuggestion('call_to_action', autofillValues.callToAction));
    fillBlank('promotions_specials', firstSuggestion('promotions_specials'));
    fillBlank(
      'existing_target_keywords',
      firstSuggestion('existing_target_keywords', autofillValues.seedKeywords),
    );
    fillBlank('pages', firstSuggestion('pages', autofillValues.pages));
    fillBlank('approval_expires_in_hours', '48');

    setForm(next);
    if (!form.monthly_budget_usd.trim() && next.monthly_budget_usd.trim()) {
      setBudgetSource('workspace');
    }
    if (budgetUnit === 'daily') {
      setDailyBudgetInput(monthlyToDailyDisplay(next.monthly_budget_usd));
    }
    appendSetupLog({
      status: 'success',
      stage: 'autofill',
      message:
        filledCount > 0
          ? `Autofilled ${filledCount} campaign field${filledCount === 1 ? '' : 's'}.`
          : 'Autofill checked all campaign fields.',
      detail: 'Values you had already entered were kept.',
    });
    stickyToast.success(
      filledCount > 0
        ? `Autofilled ${filledCount} campaign field${filledCount === 1 ? '' : 's'}`
        : 'Campaign fields are already filled',
    );
  };

  const monthlyBudgetUsd = Number.parseFloat(form.monthly_budget_usd) || 0;
  const budgetIsSet = monthlyBudgetUsd > 0;
  const targetCpaUsd = Number.parseFloat(form.target_cpa_usd) || 0;
  const targetRoasPercent = Number.parseFloat(form.target_roas_percent) || 0;
  const selectedSmartBiddingThreshold = smartBiddingThreshold(form.bidding_strategy);
  const biddingTargetIsValid =
    (form.bidding_strategy !== 'TARGET_CPA' || targetCpaUsd > 0) &&
    (form.bidding_strategy !== 'TARGET_ROAS' || targetRoasPercent > 0);
  const budgetSourceLabel =
    budgetSource === 'workspace'
      ? 'Workspace budget'
      : budgetSource === 'manual'
        ? 'Manual campaign budget'
        : 'No budget selected';
  const campaignFormBlockers: CampaignFormBlocker[] = [
    !form.business_name.trim()
      ? { message: 'Business name is required.', anchorId: 'blocker-business_name' }
      : null,
    !form.target_address.trim()
      ? { message: 'Target address is required.', anchorId: 'blocker-target_address' }
      : null,
    !form.company_information.trim()
      ? {
          message: 'Company information is required.',
          anchorId: 'blocker-company_information',
        }
      : null,
    !form.services_offered.trim()
      ? {
          message: 'Services offered is required.',
          anchorId: 'blocker-services_offered',
        }
      : null,
    !form.value_proposition.trim()
      ? {
          message: 'Value proposition is required.',
          anchorId: 'blocker-value_proposition',
        }
      : null,
    !form.call_to_action.trim()
      ? { message: 'Call to action is required.', anchorId: 'blocker-call_to_action' }
      : null,
    !budgetIsSet
      ? {
          message: 'Enter or load a monthly budget.',
          anchorId: 'blocker-monthly_budget_usd',
        }
      : null,
    form.bidding_strategy === 'TARGET_CPA' && targetCpaUsd <= 0
      ? { message: 'Enter a Target CPA amount.', anchorId: 'blocker-bidding_targets' }
      : null,
    form.bidding_strategy === 'TARGET_ROAS' && targetRoasPercent <= 0
      ? {
          message: 'Enter a Target ROAS percentage.',
          anchorId: 'blocker-bidding_targets',
        }
      : null,
    budgetIsSet && !budgetConfirmed
      ? {
          // The one that catches everyone: typing a budget is not confirming it,
          // and any edit to the budget resets budgetConfirmed back to false.
          message: 'Check the budget, then click Confirm budget.',
          anchorId: 'blocker-budget_confirmed',
        }
      : null,
  ].filter((blocker): blocker is CampaignFormBlocker => Boolean(blocker));
  const formIsValid = campaignFormBlockers.length === 0;
  const setupErrorMessage = setup.isError
    ? describeCampaignSetupError(setup.error)
    : null;
  const pacingKeywordDetails = keywordHandoff
    ? campaignPackageKeywordDetails(keywordHandoff)
    : [];

  return (
    <div className="space-y-6">
      <Tabs
        value={activeCampaignTab}
        onValueChange={(value) => {
          const nextTab = value as CampaignWorkflowTab;
          setActiveCampaignTab(nextTab);
          updateCampaignSearchParams({
            campaignTab: nextTab,
            runId: null,
          });
        }}
      >
        <TabsList>
          <TabsTrigger value="create">Campaign Builder</TabsTrigger>
          <TabsTrigger value="approvals" className="gap-1.5">
            Approvals
            {pendingApprovalRuns.length > 0 ? (
              <Badge
                variant="outline"
                className="border-coral/40 bg-coral-light px-1.5 text-[11px] font-semibold text-coral-dark"
              >
                {pendingApprovalRuns.length}
              </Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="ad-review" className="gap-1.5">
            Ad Review
            {adReviewCount > 0 ? (
              <Badge
                variant="outline"
                className="border-coral/40 bg-coral-light px-1.5 text-[11px] font-semibold text-coral-dark"
              >
                {adReviewCount}
              </Badge>
            ) : null}
          </TabsTrigger>
          {/* CORE-M-11.b.2: Current Campaigns trigger retired — the
              drill-down under /ads-tools/management is the new home
              (Q-2 ruling). The run panel's "Open Ads Management"
              button below navigates operators there. */}
          <TabsTrigger value="logs">Setup Log</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="create" className="mt-4 space-y-6">
          <PendingApprovalBanner
            runs={pendingApprovalRuns}
            onOpenRun={(runId) => {
              setActiveCampaignTab('approvals');
              updateCampaignSearchParams({ campaignTab: 'approvals', runId });
            }}
          />
          <PolicyChecklist checks={CAMPAIGN_POLICY_PREVIEW} />

      <div className="rounded-md border border-teal/30 bg-teal-light/30 p-3 text-sm">
        <div className="flex items-center gap-2 font-medium text-teal-dark">
          <CheckCircle2 className="h-4 w-4" />
          MVP write-safety guardrails
        </div>
        <div className="mt-2 grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
          <div>
            <span className="font-medium text-foreground">Launch state:</span>{' '}
            campaign, ad groups, and RSAs are created paused.
          </div>
          <div>
            <span className="font-medium text-foreground">Approval gate:</span>{' '}
            keyword clusters wait for your approval before anything is changed in Google Ads.
          </div>
          <div>
            <span className="font-medium text-foreground">Writer layer:</span>{' '}
            Google Ads mutations are routed through dry-run/audit logging.
          </div>
        </div>
      </div>

      {keywordHandoff ? (
        <CampaignKeywordHandoffPanel
          handoff={keywordHandoff}
          applied={handoffApplied}
          keywordInputCount={splitLines(form.existing_target_keywords).length}
          pageInputCount={splitLines(form.pages).length}
          onApply={() => applyKeywordHandoff({ overwrite: false })}
          onReplace={() => applyKeywordHandoff({ overwrite: true })}
          onClear={clearKeywordHandoff}
          onViewRun={viewKeywordResearchRun}
          runMissing={handoffRunMissing}
          onEdit={editKeywordHandoff}
        />
      ) : (
        /*
          ⛔ CLEARING USED TO DELETE THE WHOLE SECTION, leaving a dead end. The
          campaign builder has no other link to Keyword Research, so once the
          package was gone nothing on the page said where packages come from or
          how to get another. The operator was expected to know to navigate to a
          different screen, run research, select keywords and save a package.

          Deliberately NOT a copy of the keyword research form. That form needs
          seeds, pages, geo, language and a site filter, and it already exists on
          its own screen — duplicating it inline would be two places to maintain
          and two places to diverge. The section keeps its slot and points at the
          tool instead.
        */
        <div className="rounded-md border border-dashed border-border bg-warm-bg p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="rounded-md bg-muted p-2 text-muted-foreground">
                <Target className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-sm font-semibold">
                  No Keyword Research campaign package
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Run keyword research, select the keywords you want, and save
                  them as a campaign package to load them here. You can also
                  build a campaign without one by typing keywords and landing
                  pages directly below.
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                navigate('/ads-tools/keyword-research?platform=google-ads')
              }
            >
              <ExternalLink className="mr-1 h-3.5 w-3.5" />
              Open Keyword Research
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-4 w-4 text-teal" />
                Create new campaign
              </CardTitle>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-warm-bg"
              onClick={autofillCampaignFields}
            >
              <Sparkles className="mr-1 h-3.5 w-3.5" />
              Autofill all fields
            </Button>
          </div>
          <CardDescription>
            Fill this in and start the run. We research keywords, you approve
            them, then the campaign, ad groups and ads are created in Google
            Ads. Everything starts paused, so nothing spends until you turn it
            on in Google Ads.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-coral/30 bg-coral-light/20 p-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <Label className="text-xs font-medium uppercase text-coral-dark">
                  Campaign budget
                  <span className="ml-1 text-destructive">*</span>
                </Label>
                <div className="mt-1 text-sm text-muted-foreground">
                  The budget in USD. Use the saved workspace budget or enter one for this campaign. Google Ads works in daily budgets, so we convert it for you.
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge
                    variant="outline"
                    className={
                      budgetConfirmed
                        ? 'border-teal/30 bg-teal-light text-teal-dark'
                        : 'border-coral/40 bg-card text-coral-dark'
                    }
                  >
                    {budgetConfirmed ? 'Budget confirmed' : 'Budget not confirmed'}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    Source: {budgetSourceLabel}
                  </span>
                </div>
              </div>
              <div className="w-full sm:w-56">
                <div className="mb-1 inline-flex rounded-md border border-coral/30 bg-card p-0.5">
                  <button
                    type="button"
                    onClick={() => setBudgetUnit('monthly')}
                    className={`rounded px-2.5 py-0.5 text-xs font-medium transition-colors ${
                      budgetUnit === 'monthly'
                        ? 'bg-coral text-white shadow-sm'
                        : 'text-coral-dark hover:bg-coral-light'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setBudgetUnit('daily')}
                    className={`rounded px-2.5 py-0.5 text-xs font-medium transition-colors ${
                      budgetUnit === 'daily'
                        ? 'bg-coral text-white shadow-sm'
                        : 'text-coral-dark hover:bg-coral-light'
                    }`}
                  >
                    Daily
                  </button>
                </div>
                <Input
                  id="blocker-monthly_budget_usd"
                  type="number"
                  min="1"
                  step={budgetUnit === 'daily' ? '1' : '10'}
                  value={
                    budgetUnit === 'daily'
                      ? dailyBudgetInput
                      : form.monthly_budget_usd
                  }
                  onChange={(e) => {
                    if (budgetUnit === 'daily') {
                      // Keep the raw daily text so in-progress decimals ("33.")
                      // are never re-derived/clobbered per keystroke.
                      setDailyBudgetInput(e.target.value);
                      update('monthly_budget_usd')(
                        dailyToMonthlyCanonical(e.target.value),
                      );
                    } else {
                      update('monthly_budget_usd')(e.target.value);
                    }
                  }}
                  className="scroll-mt-24 bg-card text-base font-semibold"
                />
                <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                  {budgetUnit === 'monthly' ? (
                    <div>
                      About {budgetHintUsd(monthlyBudgetUsd / CAMPAIGN_PACING_DAYS)}{' '}
                      a day
                    </div>
                  ) : (
                    <div>
                      About {budgetHintUsd(monthlyBudgetUsd)} a month
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                id="blocker-budget_confirmed"
                type="button"
                size="sm"
                className="scroll-mt-24 bg-coral text-white shadow-md hover:bg-coral-hover hover:shadow-lg"
                disabled={!budgetIsSet}
                onClick={() => {
                  setBudgetConfirmed(true);
                    appendSetupLog({
                      status: 'success',
                      stage: 'budget',
                      message: 'Campaign budget confirmed.',
                      detail: `${budgetSourceLabel}: $${monthlyBudgetUsd.toFixed(2)} budget; ${dailyBudgetMicros(monthlyBudgetUsd).toLocaleString()} daily micros.`,
                    });
                  }}
                >
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                Confirm budget
              </Button>
              {tenantSettingsBudget ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="bg-card"
                  onClick={() => {
                    setBudgetConfirmed(false);
                    setBudgetSource('workspace');
                    setForm((prev) => ({
                      ...prev,
                      monthly_budget_usd: tenantSettingsBudget,
                    }));
                    if (budgetUnit === 'daily') {
                      setDailyBudgetInput(
                        monthlyToDailyDisplay(tenantSettingsBudget),
                      );
                    }
                  }}
                >
                  Use workspace budget (${tenantSettingsBudget})
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="bg-card"
                onClick={() => navigate('/ads-tools/budgets?platform=google-ads')}
              >
                Open Budgets tab
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/workspace-settings')}
              >
                Open Google Ads settings
              </Button>
            </div>
            {!budgetIsSet ? (
              <div className="mt-2 rounded border border-coral/30 bg-card px-3 py-2 text-xs text-coral-dark">
                No budget yet. Enter one here or save one in Workspace Settings.
              </div>
            ) : !budgetConfirmed ? (
              <div className="mt-2 rounded border border-coral/30 bg-card px-3 py-2 text-xs text-coral-dark">
                Check the budget estimate below, then click Confirm budget.
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              label="Workspace"
              hint="From the workspace you have open"
            >
              <div className="rounded border border-border bg-warm-bg px-3 py-2 text-sm">
                {tenantId}
              </div>
            </FormField>
            <FormField
              label="Business name"
              anchorId="blocker-business_name"
              hint="Used in the campaign name and the ads"
            guideFor="business_name"
              required
            >
              <Input
                value={form.business_name}
                onChange={(e) => update('business_name')(e.target.value)}
                placeholder={placeholders.businessName}
              />
              <FieldSuggestions
                suggestions={fieldSuggestions.business_name}
                onApply={(value) => applyFieldSuggestion('business_name', value)}
              />
            </FormField>
            <FormField
              label="Target address"
              anchorId="blocker-target_address"
              hint="Ads show to people near this address"
            guideFor="target_address"
              required
            >
              <Input
                value={form.target_address}
                onChange={(e) => update('target_address')(e.target.value)}
                placeholder={placeholders.targetAddress}
              />
              <FieldSuggestions
                suggestions={fieldSuggestions.target_address}
                onApply={(value) => applyFieldSuggestion('target_address', value)}
              />
            </FormField>
            <FormField
              label="Google Business Profile location"
              hint="Optional. Used to pull the phone number from the profile."
            >
              <Input
                value={form.gbp_location_resource_name}
                onChange={(e) =>
                  update('gbp_location_resource_name')(e.target.value)
                }
                placeholder="locations/..."
              />
            </FormField>
            <FormField
              label="Phone number for call ads"
              hint="Optional. Used when there is no Business Profile."
            >
              <Input
                value={form.primary_phone}
                onChange={(e) => update('primary_phone')(e.target.value)}
                placeholder={placeholders.primaryPhone}
              />
              <FieldSuggestions
                suggestions={fieldSuggestions.primary_phone}
                onApply={(value) => applyFieldSuggestion('primary_phone', value)}
              />
            </FormField>
            <FormField
              label="Status on launch"
              hint="Always paused. You turn it on in Google Ads."
            >
              <div className="flex min-h-10 items-center rounded border border-border bg-warm-bg px-3">
                <Badge
                  variant="outline"
                  className="border-teal/30 bg-teal-light text-teal-dark"
                >
                  Paused
                </Badge>
              </div>
            </FormField>
            <div className="rounded-md border border-teal/20 bg-teal-light/20 p-3">
              <div className="text-sm font-medium">Launch defaults</div>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <Badge variant="outline" className="bg-card">
                  Starts paused
                </Badge>
                <Badge variant="outline" className="bg-card">
                  Google Search only
                </Badge>
                <Badge variant="outline" className="bg-card">
                  Phrase-match keywords
                </Badge>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                These are always applied, so there is nothing to remember at
                launch.
              </div>
            </div>
          </div>

          <div className="rounded-md border border-border bg-warm-bg p-3">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="text-sm font-medium">Network settings</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Ads always show on Google Search. The two options below are off by default.
                </div>
              </div>
              <Badge variant="outline" className="w-fit bg-card">
                Search campaign
              </Badge>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div className="flex items-start gap-3 rounded border border-border bg-card px-3 py-2">
                <Checkbox
                  id="campaign-search-partners"
                  checked={form.target_search_network}
                  onCheckedChange={(checked) =>
                    update('target_search_network')(checked === true)
                  }
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label
                    htmlFor="campaign-search-partners"
                    className="cursor-pointer text-sm font-medium"
                  >
                    Include Google Search Partners
                  </Label>
                  <div className="text-xs text-muted-foreground">
                    Also shows ads on other sites that use Google search. Off by
                    default because those leads are usually lower quality.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded border border-border bg-card px-3 py-2">
                <Checkbox
                  id="campaign-display-network"
                  checked={form.target_content_network}
                  onCheckedChange={(checked) =>
                    update('target_content_network')(checked === true)
                  }
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label
                    htmlFor="campaign-display-network"
                    className="cursor-pointer text-sm font-medium"
                  >
                    Include Google Display Network
                  </Label>
                  <div className="text-xs text-muted-foreground">
                    Shows banner ads on other websites. Off by default. Search
                    only is recommended.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-md border border-border bg-card p-3">
            <SettingRow
              label="Campaign type"
              htmlFor="campaign-type"
              control={
                <Select
                  value={form.campaign_type}
                  onValueChange={(value) =>
                    update('campaign_type')(value as 'SEARCH' | 'PERFORMANCE_MAX')
                  }
                >
                  <SelectTrigger id="campaign-type" className="bg-warm-bg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CAMPAIGN_TYPE_COPY).filter(([value]) => value !== 'PERFORMANCE_MAX' || pmaxFeatureAvailable || form.campaign_type === 'PERFORMANCE_MAX').map(([value, copy]) => (
                      <SelectItem key={value} value={value}>
                        {copy.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              }
              explanation={
                <>
                  <p className="text-foreground">
                    {CAMPAIGN_TYPE_COPY[form.campaign_type].explanation}
                  </p>
                  <p>{CAMPAIGN_TYPE_INTRO}</p>
                </>
              }
            />
            {form.campaign_type === 'PERFORMANCE_MAX' && (
              <PmaxRequirementsPanel tenantId={tenantId} />
            )}
          </div>

          <div id="blocker-bidding_targets" className="scroll-mt-24">
          <CampaignBudgetPacingPanel
            monthlyBudgetUsd={monthlyBudgetUsd}
            keywordDetails={pacingKeywordDetails}
            activeServiceDaysPerMonth={
              Number.isFinite(Number.parseFloat(form.active_service_days_per_month))
                ? Number.parseFloat(form.active_service_days_per_month)
                : null
            }
            campaignGoal={form.campaign_goal}
            biddingStrategy={form.bidding_strategy}
            targetCpaUsd={form.target_cpa_usd}
            targetRoasPercent={form.target_roas_percent}
            allocationStrategy={form.budget_allocation_strategy}
            campaignStructure={form.campaign_structure}
            onBiddingStrategyChange={(value) => update('bidding_strategy')(value)}
            onCampaignGoalChange={(value) => {
              update('campaign_goal')(value);
              update('bidding_strategy')(recommendedBiddingStrategyForGoal(value));
            }}
            onTargetCpaChange={update('target_cpa_usd')}
            onTargetRoasChange={update('target_roas_percent')}
            onActiveServiceDaysChange={update('active_service_days_per_month')}
            onAllocationStrategyChange={(value) =>
              update('budget_allocation_strategy')(value)
            }
            onCampaignStructureChange={(value) => update('campaign_structure')(value)}
          />
          </div>

          {campaignGoalRequiresConversionTracking(form.campaign_goal) && (
            <div className="rounded-md border border-coral/40 bg-coral-light p-4">
              <div className="flex items-start gap-2">
                <TriangleAlert className="mt-0.5 h-4 w-4 text-coral-dark" />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="text-sm font-medium text-coral-dark">
                    Conversion tracking needed
                  </div>
                  <div className="text-xs text-coral-dark/80">
                    This goal needs a main conversion in Google Ads that counts
                    real leads, like calls or form fills. If the only main
                    conversion is a page view, the campaign won't be created.
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedSmartBiddingThreshold !== null && (
            <div className="rounded-md border border-coral/40 bg-coral-light p-4">
              <div className="flex items-start gap-2">
                <TriangleAlert className="mt-0.5 h-4 w-4 text-coral-dark" />
                <div className="min-w-0 flex-1 space-y-3">
                  <div>
                    <div className="text-sm font-medium text-coral-dark">
                      Enough conversion history?
                    </div>
                    <div className="mt-1 text-xs text-coral-dark/80">
                      {campaignBiddingStrategyLabel(form.bidding_strategy)} needs at
                      least {selectedSmartBiddingThreshold} conversions in the last
                      30 days. If the account has fewer, the campaign won't be
                      created unless you type this exact phrase to confirm.
                    </div>
                  </div>
                  <Input
                    value={form.smart_bidding_override_ack}
                    onChange={(e) =>
                      update('smart_bidding_override_ack')(e.target.value)
                    }
                    placeholder={SMART_BIDDING_OVERRIDE_ACK}
                    className="bg-card"
                  />
                  <div className="text-xs text-coral-dark/80">
                    Required phrase:{' '}
                    <code className="rounded bg-card px-1 py-0.5 text-[11px]">
                      {SMART_BIDDING_OVERRIDE_ACK}
                    </code>
                  </div>
                </div>
              </div>
            </div>
          )}

          <FormField
            label="Company information"
              anchorId="blocker-company_information"
            hint="1 to 3 sentences about the business. Used to find keywords."
            guideFor="company_information"
            required
          >
            <Textarea
              rows={3}
              value={form.company_information}
              onChange={(e) => update('company_information')(e.target.value)}
              placeholder={placeholders.companyInformation}
            />
            <FieldSuggestions
              suggestions={fieldSuggestions.company_information}
              onApply={(value) => applyFieldSuggestion('company_information', value)}
            />
          </FormField>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              label="Services offered"
              anchorId="blocker-services_offered"
              hint="Used in the ad extras (callouts and service lists)"
              guideFor="services_offered"
              required
            >
              <Textarea
                rows={2}
                value={form.services_offered}
                onChange={(e) => update('services_offered')(e.target.value)}
                placeholder={placeholders.servicesOffered}
              />
              <FieldSuggestions
                suggestions={fieldSuggestions.services_offered}
                onApply={(value) => applyFieldSuggestion('services_offered', value)}
              />
            </FormField>
            <FormField
              label="Value proposition"
              anchorId="blocker-value_proposition"
              hint="Why customers pick this business. Used in ad headlines."
            guideFor="value_proposition"
              required
            >
              <Textarea
                rows={2}
                value={form.value_proposition}
                onChange={(e) => update('value_proposition')(e.target.value)}
                placeholder={placeholders.valueProposition}
              />
              <FieldSuggestions
                suggestions={fieldSuggestions.value_proposition}
                onApply={(value) => applyFieldSuggestion('value_proposition', value)}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              label="Call to action"
              anchorId="blocker-call_to_action"
              hint="Used in at least one ad headline"
            guideFor="call_to_action"
              required
            >
              <Input
                value={form.call_to_action}
                onChange={(e) => update('call_to_action')(e.target.value)}
                placeholder={placeholders.callToAction}
              />
              <FieldSuggestions
                suggestions={fieldSuggestions.call_to_action}
                onApply={(value) => applyFieldSuggestion('call_to_action', value)}
              />
            </FormField>
            <FormField
              label="Promotion or special"
              hint="Used in at least one ad description"
            >
              <Input
                value={form.promotions_specials}
                onChange={(e) => update('promotions_specials')(e.target.value)}
                placeholder={placeholders.promotion}
              />
              <FieldSuggestions
                suggestions={fieldSuggestions.promotions_specials}
                onApply={(value) => applyFieldSuggestion('promotions_specials', value)}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              label="Campaign keywords"
              hint="From Keyword Research or typed in, one per line. Only these keywords are used, unless you turn on Find more related keywords below."
              guideFor="existing_target_keywords"
            >
              <Textarea
                rows={3}
                value={form.existing_target_keywords}
                onChange={(e) =>
                  update('existing_target_keywords')(e.target.value)
                }
                placeholder={placeholders.seedKeywords}
              />
              <FieldSuggestions
                suggestions={fieldSuggestions.existing_target_keywords}
                onApply={(value) =>
                  applyFieldSuggestion('existing_target_keywords', value)
                }
              />
            </FormField>
            <FormField
              label="Site pages (optional)"
              hint="Page URLs, one per line. Each ad group is sent to the best matching page."
            >
              <Textarea
                rows={3}
                value={form.pages}
                onChange={(e) => update('pages')(e.target.value)}
                placeholder={placeholders.pages}
              />
              <FieldSuggestions
                suggestions={fieldSuggestions.pages}
                onApply={(value) => applyFieldSuggestion('pages', value)}
              />
            </FormField>
          </div>

          <div className="rounded-md border border-border bg-warm-bg p-3">
            <div className="flex items-start gap-3">
              <Checkbox
                id="campaign-keyword-expansion"
                checked={expandCampaignKeywords}
                onCheckedChange={(checked) => setExpandCampaignKeywords(checked === true)}
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label
                  htmlFor="campaign-keyword-expansion"
                  className="cursor-pointer text-sm font-medium"
                >
                  Find more related keywords
                </Label>
                <div className="text-xs text-muted-foreground">
                  Off by default, so only the keywords above are used. Turn it on
                  to let Google suggest more related keywords first.
                </div>
              </div>
            </div>
          </div>

          <FormField
            label="Approval expiry (hours)"
            hint="How long you have to approve the keywords before the run is canceled"
          >
            <Input
              type="number"
              min="1"
              max="168"
              value={form.approval_expires_in_hours}
              onChange={(e) =>
                update('approval_expires_in_hours')(e.target.value)
              }
              className="w-32"
            />
          </FormField>

          <div className="rounded-md border border-border bg-warm-bg p-3">
            <div className="flex items-start gap-3">
              <Checkbox
                id="campaign-slack-approval"
                checked={sendSlackApproval}
                onCheckedChange={(checked) => setSendSlackApproval(checked === true)}
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label
                  htmlFor="campaign-slack-approval"
                  className="cursor-pointer text-sm font-medium"
                >
                  Ask for opinions in #core-alerts
                </Label>
                <div className="text-xs text-muted-foreground">
                  Off by default. When on, the keyword approval is also posted
                  to Slack for the team. When off, it stays in Core only.
                </div>
              </div>
            </div>
            <div className="mt-3 rounded border border-coral/30 bg-card px-3 py-2 text-xs text-coral-dark">
              Starting a run researches keywords and waits for your approval.
              Nothing is created in Google Ads until you approve, and campaigns
              start paused.
            </div>
          </div>

          <FormField
            label="Review ads before creating"
            hint="Stop so you can check and edit the ad text before the ads are created."
          >
            <div className="flex items-center gap-2">
              <Switch
                id="campaign-ad-review"
                checked={form.ad_review}
                onCheckedChange={update('ad_review')}
              />
              <Label
                htmlFor="campaign-ad-review"
                className="cursor-pointer text-sm text-muted-foreground"
              >
                {form.ad_review
                  ? 'On. The run stops so you can check the ads first.'
                  : 'Off. Ads are created right after you approve the keywords.'}
              </Label>
            </div>
          </FormField>

          <FormField
            label="Ad variations per ad group"
            hint="How many ads to write for each ad group, so you can see which works best. Google allows up to 3."
          >
            <div className="flex items-center gap-2">
              {[2, 3].map((n) => (
                <Button
                  key={n}
                  type="button"
                  size="sm"
                  variant={form.rsa_variations === n ? 'default' : 'outline'}
                  onClick={() => update('rsa_variations')(n)}
                >
                  {n} ads
                </Button>
              ))}
              <span className="text-sm text-muted-foreground">
                {form.rsa_variations === 3
                  ? 'Three ads per ad group, more to compare'
                  : 'Two ads per ad group (default)'}
              </span>
            </div>
          </FormField>

          <div className="flex items-center gap-3">
            <Button
              // Deliberately NOT disabled on invalid: a disabled button swallows
              // the click, so the user's one natural gesture teaches them nothing.
              // Clicking with blockers opens the dialog instead of submitting.
              onClick={() => {
                if (!formIsValid) {
                  setBlockersDialogOpen(true);
                  return;
                }
                setup.mutate();
              }}
              disabled={setup.isPending}
              // Not aria-disabled: the button IS actionable when invalid (it
              // explains itself). Claiming "disabled" to assistive tech while
              // still responding to clicks is worse than either alternative.
              title={
                formIsValid
                  ? undefined
                  : `${campaignFormBlockers.length} thing${
                      campaignFormBlockers.length === 1 ? '' : 's'
                    } to fix. Click to see them.`
              }
              className={`bg-coral text-white shadow-md hover:bg-coral-hover hover:shadow-lg ${
                formIsValid ? '' : 'opacity-60'
              }`}
            >
              {setup.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Setting up…
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" /> Start campaign run
                </>
              )}
            </Button>
          </div>
          {setup.isPending && setupOverlayDismissed ? (
            // Inline fallback after "Continue in background" — the full-page
            // overlay (rendered at the tab root) owns the un-dismissed case.
            <CampaignBuildingLoader
              stage="Setting up the campaign…"
              subtext="Getting the account ready and starting keyword research. This can take a minute."
            />
          ) : null}
          {!formIsValid && (
            <div className="rounded-md border border-coral/30 bg-coral-light/20 p-3 text-sm">
              <div className="font-medium text-coral-dark">
                Before you can start, fix these:
              </div>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-coral-dark">
                {campaignFormBlockers.slice(0, 5).map((blocker) => (
                  <li key={blocker.message}>
                    <button
                      type="button"
                      className="text-left underline-offset-2 hover:underline"
                      onClick={() => focusBlockerAnchor(blocker.anchorId)}
                    >
                      {blocker.message}
                    </button>
                  </li>
                ))}
              </ul>
              {campaignFormBlockers.length > 5 ? (
                <div className="mt-2 text-xs text-muted-foreground">
                  Plus {campaignFormBlockers.length - 5} more required field
                  {campaignFormBlockers.length - 5 === 1 ? '' : 's'}.
                </div>
              ) : null}
            </div>
          )}
          <CampaignBlockersDialog
            open={blockersDialogOpen}
            onOpenChange={setBlockersDialogOpen}
            blockers={campaignFormBlockers}
          />
          {setupErrorMessage ? (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{setupErrorMessage}</span>
            </div>
          ) : null}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="approvals" className="mt-4">
          <CampaignApprovalsTab
            tenantId={tenantId}
            onOpenRun={(runId) => {
              setActiveCampaignTab('approvals');
              updateCampaignSearchParams({
                runId,
                campaignTab: 'approvals',
              });
            }}
          />
        </TabsContent>

        <TabsContent value="ad-review" className="mt-4">
          <AdReviewTab
            tenantId={tenantId}
            onAdsSubmitted={(runId) => {
              // The operator's next question after approving is "is my
              // campaign building?" — the answer lives on the run panel, not
              // here. Approving used to leave them parked on this tab.
              setActiveCampaignTab('approvals');
              updateCampaignSearchParams({ runId, campaignTab: 'approvals' });
            }}
          />
        </TabsContent>

        {/* CORE-M-11.b.2: `<TabsContent value="manage">` retired along
            with its inline `<CampaignManagementTab>`. The under-tab
            `<CampaignDrilldown>` at /ads-tools/management is the new
            home for the "here are your live campaigns" surface. */}

        <TabsContent value="logs" className="mt-4">
          <CampaignSetupLogsPanel
            logs={setupLogs}
            isPending={setup.isPending}
            onClear={() => setSetupLogs([])}
          />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <CampaignHistoryTab
            tenantId={tenantId}
            onOpenRun={(runId) => {
              setActiveCampaignTab('history');
              updateCampaignSearchParams({
                runId,
                campaignTab: 'history',
              });
            }}
          />
        </TabsContent>
      </Tabs>

      {setup.isPending && !setupOverlayDismissed ? (
        // Rendered outside the Tabs so switching tabs mid-setup doesn't
        // unmount the overlay (inactive TabsContent unmounts its children).
        <CampaignBuildingOverlay
          stage="Setting up the campaign…"
          subtext="Getting the account ready and starting keyword research. This can take a minute."
          onDismiss={() => setSetupOverlayDismissed(true)}
        />
      ) : null}

      {/* The run panel belongs to the tabs that act on a run (approvals,
          manage), not beneath the builder form. Excluded on:
          - 'create': it put the thing needing attention below the fold of a
            form the operator had just finished with.
          - 'ad-review': AdReviewTab already renders the canonical RSA editor +
            submit; the panel would render a SECOND editor that drifts out of
            sync with the first. */}
      {activeRunId &&
        activeCampaignTab !== 'create' &&
        activeCampaignTab !== 'ad-review' && (
        <div ref={runPanelRef} className="scroll-mt-6">
          <CampaignRunPanel
            runId={activeRunId}
            tenantId={tenantId}
            onClose={() => updateCampaignSearchParams({ runId: null })}
            onEditAndRetry={(run) => hydrateFormFromRun(run.run_input ?? {})}
            onOpenManagement={(campaignResourceName) => {
              // CORE-M-11.b.2: instead of switching an in-page tab, the
              // "Open Ads Management" button now navigates OUT to the
              // drill-down. When we have the resource name, deep-link
              // straight to that specific campaign (M-11.e); otherwise
              // just land on Management with the drill-down collapsed.
              const campaignId = campaignIdFromResourceName(campaignResourceName);
              const target = campaignId
                ? `/ads-tools/management?platform=google-ads&campaign=${encodeURIComponent(campaignId)}`
                : `/ads-tools/management?platform=google-ads`;
              navigate(target);
            }}
          />
        </div>
      )}
    </div>
  );
}

function CampaignApprovalsTab({
  tenantId,
  onOpenRun,
}: {
  tenantId: string;
  onOpenRun: (runId: string) => void;
}) {
  const runsQuery = useQuery({
    queryKey: ['google-ads-campaign-runs', tenantId],
    queryFn: () => listCampaignRuns({ tenantId, limit: 100 }),
    refetchInterval: 5_000,
  });
  const runs = runsQuery.data?.runs ?? [];
  const approvalRuns = runs.filter((run) => run.state === 'KW_APPROVAL');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border bg-card p-4">
        <div>
          <h2 className="text-base font-semibold">Campaign approvals</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Review pending keyword approvals before the webapp writes campaigns to Google Ads.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="bg-warm-bg"
          onClick={() => runsQuery.refetch()}
          disabled={runsQuery.isFetching}
        >
          {runsQuery.isFetching ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
          )}
          Refresh
        </Button>
      </div>

      {runsQuery.isError ? (
        <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          Could not load campaign approvals: {describeCampaignSetupError(runsQuery.error)}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-coral" />
            Waiting for approval
          </CardTitle>
          <CardDescription>
            These runs are paused at the keyword approval gate. Approving one starts the Google Ads write steps.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {runsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading approvals…
            </div>
          ) : approvalRuns.length === 0 ? (
            <div className="rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
              No campaign approvals are waiting.
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Run</TableHead>
                    <TableHead>Channels</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead className="text-right">Budget/day</TableHead>
                    <TableHead className="text-right">Ad groups</TableHead>
                    <TableHead className="text-right">Keywords</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvalRuns.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <code>{run.id.slice(0, 8)}</code>
                          <div>
                            <CampaignRunStateBadge state={run.state} />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {campaignApprovalChannels(run).join(', ')}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[280px] truncate">
                        <span title={campaignRunCampaignName(run)}>
                          {campaignRunCampaignName(run)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {currency(campaignRunDailyBudgetUsd(run))}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {(run.clusters ?? []).length || '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {campaignRunKeywordCount(run)}
                      </TableCell>
                      <TableCell>{formatCampaignRunDate(run.updated_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          size="sm"
                          className="bg-teal hover:bg-teal-hover"
                          onClick={() => onOpenRun(run.id)}
                        >
                          Review approval
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ad Review sub-tab (Item J) — a dedicated home for the RSA ad-authoring editor.
//
// Lists every run currently parked at AD_REVIEW, lets the operator pick one, and
// renders the shared RsaEditor for the selected run's full ad drafts. It is an
// ADDITIONAL, more visible entry point to the same editor + submit/reject
// mutations that CampaignRunPanel already exposes inline for AD_REVIEW runs.
// ---------------------------------------------------------------------------

/**
 * What Performance Max needs before it can run, checked against this workspace.
 *
 * ⛔ WHY THIS IS IN THE BUILDER. Google rejects the asset-group mutate AFTER
 * the campaign has been created, so a workspace with no images loses the whole
 * run to it: a live attempt spent two operator approvals, created a budget and
 * a campaign, and then died on NOT_ENOUGH_MARKETING_IMAGE_ASSET,
 * NOT_ENOUGH_SQUARE_MARKETING_IMAGE_ASSET and NOT_ENOUGH_LOGO_ASSET.
 *
 * The server-side policy check blocks that now, but at Start — after the
 * operator has already filled the form and chosen the type. This says it while
 * they are still choosing, which is the moment the answer is useful.
 */
function PmaxRequirementsPanel({ tenantId }: { tenantId: string }) {
  const readiness = useQuery({
    queryKey: ['google-ads-pmax-image-readiness', tenantId],
    queryFn: () => getPmaxImageReadiness(tenantId),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  const SLOTS: Array<{ key: string; label: string; hint: string }> = [
    { key: 'MARKETING_IMAGE', label: 'Marketing image',
      hint: 'Landscape, 1.91:1 — at least 600×314' },
    { key: 'SQUARE_MARKETING_IMAGE', label: 'Square marketing image',
      hint: 'Square, 1:1 — at least 300×300' },
    { key: 'LOGO', label: 'Logo',
      hint: 'Square, 1:1 — at least 128×128' },
  ];
  const data = readiness.data;
  const covered = new Set(data?.covered ?? []);
  // ⚠ Do not tell an operator to fix images that are not the obstacle. With the
  // feature off, no upload makes PMax launchable — saying "needs images" sends
  // them to do work that changes nothing.
  const featureOff = data?.feature_enabled === false;

  return (
    <div
      className={
        'mt-3 rounded-md border p-3 ' +
        (data?.ready
          ? 'border-teal/40 bg-teal-light/20'
          : 'border-coral/40 bg-coral-light/20')
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        {readiness.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : data?.ready ? (
          <CheckCircle2 className="h-4 w-4 text-teal-dark" />
        ) : (
          <TriangleAlert className="h-4 w-4 text-coral-dark" />
        )}
        <span className="text-sm font-medium">
          {readiness.isLoading
            ? 'Checking image assets…'
            : featureOff
              ? 'Performance Max is off for this environment'
              : data?.ready
                ? 'Performance Max is ready to run'
                : 'Performance Max needs images before it can run'}
        </span>
      </div>

      <p className="mt-1 text-xs text-muted-foreground">
        {readiness.isLoading
          ? 'Reading this workspace’s uploaded Google Ads image assets.'
          : (data?.message ??
             'Could not read this workspace’s image assets.')}
      </p>

      <ul className={'mt-2 flex-col gap-1 ' + (featureOff ? 'hidden' : 'flex')}>
        {SLOTS.map((slot) => {
          const ok = covered.has(slot.key);
          return (
            <li key={slot.key} className="flex items-start gap-2 text-xs">
              {ok ? (
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-dark" />
              ) : (
                <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coral-dark" />
              )}
              <span className={ok ? 'text-muted-foreground' : 'font-medium'}>
                {slot.label}
                <span className="ml-1 font-normal text-muted-foreground">
                  — {slot.hint}
                </span>
              </span>
            </li>
          );
        })}
      </ul>

      {/* ⚠ ART'S RULE, RECORDED RATHER THAN ENFORCED. The standing instruction is
          Final URL Expansion OFF for PMax. Google removed that field after API
          v21 with no writable replacement, so we cannot set it and must not
          imply we did. Art accepted the default on condition there is a note;
          this is the note, and it deliberately says "cannot be enforced", not
          "off". */}
      <p className="mt-3 rounded border border-border bg-warm-bg p-2 text-[11px] text-muted-foreground">
        <strong className="text-foreground">Final URL Expansion cannot be set from here.</strong>{' '}
        Google removed the field after API v21, so its default applies and this
        campaign may expand to pages beyond the asset group&rsquo;s final URLs.
        To keep it off, switch it off in the Google Ads UI after the campaign is
        created.
      </p>

      {!readiness.isLoading && !data?.ready && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="bg-card"
            onClick={() => window.open('/settings/images', '_blank')}
          >
            Upload images
          </Button>
          <span className="text-xs text-muted-foreground">
            Or switch Campaign type to <strong>Search</strong>, which needs no
            images.
          </span>
        </div>
      )}
    </div>
  );
}

function AdReviewTab({
  tenantId,
  onAdsSubmitted,
}: {
  tenantId: string;
  onAdsSubmitted?: (runId: string) => void;
}) {
  const runsQuery = useQuery({
    queryKey: ['google-ads-campaign-runs', tenantId],
    queryFn: () => listCampaignRuns({ tenantId, limit: 100 }),
    refetchInterval: 5_000,
  });
  const awaitingRuns = (runsQuery.data?.runs ?? []).filter(
    (run) => run.state === 'AD_REVIEW',
  );
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  // Auto-select the first awaiting run and keep the selection valid as runs
  // resolve off AD_REVIEW (e.g. after a submit) or the tenant changes.
  useEffect(() => {
    if (awaitingRuns.length === 0) {
      if (selectedRunId !== null) setSelectedRunId(null);
      return;
    }
    if (!selectedRunId || !awaitingRuns.some((run) => run.id === selectedRunId)) {
      setSelectedRunId(awaitingRuns[0].id);
    }
  }, [awaitingRuns, selectedRunId]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border bg-card p-4">
        <div>
          <h2 className="text-base font-semibold">Ad review</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Edit and approve the AI-generated responsive search ads before the webapp
            creates them in Google Ads.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="bg-warm-bg"
          onClick={() => runsQuery.refetch()}
          disabled={runsQuery.isFetching}
        >
          {runsQuery.isFetching ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
          )}
          Refresh
        </Button>
      </div>

      {runsQuery.isError ? (
        <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          Could not load ad reviews: {describeCampaignSetupError(runsQuery.error)}
        </div>
      ) : null}

      {runsQuery.isLoading ? (
        <div className="flex items-center gap-2 rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading ad reviews…
        </div>
      ) : awaitingRuns.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <Megaphone className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">
              No campaigns awaiting ad review
            </p>
            <p className="max-w-md text-sm text-muted-foreground">
              Ads pending review appear here when a run is started with &ldquo;Review
              ads before creating&rdquo; on.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Compact run-picker — only when more than one run awaits review. */}
          {awaitingRuns.length > 1 ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card p-3">
              <span className="text-xs font-medium text-muted-foreground">
                Reviewing run
              </span>
              <Select
                value={selectedRunId ?? undefined}
                onValueChange={(value) => setSelectedRunId(value)}
              >
                <SelectTrigger className="h-9 w-full bg-warm-bg sm:w-96">
                  <SelectValue placeholder="Select a run to review" />
                </SelectTrigger>
                <SelectContent>
                  {awaitingRuns.map((run) => (
                    <SelectItem key={run.id} value={run.id}>
                      {campaignRunCampaignName(run)} · {run.id.slice(0, 8)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {/* Full-width RSA editor for the selected run */}
          <div className="min-w-0">
            {selectedRunId ? (
              <AdReviewEditor
                key={selectedRunId}
                runId={selectedRunId}
                tenantId={tenantId}
                onAdsSubmitted={onAdsSubmitted}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

// Loads a single AD_REVIEW run's full drafts and wires the shared RsaEditor to
// the same submit/reject mutations CampaignRunPanel uses. Keyed by runId in the
// parent so each selected run gets a fresh mutation lifecycle (and the editor's
// submit "morph to loader" state doesn't bleed across runs).
function AdReviewEditor({
  runId,
  tenantId,
  onAdsSubmitted,
}: {
  runId: string;
  tenantId: string;
  onAdsSubmitted?: (runId: string) => void;
}) {
  const queryClient = useQueryClient();
  const run = useQuery({
    queryKey: ['google-ads-campaign-run', runId],
    queryFn: () => getCampaignRun(runId),
    refetchInterval: 5_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: ['google-ads-campaign-run', runId],
    });
    // Drop the resolved run off the awaiting list + update the tab count badge.
    queryClient.invalidateQueries({
      queryKey: ['google-ads-campaign-runs', tenantId],
    });
  };

  const submitAds = useMutation({
    mutationFn: (adCopy: Record<string, RsaDraft[]>) =>
      submitCampaignRunAds(runId, tenantId, adCopy, 'ui-operator'),
    onSuccess: (r) => {
      toast.success(
        `Ads submitted — creating ads from ${r.state}. Taking you to the campaign run.`,
      );
      invalidate();
      onAdsSubmitted?.(runId);
    },
    onError: (err: unknown) => {
      toast.error(`Submit ads failed: ${describeCampaignSetupError(err)}`);
    },
  });

  const reject = useMutation({
    mutationFn: () => rejectCampaignRun(runId, 'ui-operator'),
    onSuccess: (r) => {
      toast.success(`Rejected — state=${r.state}. No ads were created.`);
      invalidate();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? describeCampaignSetupError(err);
      toast.error(`Reject failed: ${detail}`);
    },
  });

  const handleRegenerate = async (
    clusterName: string,
    variantIndex: number,
  ): Promise<RsaDraft> => {
    try {
      const res = await regenerateCampaignRunAd(
        runId,
        tenantId,
        clusterName,
        variantIndex,
      );
      // Keep the persisted drafts in sync; the editor already applied the fresh
      // draft to its own state so this refetch won't clobber other cards' edits.
      invalidate();
      return res.draft;
    } catch (err) {
      toast.error(`Regenerate failed: ${describeCampaignSetupError(err)}`);
      throw err;
    }
  };

  if (run.isLoading) {
    return (
      <div className="flex items-center gap-2 rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading ad drafts…
      </div>
    );
  }

  if (run.isError || !run.data) {
    return (
      <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
        Could not load this run: {describeCampaignSetupError(run.error)}
      </div>
    );
  }

  const data = run.data;

  if (data.state !== 'AD_REVIEW') {
    return (
      <div className="rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
        This run is no longer awaiting ad review (state {data.state}).
      </div>
    );
  }

  if (!data.run_input?.ad_drafts) {
    return (
      <div className="rounded border border-coral/30 bg-coral-light p-3 text-sm text-coral-dark">
        Waiting for AI-generated ad drafts… this panel refreshes every 5s.
      </div>
    );
  }

  const collisionWarning = campaignNameCollisionWarning(data);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <CampaignRunStateBadge state={data.state} />
        <span className="text-sm font-medium text-foreground">
          {campaignRunCampaignName(data)}
        </span>
      </div>
      {collisionWarning ? (
        // Surface the duplicate-campaign warning here so it isn't lost when the
        // full run panel is suppressed on this tab — the operator needs it
        // before deciding to submit.
        <div className="flex items-start gap-2 rounded-md border border-coral/40 bg-coral-light/40 p-3 text-sm text-coral-dark">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <span className="font-medium">Campaign already exists. </span>
            {collisionWarning}
          </div>
        </div>
      ) : null}
      <RsaEditor
        drafts={data.run_input.ad_drafts}
        submitting={submitAds.isPending || submitAds.isSuccess}
        onSubmit={(adCopy) => submitAds.mutate(adCopy)}
        onReject={() => reject.mutate()}
        onRegenerate={handleRegenerate}
      />
    </div>
  );
}

function CampaignHistoryTab({
  tenantId,
  onOpenRun,
}: {
  tenantId: string;
  onOpenRun: (runId: string) => void;
}) {
  const runsQuery = useQuery({
    queryKey: ['google-ads-campaign-runs', tenantId],
    queryFn: () => listCampaignRuns({ tenantId, limit: 100 }),
    refetchInterval: 10_000,
  });
  const runs = runsQuery.data?.runs ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border bg-card p-4">
        <div>
          <h2 className="text-base font-semibold">Campaign run history</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Completed, failed, and approval runs stay here. Open any run to restore its receipt.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="bg-warm-bg"
          onClick={() => runsQuery.refetch()}
          disabled={runsQuery.isFetching}
        >
          {runsQuery.isFetching ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
          )}
          Refresh
        </Button>
      </div>

      {runsQuery.isError ? (
        <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          Could not load campaign history: {describeCampaignSetupError(runsQuery.error)}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-coral" />
            Recent campaign runs
          </CardTitle>
          <CardDescription>
            Use this for smoke-test evidence and to reopen a completed campaign run.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {runsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading campaign history…
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Run</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Approval</TableHead>
                    <TableHead className="text-right">Budget/day</TableHead>
                    <TableHead className="text-right">Ad groups</TableHead>
                    <TableHead className="text-right">Keywords</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-sm text-muted-foreground">
                        No campaign runs found for this workspace.
                      </TableCell>
                    </TableRow>
                  ) : (
                    runs.map((run) => (
                      <TableRow key={run.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <code>{run.id.slice(0, 8)}</code>
                            <div className="text-xs text-muted-foreground">{run.id}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <CampaignRunStateBadge state={run.state} />
                        </TableCell>
                        <TableCell className="max-w-[280px] truncate">
                          <div className="space-y-0.5">
                            <div className="truncate font-medium" title={campaignRunCampaignName(run)}>
                              {campaignRunCampaignName(run)}
                            </div>
                            {run.campaign_resource_name ? (
                              <code title={run.campaign_resource_name}>
                                {shortResourceName(run.campaign_resource_name)}
                              </code>
                            ) : (
                              <span className="text-xs text-muted-foreground">No campaign resource yet</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{campaignApprovalChannels(run).join(', ')}</span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {currency(campaignRunDailyBudgetUsd(run))}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {Object.keys(run.ad_group_map ?? {}).length || (run.clusters ?? []).length || '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {campaignRunKeywordCount(run) || '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatCampaignRunDate(run.updated_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => onOpenRun(run.id)}
                            >
                              Open run
                            </Button>
                            {googleAdsCampaignUrl(run.campaign_resource_name) ? (
                              <Button type="button" variant="ghost" size="sm" asChild>
                                <a
                                  href={googleAdsCampaignUrl(run.campaign_resource_name) ?? '#'}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Open campaign in Google Ads"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


/**
 * Why an ad-group read failed, in the operator's words.
 *
 * Deliberately NOT `describeCampaignSetupError`: that one translates every 5xx
 * into "Backend failed while starting Campaign Creation", which is nonsense on
 * a read-only drill-down and would send an operator hunting the wrong problem.
 */
function describeAdGroupsError(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (
    detail
    && typeof detail === 'object'
    && (detail as { error?: unknown }).error === 'feature_disabled'
  ) {
    const flag = String((detail as { flag?: unknown }).flag ?? 'Google Ads feature flag');
    return `${flag} is disabled, so ad-group performance can't be read.`;
  }
  const message = (error as { message?: string })?.message;
  return message?.trim() || 'Unknown error reading ad groups from Google Ads.';
}

/**
 * Ad-group level breakdown for one campaign (#954, third accordion level).
 *
 * Mounted only inside the campaign row's collapsible content, and additionally
 * gated on `expanded`, so an account with 29 campaigns costs 29 Google Ads
 * reads only if an operator actually opens all 29. No `refetchInterval` for
 * the same reason: this is a drill-down, not a live tile.
 *
 * Failure is contained here — the campaign row and every sibling row keep
 * rendering, because a campaign whose ad groups won't load is still a campaign
 * you need to be able to pause.
 */
function CampaignAdGroupsPanel({
  tenantId,
  campaignResourceName,
  expanded,
}: {
  tenantId: string;
  campaignResourceName: string;
  expanded: boolean;
}) {
  const adGroupsQuery = useQuery({
    queryKey: ['google-ads-campaign-ad-groups', tenantId, campaignResourceName],
    queryFn: () =>
      listAdGroupPerformance({ tenantId, campaignResourceName }),
    enabled: expanded,
    staleTime: 60_000,
    retry: 1,
  });
  // Which ad group is drilled into for its per-ad (A/B) performance.
  const [openAdGroup, setOpenAdGroup] = useState<string | null>(null);

  if (adGroupsQuery.isPending) {
    return (
      <div className="flex items-center gap-2 rounded border border-border bg-card p-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading ad groups…
      </div>
    );
  }

  if (adGroupsQuery.isError) {
    return (
      <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="min-w-0 [overflow-wrap:anywhere]">
            Could not load ad groups: {describeAdGroupsError(adGroupsQuery.error)}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-card"
            onClick={() => adGroupsQuery.refetch()}
            disabled={adGroupsQuery.isFetching}
          >
            {adGroupsQuery.isFetching ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
            )}
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const adGroups = adGroupsQuery.data.ad_groups ?? [];
  const metricsWindow = adGroupsQuery.data.metrics_window;
  const metricsFailed = metricsWindow?.status !== 'ok';
  const rollup = adGroupsQuery.data.totals?.metrics;

  // An empty campaign is a fact, not a failure — a campaign can legitimately
  // exist with no ad groups yet.
  if (adGroups.length === 0) {
    return (
      <div className="rounded border border-border bg-card p-3 text-sm text-muted-foreground">
        No ad groups in this campaign yet.
      </div>
    );
  }

  return (
    <div className="rounded border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Ad groups ({adGroups.length}) · {adsMetricsWindowLabel(metricsWindow?.date_range)}
        </div>
        {adGroupsQuery.isFetching ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        ) : null}
      </div>
      {metricsFailed ? (
        <div className="border-b border-border px-3 py-2 text-xs text-muted-foreground">
          {adsMetricsUnavailableLabel(metricsWindow)}
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ad group</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Impr.</TableHead>
              <TableHead className="text-right">Clicks</TableHead>
              <TableHead className="text-right">CTR</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Conv.</TableHead>
              <TableHead className="text-right">Cost/conv.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {adGroups.map((adGroup) => {
              const isOpen = openAdGroup === adGroup.resource_name;
              return (
              <Fragment key={adGroup.resource_name}>
              <TableRow>
                <TableCell className="max-w-[18rem] font-medium">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenAdGroup((cur) =>
                        cur === adGroup.resource_name
                          ? null
                          : adGroup.resource_name,
                      )
                    }
                    aria-expanded={isOpen}
                    className="flex w-full items-center gap-1.5 text-left transition-colors hover:text-foreground"
                  >
                    {isOpen ? (
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="truncate">
                      {adGroup.name || adGroup.ad_group_id}
                    </span>
                  </button>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {adGroup.status ?? 'UNKNOWN'}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricCount(adGroup.metrics?.impressions)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricCount(adGroup.metrics?.clicks)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricRate(adGroup.metrics?.ctr)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricMoney(adGroup.metrics?.cost_micros)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricConversions(adGroup.metrics?.conversions)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricMoney(adGroup.metrics?.cost_per_conversion_micros)}
                </TableCell>
              </TableRow>
              {isOpen ? (
                <TableRow>
                  <TableCell colSpan={8} className="bg-warm-bg/60 p-2">
                    <div className="space-y-2">
                      <AdGroupAdsPanel
                        tenantId={tenantId}
                        adGroupResourceName={adGroup.resource_name}
                      />
                      <AdGroupKeywordsPanel
                        tenantId={tenantId}
                        adGroupResourceName={adGroup.resource_name}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}
              </Fragment>
              );
            })}
            {rollup && adGroups.length > 1 ? (
              <TableRow className="bg-warm-bg font-semibold">
                <TableCell>All ad groups</TableCell>
                <TableCell />
                <TableCell className="text-right tabular-nums">
                  {adsMetricCount(rollup.impressions)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricCount(rollup.clicks)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricRate(rollup.ctr)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricMoney(rollup.cost_micros)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricConversions(rollup.conversions)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricMoney(rollup.cost_per_conversion_micros)}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function adLabel(ad: AdPerformanceItem): string {
  const heads = (ad.headlines ?? []).filter(Boolean);
  if (heads.length) return heads.slice(0, 2).join(' · ');
  return ad.ad_name || `Ad ${ad.ad_id}`;
}

/**
 * Per-ad (RSA) performance inside one ad group — the A/B drill-down. Fetched
 * on demand when the operator expands an ad group. Read-only.
 */
function AdGroupAdsPanel({
  tenantId,
  adGroupResourceName,
}: {
  tenantId: string;
  adGroupResourceName: string;
}) {
  const adsQuery = useQuery({
    queryKey: ['google-ads-ad-group-ads', tenantId, adGroupResourceName],
    queryFn: () => listAdPerformance({ tenantId, adGroupResourceName }),
    staleTime: 60_000,
    retry: 1,
  });
  const queryClient = useQueryClient();
  const statusMutation = useMutation({
    mutationFn: (vars: { adResourceName: string; status: 'PAUSED' | 'ENABLED' }) =>
      updateAdStatus({ tenantId, ...vars }),
    onSuccess: (_data, vars) => {
      toast.success(vars.status === 'PAUSED' ? 'Ad paused' : 'Ad enabled');
      queryClient.invalidateQueries({
        queryKey: ['google-ads-ad-group-ads', tenantId, adGroupResourceName],
      });
    },
    onError: (err) => {
      toast.error(`Could not update ad: ${describeAdGroupsError(err)}`);
    },
  });
  const toggleAdStatus = (ad: AdPerformanceItem) => {
    const next =
      String(ad.status ?? '').toUpperCase() === 'PAUSED' ? 'ENABLED' : 'PAUSED';
    if (
      next === 'PAUSED' &&
      !window.confirm(
        'Pause this ad? It stops serving until you re-enable it.',
      )
    ) {
      return;
    }
    statusMutation.mutate({ adResourceName: ad.resource_name, status: next });
  };

  if (adsQuery.isPending) {
    return (
      <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading ads…
      </div>
    );
  }

  if (adsQuery.isError) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-destructive/30 bg-destructive/5 px-2 py-2 text-xs text-destructive">
        <span className="min-w-0 [overflow-wrap:anywhere]">
          Could not load ads: {describeAdGroupsError(adsQuery.error)}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="bg-card"
          onClick={() => adsQuery.refetch()}
          disabled={adsQuery.isFetching}
        >
          {adsQuery.isFetching ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
          )}
          Retry
        </Button>
      </div>
    );
  }

  const ads = adsQuery.data?.ads ?? [];
  const metricsWindow = adsQuery.data?.metrics_window;
  const metricsFailed = metricsWindow != null && metricsWindow.status !== 'ok';

  if (ads.length === 0) {
    return (
      <div className="px-2 py-2 text-xs text-muted-foreground">
        No ads in this ad group yet.
      </div>
    );
  }

  return (
    <div className="rounded border border-border bg-card">
      <div className="border-b border-border px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Ads in this ad group ({ads.length})
        {ads.length > 1 ? ' · compare to spot the underperformer' : ''}
      </div>
      {metricsFailed ? (
        <div className="border-b border-border px-2 py-1.5 text-[11px] text-muted-foreground">
          {adsMetricsUnavailableLabel(metricsWindow)}
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ad</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Impr.</TableHead>
              <TableHead className="text-right">Clicks</TableHead>
              <TableHead className="text-right">CTR</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Conv.</TableHead>
              <TableHead className="text-right">Cost/conv.</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ads.map((ad) => (
              <TableRow key={ad.resource_name}>
                <TableCell className="max-w-[20rem] truncate font-medium">
                  {adLabel(ad)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {ad.status ?? 'UNKNOWN'}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricCount(ad.metrics?.impressions)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricCount(ad.metrics?.clicks)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricRate(ad.metrics?.ctr)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricMoney(ad.metrics?.cost_micros)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricConversions(ad.metrics?.conversions)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricMoney(ad.metrics?.cost_per_conversion_micros)}
                </TableCell>
                <TableCell className="text-right">
                  {(() => {
                    const isPaused =
                      String(ad.status ?? '').toUpperCase() === 'PAUSED';
                    const pending =
                      statusMutation.isPending &&
                      statusMutation.variables?.adResourceName ===
                        ad.resource_name;
                    return (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="bg-card"
                        disabled={pending}
                        onClick={() => toggleAdStatus(ad)}
                      >
                        {pending ? (
                          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : null}
                        {isPaused ? 'Enable' : 'Pause'}
                      </Button>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function keywordMatchLabel(matchType?: string | null): string {
  const m = String(matchType ?? '').toUpperCase();
  if (m === 'EXACT') return 'Exact';
  if (m === 'PHRASE') return 'Phrase';
  if (m === 'BROAD') return 'Broad';
  return matchType || '—';
}

/**
 * Per-keyword performance inside one ad group — which keywords are working vs
 * not (the question clients ask most). Read-only, fetched on demand.
 */
function AdGroupKeywordsPanel({
  tenantId,
  adGroupResourceName,
}: {
  tenantId: string;
  adGroupResourceName: string;
}) {
  const kwQuery = useQuery({
    queryKey: ['google-ads-ad-group-keywords', tenantId, adGroupResourceName],
    queryFn: () => listKeywordPerformance({ tenantId, adGroupResourceName }),
    staleTime: 60_000,
    retry: 1,
  });

  if (kwQuery.isPending) {
    return (
      <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading keywords…
      </div>
    );
  }

  if (kwQuery.isError) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-destructive/30 bg-destructive/5 px-2 py-2 text-xs text-destructive">
        <span className="min-w-0 [overflow-wrap:anywhere]">
          Could not load keywords: {describeAdGroupsError(kwQuery.error)}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="bg-card"
          onClick={() => kwQuery.refetch()}
          disabled={kwQuery.isFetching}
        >
          {kwQuery.isFetching ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
          )}
          Retry
        </Button>
      </div>
    );
  }

  const keywords = kwQuery.data?.keywords ?? [];
  const metricsWindow = kwQuery.data?.metrics_window;
  const metricsFailed = metricsWindow != null && metricsWindow.status !== 'ok';

  if (keywords.length === 0) {
    return (
      <div className="px-2 py-2 text-xs text-muted-foreground">
        No keywords in this ad group yet.
      </div>
    );
  }

  return (
    <div className="rounded border border-border bg-card">
      <div className="border-b border-border px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Keywords in this ad group ({keywords.length})
      </div>
      {metricsFailed ? (
        <div className="border-b border-border px-2 py-1.5 text-[11px] text-muted-foreground">
          {adsMetricsUnavailableLabel(metricsWindow)}
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Keyword</TableHead>
              <TableHead>Match</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Impr.</TableHead>
              <TableHead className="text-right">Clicks</TableHead>
              <TableHead className="text-right">CTR</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Conv.</TableHead>
              <TableHead className="text-right">Cost/conv.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keywords.map((kw: KeywordPerformanceItem) => (
              <TableRow key={kw.resource_name}>
                <TableCell className="max-w-[20rem] truncate font-medium">
                  {kw.keyword_text || kw.resource_name}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {keywordMatchLabel(kw.match_type)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {kw.status ?? 'UNKNOWN'}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricCount(kw.metrics?.impressions)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricCount(kw.metrics?.clicks)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricRate(kw.metrics?.ctr)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricMoney(kw.metrics?.cost_micros)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricConversions(kw.metrics?.conversions)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {adsMetricMoney(kw.metrics?.cost_per_conversion_micros)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function CreatedCampaignSummary({
  tenantId,
  campaign,
  metricsWindow,
  selected,
  onSelectedChange,
  onOpenRun,
  onDeleteCampaign,
  deletePending,
}: {
  tenantId: string;
  campaign: LiveCampaignInventoryItem;
  metricsWindow?: AdsMetricsWindow | null;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
  onOpenRun?: () => void;
  onDeleteCampaign: () => void;
  deletePending: boolean;
}) {
  const googleAdsUrl = googleAdsCampaignUrl(campaign.resource_name);
  const campaignName = campaign.name || shortResourceName(campaign.resource_name);
  const matchedRunState = isCampaignRunState(campaign.matched_run_state)
    ? campaign.matched_run_state
    : null;
  const setupFailedAfterCreate =
    matchedRunState === 'FAILED' && campaign.live_in_google_ads;
  const selectionDisabled = !campaign.manageable || !campaign.live_in_google_ads;
  const budgetSync = campaign.budget_sync;
  const plannedBudgetUsd = campaignInventoryPlannedDailyBudgetUsd(campaign);
  // Collapsed by default: opening the row is what triggers its ad-group read.
  const [adGroupsExpanded, setAdGroupsExpanded] = useState(false);

  return (
    // `min-w-0` matters: this card is a grid item, and grid items refuse to
    // shrink below their min-content unless told to. Without it the ad-group
    // table's own horizontal scroll never engages and the whole page scrolls
    // sideways instead.
    <div className="min-w-0 rounded-md border border-border bg-warm-bg p-3">
      {/* The checkbox sits OUTSIDE the collapsible trigger: nesting it inside a
          <button> would be invalid markup and every selection click would also
          toggle the row. Same reason the action buttons go in `actions`. */}
      <div className="flex min-w-0 items-start gap-3">
        <Checkbox
          checked={selected}
          onCheckedChange={(checked) => onSelectedChange(checked === true)}
          disabled={selectionDisabled}
          className="mt-1"
          aria-label={`Select ${campaignName}`}
        />
        <CollapsibleSection
          className="min-w-0 flex-1"
          open={adGroupsExpanded}
          onOpenChange={setAdGroupsExpanded}
          contentClassName="pt-3"
          actionsClassName="min-w-0 shrink justify-end"
          header={
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {setupFailedAfterCreate ? (
                <Badge variant="outline" className="border-coral/30 bg-coral-light/40 text-coral-dark">
                  created with issues
                </Badge>
              ) : matchedRunState ? (
                <CampaignRunStateBadge state={matchedRunState} />
              ) : (
                <Badge
                  variant="outline"
                  className={campaignStatusBadgeClass(campaign.status)}
                >
                  {campaign.status ?? 'UNKNOWN'}
                </Badge>
              )}
              <Badge
                variant="outline"
                className={campaignSourceBadgeClass(campaign.source)}
              >
                {campaign.source_label}
              </Badge>
              <span className="text-sm font-semibold">
                {campaignName}
              </span>
              <span className="text-xs text-muted-foreground">
                {adGroupsExpanded ? 'Hide ad groups' : 'Show ad groups'}
              </span>
            </div>
            <div className="mt-1 truncate text-xs text-muted-foreground">
              {shortResourceName(campaign.resource_name)}
              {campaign.matched_run_id ? (
                <>
                  {' '}· Run <code>{campaign.matched_run_id}</code>
                </>
              ) : null}
            </div>
            {campaign.matched_run_state === 'FAILED' && campaign.matched_run_error ? (
              <div className="mt-1 max-w-2xl truncate text-xs text-destructive">
                Setup stopped after campaign creation: {campaign.matched_run_error}
              </div>
            ) : null}
            {!campaign.live_in_google_ads ? (
              <div className="mt-1 text-xs text-muted-foreground">
                This campaign is tracked in the app, but is not currently active in Google Ads inventory.
              </div>
            ) : null}
            {/* Everything the flat card used to show stays in the header, so
                it is visible collapsed or expanded — collapsing must not hide
                budget drift from the operator managing these campaigns. */}
            <AdsPerformanceStrip
              metrics={campaign.metrics}
              unavailableLabel={adsMetricsUnavailableLabel(metricsWindow)}
              className="mt-2"
            />
            <div className="mt-3 grid gap-2 text-sm md:grid-cols-4">
              <div>
                <div className="text-xs text-muted-foreground">Live daily budget</div>
                <div className="font-semibold">{currency(campaignInventoryDailyBudgetUsd(campaign))}</div>
                {plannedBudgetUsd !== null ? (
                  <div className="text-xs text-muted-foreground">
                    Webapp plan {currency(plannedBudgetUsd)}
                  </div>
                ) : null}
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Google Ads status</div>
                <div className="font-semibold">{campaign.status ?? 'UNKNOWN'}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Channel</div>
                <div className="font-semibold">{campaign.advertising_channel_type ?? '—'}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Bidding</div>
                <div className="font-semibold">{campaign.bidding_strategy_type ?? '—'}</div>
              </div>
            </div>
            {budgetSync ? (
              <div className={`mt-3 flex flex-wrap items-center gap-2 rounded border px-3 py-2 text-xs ${campaignBudgetSyncClassName(budgetSync.status)}`}>
                <Badge variant="outline" className="bg-card">
                  Budget sync: {campaignBudgetSyncLabel(budgetSync.status)}
                </Badge>
                <span>{budgetSync.evidence || 'Budget sync evidence unavailable.'}</span>
              </div>
            ) : null}
          </div>
          }
          actions={
            <>
              {onOpenRun ? (
                <Button type="button" variant="outline" size="sm" onClick={onOpenRun}>
                  Open run
                </Button>
              ) : null}
              {googleAdsUrl ? (
                <Button type="button" size="sm" className="bg-teal hover:bg-teal-hover" asChild>
                  <a href={googleAdsUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="mr-1 h-3.5 w-3.5" />
                    Google Ads
                  </a>
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-destructive/30 text-destructive hover:text-destructive"
                disabled={deletePending || selectionDisabled}
                onClick={onDeleteCampaign}
              >
                {deletePending ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                )}
                Delete
              </Button>
            </>
          }
        >
          <CampaignAdGroupsPanel
            tenantId={tenantId}
            campaignResourceName={campaign.resource_name}
            expanded={adGroupsExpanded}
          />
        </CollapsibleSection>
      </div>
    </div>
  );
}

function CampaignRunStateBadge({ state }: { state: CampaignRunState }) {
  const className =
    state === 'DONE'
      ? 'border-teal/30 bg-teal-light text-teal-dark'
      : state === 'FAILED' || state === 'REJECTED'
        ? 'border-destructive/30 bg-destructive/5 text-destructive'
        : state === 'CANCELLED'
          ? 'border-border bg-muted text-muted-foreground'
          : state === 'KW_APPROVAL' || state === 'AD_REVIEW'
            ? 'border-coral/30 bg-coral-light/40 text-coral-dark'
            : 'border-border bg-warm-bg text-muted-foreground';
  return (
    <Badge variant="outline" className={className}>
      {state === 'CANCELLED' ? 'Cancelled' : state}
    </Badge>
  );
}

function campaignApprovalChannels(run: CampaignRunResponse): string[] {
  const channels = run.run_input?.approval_channels;
  if (!Array.isArray(channels)) return ['ui'];
  const normalized = channels
    .filter((channel): channel is string => typeof channel === 'string')
    .map((channel) => channel.trim())
    .filter(Boolean);
  return normalized.length ? normalized : ['ui'];
}

function campaignRunDailyBudgetUsd(run: CampaignRunResponse): number | null {
  const value = run.run_input?.daily_budget_micros;
  return typeof value === 'number' && Number.isFinite(value)
    ? value / 1_000_000
    : null;
}

function campaignRunKeywordCount(run: CampaignRunResponse): number {
  return (run.clusters ?? []).reduce((sum, cluster) => {
    return sum + normaliseCampaignClusterKeywords(cluster.keywords).length;
  }, 0);
}

function formatCampaignRunDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isRealCampaignResource(resourceName: string | null | undefined): boolean {
  return Boolean(resourceName && /^customers\/\d+\/campaigns\/\d+$/.test(resourceName));
}

function campaignRunRemoved(run: CampaignRunResponse): boolean {
  return typeof run.run_input?.campaign_removed_at === 'string';
}

// Whether a tracked run's campaign still exists in the live Google Ads
// account — distinct from campaignRunRemoved, which only knows about
// deletions made THROUGH this app's own delete flow. A campaign removed
// directly in Google Ads' UI never sets campaign_removed_at, so this checks
// the reconciled live set instead (see list_live_campaign_inventory's union
// on the backend, which accountCampaigns/liveResourceNames are built from).
export type CampaignRunGoogleAdsPresence = 'live' | 'gone' | 'never_created';

export function campaignRunGoogleAdsPresence(
  run: CampaignRunResponse,
  liveResourceNames: ReadonlySet<string>,
): CampaignRunGoogleAdsPresence {
  if (!isRealCampaignResource(run.campaign_resource_name)) return 'never_created';
  return liveResourceNames.has(run.campaign_resource_name as string) ? 'live' : 'gone';
}

function campaignRunCampaignName(run: CampaignRunResponse): string {
  const name = run.run_input?.campaign_name;
  if (typeof name === 'string' && name.trim()) return name.trim();

  const businessName = run.run_input?.business_name;
  const suffix = run.id.replaceAll('-', '').slice(0, 12);
  if (typeof businessName === 'string' && businessName.trim() && suffix) {
    return `${businessName.trim()}-SearchAds-${suffix}`;
  }

  return shortResourceName(run.campaign_resource_name);
}

function campaignInventoryItemFromRun(run: CampaignRunResponse): LiveCampaignInventoryItem {
  const resourceName = run.campaign_resource_name ?? '';
  const removed = campaignRunRemoved(run);
  return {
    resource_name: resourceName,
    campaign_id: campaignResourceCampaignId(resourceName),
    customer_id: campaignResourceCustomerId(resourceName),
    name: campaignRunCampaignName(run),
    status: String(run.run_input?.campaign_status ?? run.run_input?.status_on_launch ?? 'PAUSED'),
    advertising_channel_type: 'SEARCH',
    bidding_strategy_type:
      typeof run.run_input?.bidding_strategy === 'string'
        ? run.run_input.bidding_strategy
        : null,
    campaign_budget_resource_name:
      typeof run.run_input?.campaign_budget_resource_name === 'string'
        ? run.run_input.campaign_budget_resource_name
        : null,
    daily_budget_micros:
      typeof run.run_input?.daily_budget_micros === 'number'
        ? run.run_input.daily_budget_micros
        : null,
    planned_daily_budget_micros:
      typeof run.run_input?.daily_budget_micros === 'number'
        ? run.run_input.daily_budget_micros
        : null,
    budget_sync: null,
    source: removed ? 'removed_in_google_ads' : 'app_created',
    source_label: removed ? 'Removed from Google Ads' : 'Created by app',
    matched_run_id: run.id,
    matched_run_state: run.state,
    matched_run_error: run.error,
    removed_in_app_at:
      typeof run.run_input?.campaign_removed_at === 'string'
        ? run.run_input.campaign_removed_at
        : null,
    synced_at: run.updated_at,
    live_in_google_ads: !removed,
    manageable: isRealCampaignResource(resourceName) && !removed,
  };
}

function campaignInventoryDailyBudgetUsd(
  campaign: LiveCampaignInventoryItem,
): number | null {
  const value = campaign.daily_budget_micros;
  return typeof value === 'number' && Number.isFinite(value)
    ? value / 1_000_000
    : null;
}

function campaignInventoryPlannedDailyBudgetUsd(
  campaign: LiveCampaignInventoryItem,
): number | null {
  const value = campaign.planned_daily_budget_micros;
  return typeof value === 'number' && Number.isFinite(value)
    ? value / 1_000_000
    : null;
}

function campaignBudgetSyncRequiresReview(campaign: LiveCampaignInventoryItem): boolean {
  const budgetSync = campaign.budget_sync;
  if (!budgetSync) return false;
  if (budgetSync.requires_review === true) return true;
  return budgetSync.status === 'drift' || budgetSync.status === 'no_live_budget';
}

function campaignBudgetSyncLabel(status: string): string {
  if (status === 'pass') return 'In sync';
  if (status === 'drift') return 'Drift';
  if (status === 'no_plan') return 'No webapp plan';
  if (status === 'no_live_budget') return 'Live budget missing';
  return status || 'Unknown';
}

function campaignBudgetSyncClassName(status: string): string {
  if (status === 'pass') return 'border-teal/30 bg-teal-light text-teal-dark';
  if (status === 'drift' || status === 'no_live_budget') {
    return 'border-destructive/30 bg-destructive/5 text-destructive';
  }
  if (status === 'no_plan') return 'border-coral/30 bg-coral-light/30 text-coral-dark';
  return 'border-border bg-card text-muted-foreground';
}

function campaignResourceCampaignId(resourceName: string): string | null {
  return resourceName.match(/^customers\/\d+\/campaigns\/(\d+)$/)?.[1] ?? null;
}

function campaignResourceCustomerId(resourceName: string): string {
  return resourceName.match(/^customers\/(\d+)\/campaigns\/\d+$/)?.[1] ?? '';
}

function campaignSourceBadgeClass(source: string): string {
  if (source === 'app_created') return 'border-teal/30 bg-teal-light text-teal-dark';
  if (source === 'google_ads_only') return 'border-coral/30 bg-coral-light/40 text-coral-dark';
  if (source === 'removed_in_app' || source === 'removed_in_google_ads') {
    return 'border-muted bg-card text-muted-foreground';
  }
  return 'border-destructive/30 bg-destructive/5 text-destructive';
}

// ---------------------------------------------------------------------------
// Ads performance formatting (#954) — shared by the account rollup, the
// campaign rows and the ad-group table so one number never reads three ways.
//
// The backend hands back micros for money and fractions for rates, and uses
// `null` to mean "could not read" while a real zero is a real zero. These
// helpers keep that distinction: unknown renders as an em dash, zero renders
// as 0 / $0.00.
// ---------------------------------------------------------------------------

const ADS_METRIC_UNKNOWN = '—';

function adsMetricCount(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return ADS_METRIC_UNKNOWN;
  return Math.round(value).toLocaleString();
}

function adsMetricConversions(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return ADS_METRIC_UNKNOWN;
  // Google reports fractional conversions (view-through weighting); rounding
  // 0.5 leads to "0" would hide the only lead a new ad group has had.
  return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1);
}

/** Fraction (0.05) → percentage string ("5.0%"). */
function adsMetricRate(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return ADS_METRIC_UNKNOWN;
  return `${(value * 100).toFixed(1)}%`;
}

function adsMetricMoney(micros: number | null | undefined): string {
  if (typeof micros !== 'number' || !Number.isFinite(micros)) return ADS_METRIC_UNKNOWN;
  const usd = micros / 1_000_000;
  // Whole dollars past $1,000 — cents on a four-figure spend is noise.
  return usd >= 1000
    ? `$${Math.round(usd).toLocaleString()}`
    : `$${usd.toFixed(2)}`;
}

function adsMetricsWindowLabel(dateRange: string | null | undefined): string {
  if (!dateRange) return 'Performance';
  const match = dateRange.match(/^LAST_(\d+)_DAYS$/);
  if (match) return `Last ${match[1]} days`;
  return dateRange.replace(/_/g, ' ').toLowerCase();
}

/**
 * What to show instead of numbers. A failed metrics read says so (with the
 * backend's reason) rather than silently rendering zeros — "$0 spend" and
 * "we couldn't ask Google" are very different answers to "how is this doing".
 */
function adsMetricsUnavailableLabel(
  metricsWindow: AdsMetricsWindow | null | undefined,
): string {
  if (!metricsWindow) return 'Performance not loaded yet.';
  if (metricsWindow.status === 'error') {
    return `Performance unavailable: ${metricsWindow.error || 'Google Ads metrics read failed.'}`;
  }
  if (metricsWindow.status === 'skipped') return 'Performance was not requested.';
  return 'No performance reported for this campaign.';
}

const ADS_PERFORMANCE_CELLS: Array<{
  label: string;
  format: (metrics: AdsPerformanceMetrics) => string;
}> = [
  { label: 'Impr.', format: (m) => adsMetricCount(m.impressions) },
  { label: 'Clicks', format: (m) => adsMetricCount(m.clicks) },
  { label: 'CTR', format: (m) => adsMetricRate(m.ctr) },
  { label: 'Cost', format: (m) => adsMetricMoney(m.cost_micros) },
  { label: 'Conv.', format: (m) => adsMetricConversions(m.conversions) },
  { label: 'Cost/conv.', format: (m) => adsMetricMoney(m.cost_per_conversion_micros) },
];

/** Inline performance figures. Used at account and campaign level. */
function AdsPerformanceStrip({
  metrics,
  unavailableLabel = 'Performance unavailable.',
  className,
}: {
  metrics?: AdsPerformanceMetrics | null;
  unavailableLabel?: string;
  className?: string;
}) {
  if (!metrics) {
    return (
      <div className={cn('text-xs text-muted-foreground', className)}>
        {unavailableLabel}
      </div>
    );
  }
  return (
    <div className={cn('flex flex-wrap items-baseline gap-x-5 gap-y-1', className)}>
      {ADS_PERFORMANCE_CELLS.map((cell) => (
        <div key={cell.label} className="flex items-baseline gap-1.5">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {cell.label}
          </span>
          <span className="text-sm font-semibold tabular-nums">{cell.format(metrics)}</span>
        </div>
      ))}
    </div>
  );
}

function campaignStatusBadgeClass(status: string | null | undefined): string {
  if (status === 'ENABLED') return 'border-teal/30 bg-teal-light text-teal-dark';
  if (status === 'PAUSED') return 'border-coral/30 bg-coral-light/40 text-coral-dark';
  if (status === 'REMOVED') return 'border-muted bg-card text-muted-foreground';
  return 'border-border bg-card text-muted-foreground';
}

const CAMPAIGN_RUN_STATE_VALUES = new Set<string>([
  'CLIENT_BOOTSTRAP',
  'KEYWORD_RESEARCH',
  'KW_APPROVAL',
  'CAMPAIGN_CREATE',
  'AD_REVIEW',
  'ASSET_CREATE',
  'APPLY_RECS',
  'DONE',
  'FAILED',
  'REJECTED',
  'CANCELLED',
]);

function isCampaignRunState(value: unknown): value is CampaignRunState {
  return typeof value === 'string' && CAMPAIGN_RUN_STATE_VALUES.has(value);
}

function shortResourceName(resourceName: string | null | undefined): string {
  if (!resourceName) return '—';
  const match = resourceName.match(/^customers\/(\d+)\/campaigns\/(-?\d+)$/);
  if (!match) return resourceName;
  return `campaigns/${match[2]}`;
}

function googleAdsCampaignUrl(resourceName: string | null | undefined): string | null {
  const match = resourceName?.match(/^customers\/(\d+)\/campaigns\/(\d+)$/);
  if (!match) return null;
  return `https://ads.google.com/aw/campaigns?campaignId=${match[2]}&customerId=${match[1]}`;
}

function CampaignKeywordHandoffPanel({
  handoff,
  applied,
  keywordInputCount,
  pageInputCount,
  onApply,
  onReplace,
  onClear,
  onViewRun,
  runMissing,
  onEdit,
}: {
  handoff: KeywordResearchCampaignHandoff;
  applied: boolean;
  keywordInputCount: number;
  pageInputCount: number;
  onApply: () => void;
  onReplace: () => void;
  onClear: () => void;
  onViewRun: () => void;
  onEdit: (patch: { selected_keywords?: string[]; pages?: string[] }) => void;
  /** The saved package points at a run the server no longer has. The handoff
   *  lives in localStorage and is never checked against the backend, so a
   *  perfectly normal-looking package can carry a dead run_id — which is how
   *  "View run" led to a raw 404. */
  runMissing: boolean;
}) {
  const [showContents, setShowContents] = useState(false);
  const savedAt = new Date(handoff.saved_at);
  const savedLabel = Number.isNaN(savedAt.getTime())
    ? 'Saved recently'
    : savedAt.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
  const keywordDetails = campaignPackageKeywordDetails(handoff);
  const totalVolume = keywordDetails.reduce((sum, keyword) => sum + keyword.volume, 0);
  const avgCpc =
    keywordDetails.length > 0
      ? keywordDetails.reduce((sum, keyword) => sum + keyword.cpc, 0) /
        keywordDetails.length
      : 0;
  const plannerRows = keywordDetails.filter(
    (keyword) => keyword.source === 'keyword_planner',
  ).length;
  const matchedPages = handoffMatchedPages(handoff);
  const visiblePages = matchedPages.slice(0, 4);
  const hiddenPageCount = matchedPages.length - visiblePages.length;
  const canFillBlankInputs =
    !applied && (keywordInputCount === 0 || pageInputCount === 0);
  // ⛔ REPLACE WAS ALWAYS CLICKABLE, INCLUDING WHEN IT COULD DO NOTHING.
  // It overwrites the keyword/page inputs FROM this package, so it only means
  // anything when the form holds values that are not already the package's.
  // When the form already matches (`applied`), or is empty (Apply's job), it is
  // a no-op — and a live button that changes nothing is exactly why these two
  // buttons were indistinguishable.
  const canReplaceInputs =
    !applied && (keywordInputCount > 0 || pageInputCount > 0);

  return (
    <div className="rounded-md border border-coral/30 bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-coral-light p-2 text-coral-dark">
            <Target className="h-4 w-4" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold">
                Keywords from Keyword Research
              </h2>
              <Badge
                variant="outline"
                className={
                  applied
                    ? 'border-teal/30 bg-teal-light text-teal-dark'
                    : 'border-coral/30 bg-coral-light text-coral-dark'
                }
              >
                {applied ? 'Added to this campaign' : 'Ready to add'}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Check the keywords and pages below before you start the
              campaign.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canFillBlankInputs}
            onClick={onApply}
            title={
              applied
                ? 'The package is already in the campaign inputs'
                : !canFillBlankInputs
                  ? 'Keywords and landing pages already have values — use "Replace inputs" to overwrite them'
                  : 'Fill the blank keyword and landing-page inputs from this package'
            }
          >
            <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
            Fill blank inputs
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-coral text-white shadow-md hover:bg-coral-hover hover:shadow-lg"
            onClick={onReplace}
            disabled={!canReplaceInputs}
            title={
              applied
                ? 'Nothing to replace — the inputs already match this package'
                : !canReplaceInputs
                  ? 'Nothing to replace — the keyword and landing-page inputs are empty. Use "Fill blank inputs".'
                  : 'Overwrite the keyword and landing-page inputs with this package'
            }
          >
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
            Replace inputs
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!handoff.run_id || runMissing}
            onClick={onViewRun}
            title={
              runMissing
                ? 'This saved package points at a keyword research run that no longer exists'
                : undefined
            }
          >
            <ExternalLink className="mr-1 h-3.5 w-3.5" />
            View run
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onClear}>
            <X className="mr-1 h-3.5 w-3.5" />
            Clear
          </Button>
        </div>
      </div>

      {/* The two buttons differ only in whether they overwrite, and the labels
          alone did not carry that — so say which one is live and why. */}
      <p className="mt-3 text-xs text-muted-foreground">
        {applied
          ? 'These keywords and landing pages are already in the campaign inputs below. Nothing to fill or replace.'
          : canFillBlankInputs
            ? 'Fill blank inputs adds this package only where the inputs below are empty; it will not touch anything you have typed.'
            : canReplaceInputs
              ? 'The inputs below already have different values. Replace inputs overwrites them with this package — Fill blank inputs would do nothing.'
              : 'Nothing to apply.'}
      </p>

      {runMissing ? (
        <p className="mt-3 rounded border border-border bg-warm-bg px-3 py-2 text-xs text-muted-foreground">
          The keyword research run behind this package is no longer stored, so
          it cannot be opened. The keywords and pages below still work — they
          are saved in this package, not fetched from the run. Use{' '}
          <span className="font-medium text-foreground">Clear</span> to remove
          it once you are done with them.
        </p>
      ) : null}

      {/*
        WHY THIS IS EDITABLE HERE. The package showed only COUNTS — "12 selected
        keywords, 3 landing pages" — so the operator could not see what Apply or
        Replace was about to put in the form, and Replace overwrites whatever
        they had typed. Editing after applying does not survive the next
        Replace, because the package is the source. So the values are edited at
        the source, and the change is written straight back to the saved package.
      */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => setShowContents((v) => !v)}
          className="text-xs font-medium text-coral underline-offset-2 hover:underline"
        >
          {showContents ? 'Hide package contents' : 'Show / edit package contents'}
        </button>
      </div>

      {showContents ? (
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-[11px] font-medium uppercase text-muted-foreground">
              Keywords ({handoff.selected_keywords.length}) — one per line
            </label>
            <Textarea
              value={handoff.selected_keywords.join('\n')}
              onChange={(e) =>
                onEdit({
                  selected_keywords: e.target.value
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              rows={6}
              className="mt-1 bg-warm-bg font-mono text-xs"
            />
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase text-muted-foreground">
              Landing pages ({matchedPages.length}) — one per line
            </label>
            <Textarea
              value={(handoff.pages ?? []).join('\n')}
              onChange={(e) =>
                onEdit({
                  pages: e.target.value
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              rows={6}
              className="mt-1 bg-warm-bg font-mono text-xs"
            />
            {matchedPages.length > (handoff.pages ?? []).length ? (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {matchedPages.length - (handoff.pages ?? []).length} more page
                {matchedPages.length - (handoff.pages ?? []).length === 1 ? '' : 's'}{' '}
                come from the selected keywords' own landing pages and are not
                editable here.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-6">
        <div className="rounded border border-border bg-warm-bg px-3 py-2">
          <div className="text-[11px] font-medium uppercase text-muted-foreground">
            Selected keywords
          </div>
          <div className="text-lg font-semibold tabular-nums">
            {handoff.selected_keywords.length}
          </div>
        </div>
        <div className="rounded border border-border bg-warm-bg px-3 py-2">
          <div className="text-[11px] font-medium uppercase text-muted-foreground">
            Landing pages
          </div>
          <div className="text-lg font-semibold tabular-nums">
            {matchedPages.length}
          </div>
        </div>
        <div className="rounded border border-border bg-warm-bg px-3 py-2">
          <div className="text-[11px] font-medium uppercase text-muted-foreground">
            Total volume
          </div>
          <div className="text-lg font-semibold tabular-nums">
            {totalVolume > 0 ? totalVolume.toLocaleString() : '—'}
          </div>
        </div>
        <div className="rounded border border-border bg-warm-bg px-3 py-2">
          <div className="text-[11px] font-medium uppercase text-muted-foreground">
            Avg CPC
          </div>
          <div className="text-lg font-semibold tabular-nums">
            {currency(avgCpc)}
          </div>
        </div>
        <div className="rounded border border-border bg-warm-bg px-3 py-2">
          <div className="text-[11px] font-medium uppercase text-muted-foreground">
            With Google data
          </div>
          <div className="text-lg font-semibold tabular-nums">
            {plannerRows}/{keywordDetails.length}
          </div>
        </div>
        <div className="rounded border border-border bg-warm-bg px-3 py-2">
          <div className="text-[11px] font-medium uppercase text-muted-foreground">
            Source run
          </div>
          <div className="truncate text-sm font-medium">
            {handoff.run_id ? handoff.run_id.slice(0, 8) : 'Not saved'}
          </div>
        </div>
        <div className="rounded border border-border bg-warm-bg px-3 py-2">
          <div className="text-[11px] font-medium uppercase text-muted-foreground">
            Saved
          </div>
          <div className="text-sm font-medium">{savedLabel}</div>
        </div>
      </div>

      {(handoff.planner_language_label || handoff.planner_geo_label || handoff.site_filter_url) ? (
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 rounded border border-border bg-warm-bg px-3 py-2 text-xs">
          {handoff.planner_language_label ? (
            <span>
              <span className="text-muted-foreground">Language:</span>{' '}
              {handoff.planner_language_label}
            </span>
          ) : null}
          {handoff.planner_geo_label ? (
            <span>
              <span className="text-muted-foreground">Market:</span>{' '}
              {handoff.planner_geo_label}
            </span>
          ) : null}
          {handoff.site_filter_url ? (
            <span>
              <span className="text-muted-foreground">Site filter:</span>{' '}
              <a
                href={handoff.site_filter_url}
                target="_blank"
                rel="noreferrer"
                className="text-teal-dark hover:underline"
              >
                {handoff.site_filter_url}
              </a>
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="mt-4 space-y-4">
        <CollapsibleSection
          header={
            <div className="text-xs font-medium uppercase text-muted-foreground">
              Keywords for this campaign
            </div>
          }
          actions={
            <Badge variant="outline" className="bg-warm-bg text-[10px]">
              {keywordDetails.length} keyword{keywordDetails.length === 1 ? '' : 's'}
            </Badge>
          }
        >
          <div className="mt-2">
            <CampaignKeywordDetailsTable keywords={keywordDetails} />
          </div>
        </CollapsibleSection>
        <CollapsibleSection
          header={
            <div className="text-xs font-medium uppercase text-muted-foreground">
              Landing pages
            </div>
          }
          actions={
            <Badge variant="outline" className="bg-warm-bg text-[10px]">
              {matchedPages.length} page{matchedPages.length === 1 ? '' : 's'}
            </Badge>
          }
        >
          <div className="mt-2 space-y-1 text-sm">
            {visiblePages.length > 0 ? (
              visiblePages.map((page) => (
                <div
                  key={page}
                  className="truncate rounded border border-border bg-warm-bg px-2 py-1"
                  title={page}
                >
                  {page}
                </div>
              ))
            ) : (
              <div className="rounded border border-coral/30 bg-coral-light/20 px-2 py-1 text-coral-dark">
                No landing pages selected yet
              </div>
            )}
            {hiddenPageCount > 0 ? (
              <div className="text-xs text-muted-foreground">
                +{hiddenPageCount} more page{hiddenPageCount !== 1 ? 's' : ''}
              </div>
            ) : null}
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
}

function CampaignSetupLogsPanel({
  logs,
  isPending,
  onClear,
}: {
  logs: CampaignSetupLogEntry[];
  isPending: boolean;
  onClear: () => void;
}) {
  const visibleLogs = logs.length > 0
    ? logs
    : [
        {
          id: 0,
          status: 'info' as const,
          stage: 'ready',
          message: 'Campaign setup logs will appear here.',
          detail: 'Use Autofill all fields, then Start campaign run.',
          createdAt: '--',
        },
      ];

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin text-teal" />
          ) : (
            <Clock className="h-4 w-4 text-teal" />
          )}
          <div>
            <div className="text-sm font-medium">Campaign setup log</div>
            <div className="text-xs text-muted-foreground">
              Shows autofill, budget conversion, the setup request, and the result.
            </div>
          </div>
        </div>
        {logs.length > 0 ? (
          <Button type="button" variant="ghost" size="sm" onClick={onClear}>
            Clear logs
          </Button>
        ) : null}
      </div>
      {isPending ? (
        <div className="mb-3">
          <CampaignBuildingLoader
            stage="Setting up the campaign…"
            subtext="Keyword research is running — new entries appear in the log below."
          />
        </div>
      ) : null}
      <div className="space-y-2">
        {visibleLogs.map((log) => (
          <div
            key={log.id}
            className="flex gap-2 rounded border border-border bg-warm-bg px-3 py-2 text-xs"
          >
            <div className="pt-0.5">
              {log.status === 'success' ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-teal-dark" />
              ) : log.status === 'error' ? (
                <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
              ) : (
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-medium text-foreground">{log.stage}</span>
                <span className="text-muted-foreground">{log.createdAt}</span>
              </div>
              <div>{log.message}</div>
              {log.detail ? (
                <div className="mt-0.5 text-muted-foreground">{log.detail}</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** One reason the campaign form can't be submitted, plus where to fix it. */
interface CampaignFormBlocker {
  message: string;
  anchorId: string;
}

/**
 * Scroll a blocker's field into view and focus it.
 *
 * The anchor is either the control itself (budget input, Confirm budget button)
 * or a FormField wrapper, in which case we focus the first control inside it.
 */
function focusBlockerAnchor(anchorId: string): void {
  const anchor = document.getElementById(anchorId);
  if (!anchor) return;
  anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const focusable =
    anchor instanceof HTMLInputElement ||
    anchor instanceof HTMLTextAreaElement ||
    anchor instanceof HTMLButtonElement
      ? anchor
      : anchor.querySelector<HTMLElement>(
          'input, textarea, select, button, [contenteditable="true"]',
        );
  // Let the smooth scroll settle before focusing, or the browser jump-cuts.
  window.setTimeout(() => focusable?.focus({ preventScroll: true }), 320);
}

/**
 * Explains why "Start campaign run" won't submit yet.
 *
 * The button stays enabled on purpose: a natively disabled button fires no
 * click event, so the one gesture a stuck user actually makes — clicking it —
 * would tell them nothing. Instead the click opens this, and each row jumps to
 * the field that needs fixing.
 */
function CampaignBlockersDialog({
  open,
  onOpenChange,
  blockers,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blockers: CampaignFormBlocker[];
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* ui/dialog is hand-rolled, not Radix — it renders a bare div with no
          role/aria-modal. Supply them here so screen readers announce it. */}
      <DialogContent
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-blockers-title"
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle
            id="campaign-blockers-title"
            className="flex items-center gap-2"
          >
            <TriangleAlert className="h-4 w-4 text-coral" />
            Can&apos;t start this run yet
          </DialogTitle>
          <DialogDescription>
            {blockers.length} thing{blockers.length === 1 ? '' : 's'} need
            {blockers.length === 1 ? 's' : ''} fixing before this campaign can be
            created.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-2">
          {blockers.map((blocker) => (
            <li
              key={blocker.message}
              className="flex items-center justify-between gap-3 rounded-md border border-coral/30 bg-coral-light/20 p-2.5 text-sm text-coral-dark"
            >
              <span>{blocker.message}</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="shrink-0 bg-warm-bg"
                onClick={() => {
                  onOpenChange(false);
                  focusBlockerAnchor(blocker.anchorId);
                }}
              >
                Fix
              </Button>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Worked examples for the fields that decide whether a run is any good.
 *
 * ⛔ WHY THESE SPECIFIC EXAMPLES. Every "bad" string below is one that actually
 * reached Google or the keyword pipeline from this builder, and every "why" is
 * the measured consequence — not general copywriting advice:
 *
 *   target_address      "Primary service address or market" was autofilled from
 *                       the placeholder and geocoded → ZERO_RESULTS, run dead
 *                       after the operator had approved it.
 *   company_information vague input produced national keywords; a run came back
 *                       15 of 114 carrying the client's own market.
 *   services_offered    the literal placeholder "primary service, consultation"
 *                       reached a real run and drove the ad callouts.
 *   value_proposition   "What makes this workspace the best choice?" is the
 *                       placeholder, and it is required to appear in a headline.
 *   call_to_action      must fit a 30-char headline near-verbatim; long CTAs get
 *                       truncated or dropped.
 *   campaign keywords   brand prose seeded a dentist on "perianal abscess";
 *                       market-carrying service terms are what convert.
 */
const FIELD_GUIDES: Record<string, { good: string; bad: string; why: string }> = {
  business_name: {
    good: 'Mike Hammer Moving',
    bad: 'mikehammermoving.com',
    why: 'Goes into ad copy, so it should read the way a customer says it — not the domain.',
  },
  target_address: {
    good: '1234 Main St, Kansas City, MO 64111  —  or just: Kansas City, MO',
    bad: 'Primary service address or market',
    why: 'Geocoded to centre the 5-mile radius. A description instead of a place fails at Google with ZERO_RESULTS, after approval.',
  },
  company_information: {
    good: 'Mike Hammer Moving is a full-service moving company serving the Kansas City metro: residential, commercial, senior relocation and professional packing. Crews are trained, uniformed staff using company trucks.',
    bad: 'We are a great local business that helps customers with their needs.',
    why: 'Drives keyword brainstorm and clustering. Name the services and the metro — vague input returns national keywords the client cannot win.',
  },
  services_offered: {
    good: 'residential moving, commercial moving, senior relocation, packing services, piano moving',
    bad: 'primary service, consultation',
    why: 'Becomes the ad callouts and snippets. Use the service names a customer would search for.',
  },
  value_proposition: {
    good: 'Trained, uniformed movers with dedicated company trucks — never day labor',
    bad: 'What makes this workspace the best choice?',
    why: 'Required to appear in at least one headline, so it has to be a claim, not a question.',
  },
  call_to_action: {
    good: 'Get a free moving quote',
    bad: 'Contact us today to learn more about everything we can offer you',
    why: 'Must fit a 30-character headline near-verbatim. Long CTAs get truncated or dropped.',
  },
  existing_target_keywords: {
    good: 'movers kansas city\nmoving company kansas city\ncommercial movers kansas city',
    bad: 'moving\nbest company\nhow much does moving cost',
    why: 'Seeds the expansion. Service + city converts; bare or question-form terms pull in traffic that never books.',
  },
};

function FieldGuide({ field }: { field: string }) {
  const guide = FIELD_GUIDES[field];
  const [open, setOpen] = useState(false);
  if (!guide) return null;
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] font-medium text-coral underline-offset-2 hover:underline"
        aria-expanded={open}
      >
        {open ? 'Hide example' : 'See a good example'}
      </button>
      {open && (
        <div className="mt-1.5 space-y-1.5 rounded border border-border bg-warm-bg p-2">
          <div className="flex gap-1.5 text-[11px]">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-dark" />
            <span className="whitespace-pre-line">{guide.good}</span>
          </div>
          <div className="flex gap-1.5 text-[11px] text-muted-foreground">
            <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coral-dark" />
            <span className="whitespace-pre-line">{guide.bad}</span>
          </div>
          <p className="text-[10px] text-muted-foreground">{guide.why}</p>
        </div>
      )}
    </div>
  );
}

function FormField({
  label,
  hint,
  required,
  anchorId,
  guideFor,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  /** Jump target for the "why can't I start this run" dialog. */
  anchorId?: string;
  /** Key into FIELD_GUIDES — shows an inline good/bad example. */
  guideFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div id={anchorId} className="space-y-1 scroll-mt-24">
      <Label className="text-xs font-medium text-muted-foreground">
        {label}
        {required ? <span className="ml-1 text-destructive">*</span> : null}
      </Label>
      {children}
      {hint ? (
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      ) : null}
      {guideFor ? <FieldGuide field={guideFor} /> : null}
    </div>
  );
}

/**
 * Clickable values for a field.
 *
 * ⛔ NEVER OFFER INSTRUCTION TEXT AS A VALUE. "Primary service address or
 * market" was offered here as a chip labelled "Placeholder"; an operator
 * clicked it, it was geocoded, and Google returned ZERO_RESULTS — killing a
 * campaign run AFTER approval. A previous fix stopped AUTOFILL from writing it
 * but left the chip on screen, which is the more direct invitation of the two.
 *
 * A placeholder already renders inside the empty field as grey hint text.
 * Re-offering it as a suggestion converts a hint into a value, which is the
 * entire defect.
 */
function FieldSuggestions({
  suggestions,
  onApply,
}: {
  suggestions?: CampaignFieldSuggestion[];
  onApply: (value: string) => void;
}) {
  const usable = (suggestions ?? []).filter(
    (s) => !/^placeholder$/i.test(s.label.trim()),
  );
  if (!usable.length) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-teal-dark">
        <Sparkles className="h-3 w-3" />
        Suggestions
      </span>
      {usable.slice(0, 4).map((suggestion) => (
        <button
          key={`${suggestion.source}-${suggestion.label}-${suggestion.value}`}
          type="button"
          onClick={() => onApply(suggestion.value)}
          title={suggestion.value}
          className="max-w-[220px] truncate rounded border border-teal/30 bg-teal-light/20 px-2 py-0.5 text-[11px] text-teal-dark hover:bg-teal-light"
        >
          {suggestion.label}
        </button>
      ))}
    </div>
  );
}

function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Campaign run state panel — polls the run, surfaces approve/reject when
// the pipeline pauses on KW_APPROVAL.
// ---------------------------------------------------------------------------

const CAMPAIGN_RUN_STATES: Record<CampaignRunState, string> = {
  CLIENT_BOOTSTRAP: 'Geocoding + optional phone lookup',
  KEYWORD_RESEARCH: 'Researching keywords (LLM)',
  KW_APPROVAL: 'Awaiting your approval',
  CAMPAIGN_CREATE: 'Creating campaign shell',
  AD_REVIEW: 'Awaiting ad review',
  ASSET_CREATE: 'Creating ad groups, keywords, and assets',
  APPLY_RECS: 'Applying recommendations',
  DONE: 'Done',
  FAILED: 'Failed',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

// States where the pipeline is actively working (no operator input needed) —
// these drive the megaphone CampaignBuildingLoader in the run panel Overview.
// KW_APPROVAL / AD_REVIEW / DONE / FAILED / REJECTED are deliberately absent
// (AD_REVIEW is an operator-pause state like KW_APPROVAL — the RSA editor
// renders there instead of the building loader).
const CAMPAIGN_ACTIVE_STAGE_TEXT: Partial<Record<CampaignRunState, string>> = {
  CLIENT_BOOTSTRAP: 'Setting up the campaign…',
  KEYWORD_RESEARCH: 'Researching keywords…',
  CAMPAIGN_CREATE: 'Creating the campaign…',
  ASSET_CREATE: 'Writing ads & attaching assets…',
  APPLY_RECS: 'Applying recommendations…',
};

const CAMPAIGN_CREATION_PROGRESS: Record<
  CampaignRunState,
  { value: number; label: string; detail: string; indicatorClassName?: string }
> = {
  CLIENT_BOOTSTRAP: {
    value: 15,
    label: 'Preparing campaign inputs',
    detail: 'Checking location, phone, and workspace settings.',
  },
  KEYWORD_RESEARCH: {
    value: 30,
    label: 'Researching campaign keywords',
    detail: 'Building keyword clusters before approval.',
  },
  KW_APPROVAL: {
    value: 45,
    label: 'Waiting for approval',
    // Deliberately does NOT say "nothing is created until you approve". That is
    // true, and it reads as "approving is harmless" — which is wrong whenever
    // ad review is off, because then approving creates the campaign outright.
    // The gate states the actual consequence; this line only states the state.
    detail: 'This run is paused and waiting for a human decision.',
  },
  CAMPAIGN_CREATE: {
    value: 62,
    label: 'Creating campaign in Google Ads',
    detail: 'Creating the campaign budget, campaign shell, and location targeting.',
  },
  AD_REVIEW: {
    value: 72,
    label: 'Review ads',
    detail: 'Review and edit the AI-generated ad copy before ads are created.',
  },
  ASSET_CREATE: {
    value: 82,
    label: 'Creating ad groups and ads',
    detail: 'Writing ad groups, phrase-match keywords, RSAs, and extensions.',
  },
  APPLY_RECS: {
    value: 94,
    label: 'Finalizing Google Ads setup',
    detail: 'Checking safe recommendations before marking the run complete.',
  },
  DONE: {
    value: 100,
    label: 'Confirmed in Google Ads',
    detail: 'The campaign has a live Google Ads resource ID.',
    indicatorClassName: 'bg-teal',
  },
  FAILED: {
    value: 100,
    label: 'Campaign creation failed',
    detail: 'Review the error below before retrying.',
    indicatorClassName: 'bg-destructive',
  },
  REJECTED: {
    value: 100,
    label: 'Campaign rejected',
    detail: 'No Google Ads campaign was created from this run.',
    indicatorClassName: 'bg-muted-foreground',
  },
  CANCELLED: {
    value: 100,
    label: 'Campaign run cancelled',
    detail: 'No campaign was created — the run was cancelled before it finished.',
    indicatorClassName: 'bg-muted-foreground',
  },
};

function campaignCreationProgress(run: CampaignRunResponse) {
  const base = CAMPAIGN_CREATION_PROGRESS[run.state];
  const realResource = isRealCampaignResource(run.campaign_resource_name);
  const dryRunResource =
    typeof run.campaign_resource_name === 'string' &&
    /\/-\d+$/.test(run.campaign_resource_name);

  if (run.state === 'DONE' && dryRunResource) {
    return {
      ...base,
      label: 'Dry-run completed',
      detail: 'Google Ads dry-run mode returned simulated resource IDs only.',
      indicatorClassName: 'bg-coral',
    };
  }

  if (run.state === 'DONE' && !realResource) {
    return {
      ...base,
      value: 96,
      label: 'Waiting for Google Ads confirmation',
      detail: 'Setup finished, but Google Ads has not confirmed the live campaign yet.',
      indicatorClassName: 'bg-coral',
    };
  }

  return base;
}

function campaignNameCollisionWarning(run: CampaignRunResponse): string | null {
  const warning = run.run_input?.campaign_name_collision_warning;
  if (typeof warning === 'string' && warning.trim()) return warning.trim();
  if (run.run_input?.campaign_name_collision_detected !== true) return null;

  const baseName = run.run_input?.campaign_name_base;
  const finalName = run.run_input?.campaign_name;
  if (typeof baseName === 'string' && typeof finalName === 'string') {
    return `Campaign "${baseName}" already exists in Google Ads. This run will use "${finalName}" instead.`;
  }
  return 'A campaign with this name already exists in Google Ads. This run will use a unique run-id suffix.';
}

function campaignDisplayNames(run: CampaignRunResponse): string[] {
  const campaignName = run.run_input?.campaign_name;
  if (typeof campaignName === 'string' && campaignName.trim()) {
    return [campaignName.trim()];
  }

  const campaignNameMap = run.run_input?.campaign_name_map;
  if (campaignNameMap && typeof campaignNameMap === 'object' && !Array.isArray(campaignNameMap)) {
    return Object.values(campaignNameMap)
      .filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
      .map((name) => name.trim());
  }

  return [];
}

function CampaignRunPanel({
  runId,
  tenantId,
  onClose,
  onEditAndRetry,
  onOpenManagement,
}: {
  runId: string;
  tenantId: string;
  onClose: () => void;
  onEditAndRetry?: (run: CampaignRunResponse) => void;
  /**
   * CORE-M-11.b.2: the "Open Ads Management" button fires this with the
   * run's `campaign_resource_name` so the caller can navigate to the
   * drill-down deep link (`/ads-tools/management?campaign=<id>`). Older
   * callers that ignore the argument still work.
   */
  onOpenManagement?: (campaignResourceName: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const autoReadbackAttemptsRef = useRef<Record<string, number>>({});
  const [activePanelTab, setActivePanelTab] = useState('overview');
  // Full-page building overlay: "Continue in background" hides it for the
  // current active stretch; it re-arms whenever a new run (or a run newly
  // entering an active state) starts building.
  const [overlayDismissed, setOverlayDismissed] = useState(false);

  const run = useQuery({
    queryKey: ['google-ads-campaign-run', runId],
    queryFn: () => getCampaignRun(runId),
    refetchInterval: 5_000,
    // Paint from the runs list (already polled on the same 5s cadence) while
    // the per-run fetch is in flight. Without this the panel mounts with
    // `data === undefined` and renders a tiny "Loading run state…" line — so
    // right after the redirect, when the run IS building, the operator sees no
    // overlay and no Cancel button for a full poll cycle.
    placeholderData: () =>
      (
        queryClient.getQueryData<{ runs: CampaignRunResponse[] }>([
          'google-ads-campaign-runs',
          tenantId,
        ])?.runs ?? []
      ).find((candidate) => candidate.id === runId),
  });

  const approve = useMutation({
    mutationFn: () => approveCampaignRun(runId, 'ui-operator'),
    onSuccess: (r) => {
      toast.success(
        `Approved — write steps are starting from ${r.state}. Refresh Read-back to confirm the Google Ads state.`,
      );
      queryClient.invalidateQueries({
        queryKey: ['google-ads-campaign-run', runId],
      });
    },
    onError: (err: unknown) => {
      const detail = describeCampaignSetupError(err);
      toast.error(`Approve failed: ${detail}`);
    },
  });

  const reject = useMutation({
    mutationFn: () => rejectCampaignRun(runId, 'ui-operator'),
    onSuccess: (r) => {
      toast.success(
        `Rejected — state=${r.state}. Refresh Read-back to confirm the Google Ads state.`,
      );
      queryClient.invalidateQueries({
        queryKey: ['google-ads-campaign-run', runId],
      });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Reject failed: ${detail}`);
    },
  });

  const submitAds = useMutation({
    mutationFn: (adCopy: Record<string, RsaDraft[]>) =>
      submitCampaignRunAds(runId, tenantId, adCopy, 'ui-operator'),
    onSuccess: (r) => {
      toast.success(
        `Ads submitted — creating ads from ${r.state}. This panel updates as the run progresses.`,
      );
      queryClient.invalidateQueries({
        queryKey: ['google-ads-campaign-run', runId],
      });
    },
    onError: (err: unknown) => {
      const detail = describeCampaignSetupError(err);
      toast.error(`Submit ads failed: ${detail}`);
    },
  });

  const handleRegenerateAd = async (
    clusterName: string,
    variantIndex: number,
  ): Promise<RsaDraft> => {
    try {
      const res = await regenerateCampaignRunAd(
        runId,
        tenantId,
        clusterName,
        variantIndex,
      );
      // Keep persisted drafts in sync; the editor already applied the fresh
      // draft to its own state so this refetch won't clobber other cards' edits.
      queryClient.invalidateQueries({
        queryKey: ['google-ads-campaign-run', runId],
      });
      return res.draft;
    } catch (err) {
      toast.error(`Regenerate failed: ${describeCampaignSetupError(err)}`);
      throw err;
    }
  };

  const cancel = useMutation({
    mutationFn: () => cancelCampaignRun(runId, { cancelledBy: 'ui' }),
    onSuccess: (r) => {
      if (r.state === 'CANCELLED') {
        toast.success('Run cancelled — no campaign was created.');
      } else {
        toast.success('Cancelling run… it will stop at the next checkpoint.');
      }
      queryClient.invalidateQueries({
        queryKey: ['google-ads-campaign-run', runId],
      });
      queryClient.invalidateQueries({
        queryKey: ['google-ads-campaign-runs', tenantId],
      });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? describeCampaignSetupError(err);
      toast.error(`Cancel failed: ${detail}`);
    },
  });

  const handleCancelRun = () => {
    if (cancel.isPending) return;
    const confirmed = window.confirm(
      'Cancel this campaign run? Work in progress will stop.',
    );
    if (!confirmed) return;
    cancel.mutate();
  };

  const refreshReadback = useMutation({
    mutationFn: ({ silent = false }: { silent?: boolean } = {}) =>
      refreshCampaignRunReadback({ runId, tenantId }).then((run) => ({
        run,
        silent,
      })),
    onSuccess: ({ run: r, silent }) => {
      queryClient.setQueryData(['google-ads-campaign-run', runId], r);
      queryClient.invalidateQueries({
        queryKey: ['google-ads-campaign-runs', tenantId],
      });
      if (!silent) {
        toast.success('Google Ads read-back refreshed');
      }
    },
    onError: (err: unknown) => {
      const detail = describeCampaignSetupError(err);
      toast.error(`Read-back refresh failed: ${detail}`);
    },
  });
  const { mutate: refreshReadbackMutate, isPending: isReadbackRefreshing } =
    refreshReadback;

  // The panel isn't remounted when the operator opens a different run, so
  // clear approve success/pending state (drives the post-approval loader
  // morph) whenever the run changes.
  const { reset: resetApprove } = approve;
  const { reset: resetSubmitAds } = submitAds;
  useEffect(() => {
    resetApprove();
    resetSubmitAds();
  }, [runId, resetApprove, resetSubmitAds]);

  const data = run.data;
  const policyChecks = data?.policy_checks ?? [];
  const dryRunResource =
    typeof data?.campaign_resource_name === 'string' &&
    /\/-\d+$/.test(data.campaign_resource_name);
  const creationProgress = data ? campaignCreationProgress(data) : null;
  // A cancel was requested (flag set on run_input by the backend) but the run
  // is still on an active step — cooperative cancel stops at the next
  // checkpoint, so reflect "Cancelling…" immediately instead of the normal
  // stage text (which would look stuck for the few seconds until the poll
  // sees the CANCELLED transition).
  const cancelRequested =
    cancel.isPending || Boolean(data?.run_input?._cancel_requested);
  // With the ad-review gate on, approving keywords leads to AD_REVIEW — not to
  // campaign creation. Saying otherwise made the operator think a campaign was
  // being built behind their back.
  const adReviewRequested = Boolean(data?.run_input?.ad_review);
  const rawActiveStageText = data
    ? CAMPAIGN_ACTIVE_STAGE_TEXT[data.state]
    : undefined;
  const activeStageText = rawActiveStageText
    ? cancelRequested
      ? 'Cancelling the run…'
      : rawActiveStageText
    : undefined;
  const activeSubtext = cancelRequested
    ? 'Finishing the current step, then stopping — no campaign will be created.'
    : creationProgress?.detail;
  const isActivelyBuilding = Boolean(activeStageText);

  // Re-arm the overlay when the operator opens a different run, or when this
  // run (re-)enters an active building state (e.g. KW_APPROVAL → CAMPAIGN_CREATE
  // after approval). Leaving the active set unmounts the overlay via the
  // isActivelyBuilding render guard regardless of dismissal.
  useEffect(() => {
    setOverlayDismissed(false);
  }, [runId, isActivelyBuilding]);
  const duplicateCampaignWarning = data ? campaignNameCollisionWarning(data) : null;
  const campaignNames = data ? campaignDisplayNames(data) : [];
  const googleAdsReadback = data?.run_input?.google_ads_readback ?? null;
  const googleAdsReadbackStatus = googleAdsReadback
    ? googleAdsReadbackStatusLabel(
        String(googleAdsReadback.status || 'warning').toLowerCase(),
      )
    : 'Pending';
  const googleAdsReadbackNeedsSync = googleAdsReadbackNeedsRefresh(googleAdsReadback);
  // ⛔ WHAT THIS BUTTON DOES DEPENDS ON A TOGGLE SET EARLIER, ON ANOTHER SCREEN.
  // With ad review ON there is a second gate: approving here only drafts ads.
  // With it OFF this click creates the campaign in Google Ads immediately.
  //
  // I told Jacob approving was safe on 2026-08-20, having checked that the
  // ad-review gate contains no writer. It does not — but with the toggle off
  // that gate is never reached, and his approval created a live (paused)
  // campaign. The screen said "No Google Ads campaign is created until this run
  // is approved", which is true and reads exactly like a reassurance that
  // approving is harmless. A button must name its consequence, not its position
  // in a workflow.
  const adReviewEnabled = Boolean(
    (data?.run_input as Record<string, unknown> | undefined)?.['ad_review'],
  );
  const hasBlockingPolicyFailure = policyChecks.some(
    (check) => check.severity === 'blocking' && check.status === 'fail',
  );
  const shouldAutoRefreshReadback =
    data?.state === 'DONE' &&
    !dryRunResource &&
    googleAdsReadbackNeedsRefresh(googleAdsReadback);

  useEffect(() => {
    if (!shouldAutoRefreshReadback || isReadbackRefreshing) return;
    const attempts = autoReadbackAttemptsRef.current[runId] ?? 0;
    if (attempts >= 3) return;
    const timeout = window.setTimeout(() => {
      autoReadbackAttemptsRef.current[runId] = attempts + 1;
      refreshReadbackMutate({ silent: true });
    }, 30_000);
    return () => window.clearTimeout(timeout);
  }, [
    isReadbackRefreshing,
    refreshReadbackMutate,
    runId,
    shouldAutoRefreshReadback,
  ]);
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-coral" />
            Campaign run
            {data ? (
              <Badge variant="outline" className="ml-2">
                {data.state}
              </Badge>
            ) : null}
          </CardTitle>
          <CardDescription>
            ID: <code>{runId}</code> · auto-refreshes every 5s
          </CardDescription>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {!data ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading run state…
          </div>
        ) : (
          <>
            {isActivelyBuilding && !overlayDismissed ? (
              <CampaignBuildingOverlay
                stage={activeStageText}
                subtext={activeSubtext}
                onDismiss={() => setOverlayDismissed(true)}
                onCancel={cancelRequested ? undefined : handleCancelRun}
                cancelPending={cancelRequested}
              />
            ) : null}
            <Tabs value={activePanelTab} onValueChange={setActivePanelTab}>
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="readback">Read-back</TabsTrigger>
                <TabsTrigger value="audit" disabled={data.state !== 'DONE'}>
                  Audit Evidence
                </TabsTrigger>
                <TabsTrigger value="raw">Raw State</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4 space-y-4">
                <div className="text-sm">
                  <strong>Phase:</strong>{' '}
                  {CAMPAIGN_RUN_STATES[data.state] ?? data.state}
                </div>

                {activeStageText && overlayDismissed ? (
                  // Inline fallback after "Continue in background" — the
                  // full-page overlay above owns the un-dismissed case. Keep
                  // cancel reachable here too.
                  <div className="space-y-2">
                    <CampaignBuildingLoader
                      stage={activeStageText}
                      subtext={activeSubtext}
                    />
                    {!cancelRequested ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCancelRun}
                      disabled={cancel.isPending}
                      className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    >
                      {cancel.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                          Cancelling…
                        </>
                      ) : (
                        'Cancel run'
                      )}
                    </Button>
                    ) : null}
                  </div>
                ) : null}

                {creationProgress ? (
                  <div className="rounded border bg-background p-3 text-sm">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="font-medium">{creationProgress.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {creationProgress.value}%
                      </div>
                    </div>
                    <Progress
                      value={creationProgress.value}
                      className="h-2"
                      indicatorClassName={creationProgress.indicatorClassName}
                    />
                    <div className="mt-2 text-xs text-muted-foreground">
                      {creationProgress.detail}
                    </div>
                    {data.campaign_resource_name ? (
                      <div className="mt-2 text-xs">
                        Google Ads resource:{' '}
                        <code>{shortResourceName(data.campaign_resource_name)}</code>
                      </div>
                    ) : null}
                    {campaignNames.length > 0 ? (
                      <div className="mt-1 text-xs">
                        Campaign name:{' '}
                        <span className="font-medium">
                          {campaignNames.join(', ')}
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="rounded border border-teal/20 bg-teal-light/20 p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-medium">Your defaults</div>
                    <Badge
                      variant="outline"
                      className={
                        googleAdsReadbackNeedsSync
                          ? 'border-coral/40 bg-card text-coral-dark'
                          : 'border-teal/30 bg-card text-teal-dark'
                      }
                    >
                      {googleAdsReadbackStatus}
                    </Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <Badge variant="outline" className="bg-card">
                      Launch paused
                    </Badge>
                    <Badge variant="outline" className="bg-card">
                      Search-only
                    </Badge>
                    <Badge variant="outline" className="bg-card">
                      Phrase-match keywords
                    </Badge>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    The run starts paused, uses search-only targeting, and writes phrase-match
                    ad groups. Refresh read-back after approval if you want to confirm the
                    Google Ads state before moving on.
                  </div>
                </div>

                {duplicateCampaignWarning ? (
                  <div className="flex items-start gap-2 rounded border border-coral/30 bg-coral-light p-3 text-sm text-coral-dark">
                    <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Campaign already exists</div>
                      <div className="mt-1 text-xs">{duplicateCampaignWarning}</div>
                    </div>
                  </div>
                ) : null}

                {data.error ? (
                  <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                    <strong>Error:</strong> {describeCampaignRunError(data.error)}
                  </div>
                ) : null}

                {(data.state === 'FAILED' ||
                  data.state === 'CANCELLED' ||
                  data.error) &&
                onEditAndRetry ? (
                  <div className="flex flex-wrap items-center gap-3 rounded border border-coral/30 bg-coral-light p-3">
                    <Button
                      onClick={() => onEditAndRetry(data)}
                      className="bg-coral text-white shadow-md hover:bg-coral-hover hover:shadow-lg"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" /> Edit & Retry
                    </Button>
                    <span className="text-xs text-coral-dark">
                      Loads these inputs into the builder so you can adjust and
                      submit a new run.
                    </span>
                  </div>
                ) : null}

                <PolicyChecklist
                  checks={policyChecks}
                  title="Campaign setup policy checks"
                />

                {data.state === 'KW_APPROVAL' && data.clusters && (
                  <div className="space-y-2">
                    <div className="text-sm">
                      <strong>{data.clusters.length}</strong> keyword cluster(s)
                      ready for review:
                    </div>
                    <UnusedPagesNotice
                      pages={unusedSelectedPages(data.run_input, data.clusters)}
                    />
                    <ClustersList clusters={data.clusters} />
                    {approve.isPending || approve.isSuccess ? (
                      // Morph: once Approve is clicked the buttons give way to
                      // the megaphone loader until the 5s refetch moves the run
                      // into CAMPAIGN_CREATE/ASSET_CREATE (which unmounts this
                      // whole KW_APPROVAL block).
                      //
                      // Cancel stays reachable through that window. Without it
                      // there is a gap of up to one poll where the operator has
                      // approved, campaign creation is starting server-side, and
                      // the UI offers no way out.
                      <div className="flex flex-wrap items-center gap-3">
                        <CampaignBuildingLoader
                          variant="inline"
                          stage={
                            approve.isPending
                              ? 'Approving keywords…'
                              : adReviewRequested
                                ? 'Approved — writing ads for your review…'
                                : 'Approved — starting campaign creation…'
                          }
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-destructive/40 text-destructive hover:bg-destructive/5"
                          onClick={handleCancelRun}
                          disabled={cancelRequested}
                        >
                          {cancelRequested ? 'Cancelling…' : 'Cancel run'}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm">
                          {adReviewEnabled ? (
                            <>
                              Ad review is <strong>on</strong>. Approving drafts
                              the ads and stops again for your review — no
                              campaign is created yet.
                            </>
                          ) : (
                            <>
                              Ad review is <strong>off</strong>, so approving
                              creates the campaign in Google Ads now. It launches
                              paused and will not serve until someone enables it.
                            </>
                          )}
                        </p>
                        <div className="flex gap-2">
                        <Button
                          onClick={() => approve.mutate()}
                          disabled={hasBlockingPolicyFailure}
                          className="bg-coral text-white shadow-md hover:bg-coral-hover hover:shadow-lg"
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          {adReviewEnabled
                            ? 'Approve keywords & draft ads'
                            : 'Approve & create campaign in Google Ads'}
                        </Button>
                        {hasBlockingPolicyFailure && (
                          <span className="self-center text-xs text-destructive">
                            Resolve blocking setup checks before approval.
                          </span>
                        )}
                        <Button
                          variant="outline"
                          onClick={() => reject.mutate()}
                          disabled={reject.isPending}
                        >
                          {reject.isPending ? 'Rejecting…' : 'Reject'}
                        </Button>
                      </div>
                      </div>
                    )}
                  </div>
                )}

                {data.state === 'AD_REVIEW' &&
                  (data.run_input?.ad_drafts ? (
                    <RsaEditor
                      drafts={data.run_input.ad_drafts}
                      submitting={submitAds.isPending || submitAds.isSuccess}
                      onSubmit={(adCopy) => submitAds.mutate(adCopy)}
                      onReject={() => reject.mutate()}
                      onRegenerate={handleRegenerateAd}
                    />
                  ) : (
                    <div className="rounded border border-coral/30 bg-coral-light p-3 text-sm text-coral-dark">
                      Waiting for AI-generated ad drafts… this panel refreshes every 5s.
                    </div>
                  ))}

                {data.state === 'CANCELLED' && (
                  <div className="rounded border border-border bg-muted p-3 text-sm text-muted-foreground">
                    <div className="flex items-start gap-2">
                      <X className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <div className="min-w-0">
                        <strong className="text-foreground">Run cancelled</strong>{' '}
                        — no campaign was created. You can adjust the inputs and
                        start a fresh run with Edit &amp; Retry above.
                      </div>
                    </div>
                  </div>
                )}

                {data.state === 'DONE' && (
                  <div className={`rounded border p-3 text-sm ${
                    dryRunResource
                      ? 'border-coral/30 bg-coral-light text-coral-dark'
                      : 'border-teal/30 bg-teal-light text-teal-dark'
                  }`}>
                    <div className="flex items-start gap-2">
                      <CheckCircle2
                        className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                          dryRunResource ? 'text-coral' : 'text-teal-dark'
                        }`}
                      />
                      <div className="min-w-0">
                        <strong>
                          {dryRunResource ? 'Dry-run completed.' : 'Campaign created.'}
                        </strong>{' '}
                        {campaignNames.length > 0 ? (
                          <>
                            Campaign:{' '}
                            <span className="font-medium">
                              {campaignNames.join(', ')}
                            </span>.{' '}
                          </>
                        ) : null}
                        {data.campaign_resource_name ? (
                          <>Resource: <code>{data.campaign_resource_name}</code></>
                        ) : null}
                        {dryRunResource ? (
                          <div className="mt-1 text-xs">
                            Negative resource IDs mean Google Ads dry-run mode returned simulated resources.
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {isRealCampaignResource(data.campaign_resource_name) &&
                      googleAdsCampaignUrl(data.campaign_resource_name) ? (
                        <Button
                          size="sm"
                          asChild
                          className="bg-teal-dark text-white shadow-sm hover:bg-teal-hover"
                        >
                          <a
                            href={
                              googleAdsCampaignUrl(data.campaign_resource_name) ?? '#'
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="mr-2 h-4 w-4" />
                            View in Google Ads
                          </a>
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => setActivePanelTab('readback')}
                          className="bg-teal-dark text-white shadow-sm hover:bg-teal-hover"
                        >
                          View build details
                        </Button>
                      )}
                      {onOpenManagement ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            onOpenManagement(data.campaign_resource_name ?? null)
                          }
                          className="bg-card"
                        >
                          Open Ads Management
                        </Button>
                      ) : null}
                    </div>
                    {!isRealCampaignResource(data.campaign_resource_name) ? (
                      <div className="mt-2 text-xs text-muted-foreground">
                        Dry run — no campaign was created in Google Ads.
                      </div>
                    ) : null}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="readback" className="mt-4">
                <GoogleAdsReadbackPanel
                  readback={googleAdsReadback}
                  isRefreshing={isReadbackRefreshing}
                  onRefresh={() => refreshReadbackMutate({ silent: false })}
                />
              </TabsContent>

              <TabsContent value="audit" className="mt-4">
                <CampaignAuditEvidencePanel
                  runId={runId}
                  tenantId={tenantId}
                  enabled={data.state === 'DONE'}
                />
              </TabsContent>

              <TabsContent value="raw" className="mt-4">
                <pre className="max-h-64 overflow-auto rounded bg-warm-bg p-2 text-xs">
                  {JSON.stringify(data, null, 2)}
                </pre>
              </TabsContent>
            </Tabs>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Selected pages that produced no keyword cluster.
 *
 * ⛔ REPORTED BY JACOB, 2026-08-20: he selected two pages, got one cluster, and
 * reasonably asked whether that was right. It was — both his keywords were
 * service keywords, so /contact matched nothing — but the gate said only
 * "1 keyword cluster(s) ready for review" and left him to work out which of his
 * two selections had silently produced nothing.
 *
 * A selection that quietly does nothing is the same defect shape as a monitor
 * that quietly watches nothing: the screen looks like a smaller success rather
 * than a partial one. So name the pages that dropped out, and say why.
 */
export function unusedSelectedPages(
  runInput: Record<string, unknown> | null | undefined,
  clusters: CampaignRunCluster[] | null | undefined,
): string[] {
  const rawPages = (runInput ?? {})['pages'];
  if (!Array.isArray(rawPages)) return [];
  const normalise = (value: unknown) =>
    String(value ?? '')
      .trim()
      .replace(/\/+$/, '')
      .toLowerCase();
  const covered = new Set(
    (clusters ?? [])
      .map((cluster) => normalise(cluster.landing_page))
      .filter(Boolean),
  );
  const seen = new Set<string>();
  const out: string[] = [];
  for (const page of rawPages) {
    const key = normalise(page);
    // An empty entry is not a page the operator chose, and a duplicate must not
    // be reported twice.
    if (!key || covered.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(String(page).trim());
  }
  return out;
}

function UnusedPagesNotice({ pages }: { pages: string[] }) {
  if (pages.length === 0) return null;
  return (
    <div className="rounded border border-border bg-warm-bg p-3 text-sm">
      <div className="font-medium">
        {pages.length === 1
          ? '1 page you selected produced no keyword cluster'
          : `${pages.length} pages you selected produced no keyword cluster`}
      </div>
      <ul className="mt-1 list-disc pl-5 text-muted-foreground">
        {pages.map((page) => (
          <li key={page} className="break-all">
            {page}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-muted-foreground">
        No ad group will be created for {pages.length === 1 ? 'it' : 'them'}.
        None of the keywords in this run matched, which usually means the
        keywords describe a different service, or keyword expansion was off and
        the keywords you supplied only covered the other page(s).
      </p>
    </div>
  );
}

function ClustersList({
  clusters,
}: {
  clusters: CampaignRunCluster[];
}) {
  return (
    <div className="space-y-2">
      {clusters.map((cluster, idx) => {
        const keywords = normaliseCampaignClusterKeywords(cluster.keywords);
        return (
          <Collapsible
            defaultOpen
            key={`${cluster.cluster_name ?? cluster.landing_page ?? idx}`}
            className="rounded border border-border bg-warm-bg p-3 text-sm"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="group flex items-center gap-1.5 text-left font-medium"
                  >
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=closed]:-rotate-90" />
                    {cluster.cluster_name || `Cluster ${idx + 1}`}
                  </button>
                </CollapsibleTrigger>
                {cluster.landing_page ? (
                  <a
                    href={cluster.landing_page}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-0.5 inline-flex items-center gap-1 pl-[22px] text-xs text-teal hover:underline"
                  >
                    {cluster.landing_page}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
              </div>
              <Badge variant="outline" className="bg-card text-[10px]">
                {keywords.length} keyword{keywords.length === 1 ? '' : 's'}
              </Badge>
            </div>
            {keywords.length > 0 && (
              <CollapsibleContent className="mt-2 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="h-7 text-xs">Keyword</TableHead>
                      <TableHead className="h-7 w-24 text-right text-xs">CPC</TableHead>
                      <TableHead className="h-7 w-24 text-right text-xs">Volume</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {keywords.map((keyword) => (
                      <TableRow key={keyword.keyword}>
                        <TableCell className="py-1.5 text-xs">
                          {keyword.keyword}
                        </TableCell>
                        <TableCell className="py-1.5 text-right text-xs tabular-nums">
                          {typeof keyword.cpc === 'number'
                            ? `$${keyword.cpc.toFixed(2)}`
                            : '-'}
                        </TableCell>
                        <TableCell className="py-1.5 text-right text-xs tabular-nums">
                          {typeof keyword.volume === 'number'
                            ? keyword.volume.toLocaleString()
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CollapsibleContent>
            )}
          </Collapsible>
        );
      })}
    </div>
  );
}

function normaliseCampaignClusterKeywords(
  keywords: CampaignRunCluster['keywords'],
): CampaignRunKeyword[] {
  return (keywords ?? [])
    .map((item) => {
      if (typeof item === 'string') {
        return { keyword: item };
      }
      return {
        keyword: item.keyword,
        cpc: item.cpc,
        volume: item.volume,
      };
    })
    .filter((item) => Boolean(item.keyword?.trim()));
}

// CORE-K-01: `refresh` is the Cluster Refresh subtab — the standalone
// /ads-tools/cluster-refresh route redirects to this subtab (see
// App.tsx). The old ClusterRefreshTab component lives on unchanged;
// only its mount point moved.
type KeywordResearchSubTab =
  | 'research'
  | 'sponsored'
  | 'clusters'
  | 'negatives'
  | 'refresh'
  | 'history';

function normalizeKeywordResearchSubTab(value: string | null): KeywordResearchSubTab {
  if (value === 'history') return 'history';
  if (value === 'sponsored') return 'sponsored';
  if (value === 'clusters') return 'clusters';
  if (value === 'negatives') return 'negatives';
  if (value === 'refresh') return 'refresh';
  return 'research';
}

function AdsKeywordResearchTab({ tenantId }: { tenantId: string }) {
  const { data: brandBible } = useBrandBible(tenantId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [companyInfoOverride, setCompanyInfoOverride] = useState<string | null>(null);
  const [pagesOverride, setPagesOverride] = useState<string[] | null>(null);
  const leavingHistoryRef = useRef(false);

  const urlResearchTab = normalizeKeywordResearchSubTab(
    searchParams.get('research_tab'),
  );
  const [activeResearchTab, setActiveResearchTab] = useState<KeywordResearchSubTab>(
    urlResearchTab,
  );
  const selectedRunId = searchParams.get('run');

  useEffect(() => {
    setActiveResearchTab(urlResearchTab);
    if (urlResearchTab === 'history') {
      leavingHistoryRef.current = false;
    }
  }, [urlResearchTab]);

  const setResearchParams = useCallback(
    (tab: KeywordResearchSubTab, runId?: string | null) => {
      setActiveResearchTab(tab);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('platform', ACTIVE_ADS_PLATFORM);
        if (tab === 'history') {
          next.set('research_tab', 'history');
          if (runId) {
            next.set('run', runId);
          } else {
            next.delete('run');
          }
        } else if (
          tab === 'clusters'
          || tab === 'negatives'
          || tab === 'sponsored'
          || tab === 'refresh'
        ) {
          next.set('research_tab', tab);
          next.delete('run');
        } else {
          next.delete('research_tab');
          next.delete('run');
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const handleRerun = useCallback(
    (ctx: { company_information: string; pages: string[] }) => {
      leavingHistoryRef.current = true;
      setCompanyInfoOverride(ctx.company_information);
      setPagesOverride(ctx.pages);
      setResearchParams('research');
    },
    [setResearchParams],
  );
  const handleNewResearch = useCallback(() => {
    leavingHistoryRef.current = true;
    setCompanyInfoOverride(null);
    setPagesOverride(null);
    setResearchParams('research');
  }, [setResearchParams]);
  const handleSelectedRunChange = useCallback(
    (runId: string | null) => {
      if (leavingHistoryRef.current) return;
      const currentTab = normalizeKeywordResearchSubTab(
        new URLSearchParams(window.location.search).get('research_tab'),
      );
      if (currentTab !== 'history' && activeResearchTab !== 'history') return;
      setResearchParams('history', runId);
    },
    [activeResearchTab, setResearchParams],
  );

  const defaultCompanyInformation =
    companyInfoOverride
    ?? brandBible?.keyword_research_brief
    ?? brandBible?.description
    ?? '';
  const workspaceLocations = [
    ...(brandBible?.service_locations ?? []),
    ...Object.values(brandBible?.locations ?? {}).flat(),
  ].filter((location): location is string => Boolean(location?.trim()));
  // CORE-K-09: only `service_locations` is writable from the picker's
  // modal; `locations` (city -> areas) stays edited in Brand Bible.
  const editableWorkspaceLocations = (brandBible?.service_locations ?? [])
    .map((location) => location?.trim())
    .filter((location): location is string => Boolean(location));

  return (
    <div className="space-y-4">
      <Tabs
        value={activeResearchTab}
        onValueChange={(value) => {
          const nextTab = normalizeKeywordResearchSubTab(value);
          leavingHistoryRef.current = nextTab !== 'history';
          setResearchParams(nextTab);
        }}
      >
        <TabsList>
          <TabsTrigger value="research">Run New Research</TabsTrigger>
          <TabsTrigger value="sponsored">Sponsored Ads</TabsTrigger>
          <TabsTrigger value="clusters">Ad Group Clusters</TabsTrigger>
          <TabsTrigger value="negatives">Negative Keywords</TabsTrigger>
          {/* CORE-K-01: Cluster Refresh absorbed from the retired
              /ads-tools/cluster-refresh nav entry. The old standalone
              route redirects here (?research_tab=refresh). */}
          <TabsTrigger value="refresh">Cluster Refresh</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="research" className="mt-4">
          <AdCampaignKeywordsPanel
            defaultCompanyInformation={defaultCompanyInformation}
            defaultPages={pagesOverride ?? []}
            workspaceName={brandBible?.business_name ?? tenantId}
            workspaceServices={brandBible?.services_offered ?? []}
            workspaceLocations={workspaceLocations}
            editableWorkspaceLocations={editableWorkspaceLocations}
            hideClusterResults
            defaultBriefGeneratedAt={
              companyInfoOverride
                ? null
                : (brandBible?.keyword_research_brief_generated_at ?? null)
            }
          />
        </TabsContent>

        <TabsContent value="sponsored" className="mt-4">
          {activeResearchTab === 'sponsored' && <SponsoredAdsLookupPanel />}
        </TabsContent>

        <TabsContent value="clusters" className="mt-4">
          {activeResearchTab === 'clusters' && (
            <AdCampaignClusterResultsPanel
              workspaceName={brandBible?.business_name ?? tenantId}
            />
          )}
        </TabsContent>

        <TabsContent value="negatives" className="mt-4">
          {activeResearchTab === 'negatives' && (
            <AdCampaignNegativeKeywordsPanel
              workspaceName={brandBible?.business_name ?? tenantId}
            />
          )}
        </TabsContent>

        <TabsContent value="refresh" className="mt-4">
          {activeResearchTab === 'refresh' && (
            <ClusterRefreshTab tenantId={tenantId} />
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {activeResearchTab === 'history' && (
            <KeywordResearchHistoryPanel
              tenantId={tenantId}
              onRerun={handleRerun}
              onNewResearch={handleNewResearch}
              initialSelectedRunId={selectedRunId}
              onSelectedRunChange={handleSelectedRunChange}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const SECTION_COPY = {
  'keyword-research': {
    title: 'Keyword Research',
    description: 'Research Google Ads keywords and review past research runs',
  },
  management: {
    title: 'Management',
    // ⚠ The description names the difference from Weekly Management, because
    // two sections with near-identical names is exactly where an operator
    // guesses wrong. This one is live account state, on demand. Weekly
    // Management is a scheduled run that produces findings on a cadence.
    description:
      'Live account state: ad health, conversion actions and change history, '
      + 'read on demand',
  },
  campaigns: {
    title: 'Campaigns',
    description: 'Build campaigns, review approvals, and inspect live campaign status',
  },
  'campaign-qa': {
    title: 'Campaign QA',
    description: 'Validate campaign QA CSV files before operator review',
  },
  'account-audit': {
    title: 'Account Audit',
    description: 'Check setup and connection readiness before anything is written',
  },
  // CORE-D-01: renamed from "Weekly Management". The slug stays
  // `weekly-management` for one release so any cached tenant that keeps its
  // section in URL history still resolves; the `client-dashboard` slug is
  // the canonical spelling going forward.
  'weekly-management': {
    title: 'Client Dashboard',
    description: 'All-client overview: pacing, approvals, weekly run findings',
  },
  'client-dashboard': {
    title: 'Client Dashboard',
    description: 'All-client overview: pacing, approvals, weekly run findings',
  },
  'campaign-settings': {
    title: 'Campaign Settings',
    description: 'Locations, ad schedule and extensions per campaign',
  },
  'search-terms': {
    title: 'Search Terms & Negatives',
    description: 'Review search terms and approve negative keyword exclusions',
  },
  // CORE-PF-01 / PF-02: `monthly-recaps` and `kpi-diagnostics` are
  // historical slugs — both now render the Performance page. The nav
  // surfaces only "Performance". App.tsx also redirects direct URL hits
  // for the two old paths, so these SECTION_COPY entries only fire when
  // an internal `?tab=...` alias survived the rename. Descriptions match
  // the current-name copy so a reader hitting a historical link sees
  // the same page identity as everyone else, without any operator-
  // visible ticket-ID noise.
  // CORE-G-2.b: the G-2 purpose sentence lives here, on the one outer
  // header; PerformancePage no longer renders its own. The wrapper appends
  // " for <tenant>." so these must NOT end with a period, and must read
  // cleanly before "for <tenant>" (no "…for both").
  'monthly-recaps': {
    title: 'Performance',
    description:
      'Where the account stands right now, and the monthly recap the client sees',
  },
  'kpi-diagnostics': {
    title: 'Performance',
    description:
      'Where the account stands right now, and the monthly recap the client sees',
  },
  performance: {
    title: 'Performance',
    description:
      'Where the account stands right now, and the monthly recap the client sees',
  },
  budgets: {
    title: 'Budget Pacing',
    description: 'Review budget pacing and approve budget reallocations',
  },
  // CORE-K-01: Cluster Refresh folds into Keyword Research as a subtab.
  // The App-level redirect turns /ads-tools/cluster-refresh into
  // /ads-tools/keyword-research?research_tab=refresh, so this slug is
  // no longer reached under normal navigation. Kept as a fallback so a
  // stale in-app link (or a manual URL edit that skips the redirect)
  // still renders correct copy at the top.
  'cluster-refresh': {
    title: 'Keyword Research',
    description: 'Cluster Refresh is now a Keyword Research subtab',
  },
  competitive: {
    title: 'Competitive Insights',
    description: 'Inspect auction insights and competitive diagnostics',
  },
  // CORE-P-02 keeps `placement-review` as a slug so historical URLs still
  // resolve; the render branch below routes it to PmaxPage now that the
  // Placement Review surface is absorbed there. The nav no longer surfaces
  // Placement Review as a standalone entry.
  'placement-review': {
    title: 'Performance Max',
    description: 'Placements moved into Performance Max',
  },
  pmax: {
    title: 'Performance Max',
    // CORE-P-01.c: the G-2 purpose sentence lives here, on the one outer
    // header; PmaxPage no longer renders its own. The wrapper appends
    // " for <tenant>." so this must NOT end with a period.
    description:
      'Performance Max is its own animal: one blended cost across Search, Display, YouTube, Discover and Maps, so it needs its own tab, not a row inside Search',
  },
  reports: {
    title: 'Reports (Read-Only)',
    description:
      'Launch-layer reports: escape-hatch tasks, asset coverage, ad destinations, rule firings, recommendation snapshots, monitor history',
  },
} as const;

// Panel ships dark: VITE_ADS_REPORTS_PANEL=true reveals the Reports section.
// (Direct env read — this branch predates the FEATURE_FLAGS registry on main.)
const ADS_REPORTS_PANEL_ENABLED = import.meta.env.VITE_ADS_REPORTS_PANEL === 'true';

type AdsToolsSection = keyof typeof SECTION_COPY;

function normalizeSection(value: string | undefined): AdsToolsSection | null {
  return value && value in SECTION_COPY ? (value as AdsToolsSection) : null;
}

// Sections that have been hardened for the client-facing lens. In client view
// mode these render as intended; every other section still shows operator
// content, so we surface an honest banner rather than pretend otherwise.
const CLIENT_HARDENED_SECTIONS: ReadonlySet<AdsToolsSection> = new Set([
  'kpi-diagnostics',
  'monthly-recaps',
]);

/**
 * Weekly Management, split into two screens (IA fix):
 *  - FLEET   — operator landing, tenant-independent: dashboards + the cross-client
 *              board. Clicking a client selects it and drills into the client view.
 *  - CLIENT  — single-tenant: mode, KPI goals, manual trigger, recent runs,
 *              approvals for the selected client only. No cross-client table.
 * Resolves the confusion of a cross-client table living on a tenant-scoped page.
 */
function WeeklyManagementSection({ tenantId }: { tenantId: string | null }) {
  const [view, setView] = useState<'fleet' | 'client'>('fleet');

  const drillInto = useCallback((domain: string) => {
    // Set the app-wide tenant (also updates the switcher), then show the client view.
    useWorkspaceStore.getState().selectTenantByWebsite(domain);
    setView('client');
  }, []);

  if (view === 'fleet') {
    return (
      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">Fleet — all enrolled clients</span>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">operator</span>
          </div>
          <div className="text-xs text-muted-foreground">
            Cross-client triage, sorted by $ at stake. Click a client to drill into its Client Dashboard.
          </div>
        </div>
        {/* CORE-D-04: any client projected to underspend the cycle surfaces
            here as well as on M-08. Same signal, same cached read as the
            fleet board below (one network trip). */}
        <UnderspendFlagBanner onDrillInto={drillInto} />
        <WeeklyFleetBoard onSelectClient={drillInto} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <button
          onClick={() => setView('fleet')}
          className="font-medium text-coral hover:underline"
        >
          ‹ Fleet
        </button>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">{tenantId ?? 'no client selected'}</span>
      </div>
      {tenantId ? (
        <>
          <WeeklySweepModeControl tenantId={tenantId} />
          <KpiTargetsCard tenantId={tenantId} />
          <ManualTriggerPanel tenantId={tenantId} />
          <RecentRunsPanel tenantId={tenantId} />
          <ApprovalQueuePanel tenantId={tenantId} />
        </>
      ) : (
        <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No client selected. Go back to the Fleet and pick a client to manage.
        </div>
      )}
    </div>
  );
}

export default function GoogleAdsOpsPage() {
  const tenantId = useTenantStore((s) => s.currentTenant);
  const viewMode = useAdsViewStore((s) => s.viewMode);
  const { section: sectionParam } = useParams();
  const section = normalizeSection(sectionParam);
  // CORE-M-01: URL-persisted selected tab for the Management category bar.
  // Read here even on non-management sections so the hook call site is stable
  // (React rules-of-hooks). The value is only rendered when `section === 'management'`.
  const [managementTab] = useManagementTabFromUrl();

  // Does the active client have Google Ads mapped? The ONE capability model
  // decides (googleAdsGate.ts); while it loads nothing mounts, and if its fetch
  // fails the workspace list flag (has_google_ads, by website) decides.
  const { capabilities, isLoading, isError } = useClientCapabilities(tenantId);
  const listFlag = useWorkspaceStore((s) =>
    s.workspaces.find((w) => w.website?.trim().toLowerCase() === tenantId?.trim().toLowerCase())
      ?.has_google_ads,
  );

  if (!section) {
    return <Navigate to="/ads-tools/keyword-research?platform=google-ads" replace />;
  }

  const sectionCopy = SECTION_COPY[section];

  if (!tenantId) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>No workspace selected</CardTitle>
            <CardDescription>
              Pick a workspace from the top bar to view {sectionCopy.title}.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // The picker lets any client be selected, so a client without Google Ads can
  // be active here. Gated sections show a message (and the read-only MCC check)
  // instead of failing with raw errors; exempt sections (googleAdsGate.ts) render.
  const gate = resolveGoogleAdsGate({ section, capabilities, isLoading, isError, listFlag });
  if (gate !== 'open') {
    return (
      <div className="p-6">
        {gate === 'checking' ? (
          <GoogleAdsCheckingState />
        ) : (
          <NoGoogleAdsAccountState tenantId={tenantId} sectionTitle={sectionCopy.title} />
        )}
      </div>
    );
  }

  const clientView = viewMode === 'client';
  const sectionHardened = CLIENT_HARDENED_SECTIONS.has(section);

  return (
    // key={tenantId}: remount the whole section on a workspace/tenant switch so
    // tab-local state (selected report, applied keys, filters, date windows) is
    // reset and every query refetches for the new client — otherwise switching
    // workspace left stale contents until a manual page refresh.
    <div key={tenantId} className="space-y-6 p-6">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold">{sectionCopy.title}</h1>
          {clientView ? (
            <Badge
              variant="outline"
              className="bg-teal-light/40 text-teal-dark border-teal/40"
            >
              Client view
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="bg-coral-light/40 text-coral-dark border-coral/40"
            >
              Operator only
            </Badge>
          )}
        </div>
        {/* CORE-G-3 (partial): both branches of this one shared header used
            to append client-entitlement framing — a sentence naming what
            clients are allowed to see, and on the other branch a note that
            this was the client's own view. Because every section of this
            page renders through here, that one sentence appeared on ~10 Ads
            pages at once. Client view is already announced by its own banner
            below, so the framing was redundant as well as client-facing.
            Deletion, not paraphrase, per the ticket; the exact strings are
            deliberately not repeated here, because the absence test in
            features/ads/__tests__/noClientFacingCopy.test.ts greps the
            source and a comment would keep it red. The rest of G-3 (the
            view-as mechanism, entitlement tiers, the orphaned client
            performance page) still waits on R-5. */}
        <p className="text-sm text-muted-foreground">
          {sectionCopy.description} for <strong>{tenantId}</strong>.
        </p>
      </div>

      {clientView && !sectionHardened && (
        <div className="flex items-start gap-2 rounded-md border border-amber/40 bg-amber-light/30 px-3 py-2 text-xs font-medium text-amber-dark">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber" />
          <span>
            Client view preview — this page still shows operator content and is
            not yet client-hardened.
          </span>
        </div>
      )}

      {section === 'reports' &&
        (ADS_REPORTS_PANEL_ENABLED ? (
          <AdsReportsTab tenantId={tenantId} />
        ) : (
          <p className="text-sm text-muted-foreground">
            The Reports panel is not enabled in this environment (VITE_ADS_REPORTS_PANEL).
          </p>
        ))}

      {section === 'keyword-research' && (
        <AdsKeywordResearchTab tenantId={tenantId} />
      )}

      {section === 'campaigns' && <CampaignsTab tenantId={tenantId} />}

      {section === 'campaign-qa' && <CampaignQaTab tenantId={tenantId} />}

      {section === 'account-audit' && <AccountAuditTab tenantId={tenantId} />}

      {section === 'management' && (
        <div className="space-y-4">
          {/* CORE-M-01: the category bar is the front door for every operator's
              day (Chans's spec §2.1). Ships with empty tab shells; CORE-M-02..
              CORE-M-08 fill the seven bodies in follow-up tickets. The legacy
              Management surface is kept mounted below the bar during the
              migration so operators do not lose the tool. */}
          {/* Gap A phase 2c: the account caveats (verdict line, write actions
              off, Performance Max not covered) render ONCE above the bar.
              They are true whichever tab is open, so they are not a tab body
              and not interleaved with the tools; under the bar is the tab
              body, the drill-down and change history. */}
          <ManagementAccountCaveats tenantId={tenantId} />
          <ManagementCategoryBar tenantId={tenantId} />
          <ManagementTabBody slug={managementTab} tenantId={tenantId} />
          {/* CORE-M-11 phase A: campaign structure drill-down. Sits below
              the category-tab body so operators clear the alerts above
              first, then skim the structure with full context (Chans's
              §2.11 workflow). Collapsed by default. Q-2 ruling: Current
              Campaigns folds in here, not its own left-nav tab. */}
          <CampaignDrilldown tenantId={tenantId} />
          {/* Gap A: the "Current tools (migrating into the tabs above…)"
              header and the migration advisory banner are both deleted. That
              banner rendered an internal ticket table (Block / Moves to /
              Ticket / Status, with rows naming our own tickets) to whoever
              opened the page. An operator cannot act on our migration
              schedule, and it under-reported anyway: it marked Ad Health
              "deleted" while all six of those tiles were still on the screen
              below it. The component name is deliberately not written here,
              because the guard in
              features/ads/management/__tests__/useManagementWindow.test.tsx
              greps this file for it. */}
          <div className="space-y-4 border-t border-border pt-4">
            {/* P5: the budget-push approval queue — a GLOBAL operator
                worklist (one row per contract+cycle), so it renders above
                the per-tenant management tools regardless of the selected
                workspace. */}
            <BudgetPushQueueCard />
            <AdsManagementTab tenantId={tenantId} />
          </div>
        </div>
      )}

      {(section === 'weekly-management' || section === 'client-dashboard') && (
        <WeeklyManagementSection tenantId={tenantId} />
      )}

      {section === 'search-terms' && (
        <SearchTermsNegativesTab tenantId={tenantId} />
      )}

      {/* CORE-PF-01 / PF-02: Performance page = KPI diagnostics + collapsible
          Monthly Recaps. Historical `monthly-recaps` and `kpi-diagnostics`
          slugs render the same page so bookmarks resolve without loss. */}
      {(section === 'performance'
        || section === 'kpi-diagnostics'
        || section === 'monthly-recaps') && (
        <PerformancePage tenantId={tenantId} />
      )}

      {section === 'budgets' && <BudgetReallocationTab tenantId={tenantId} />}

      {/* CORE-K-01: `cluster-refresh` no longer resolves to a standalone
          section here — the App-level redirect (App.tsx) sends the URL
          to /ads-tools/keyword-research?research_tab=refresh, which
          renders the ClusterRefreshTab inside the Keyword Research
          subtabs above. This branch used to render it directly. */}

      {section === 'competitive' && <CompetitiveTab tenantId={tenantId} />}

      {/* CORE-P-02: Placement Review lives inside Performance Max now.
          Old URLs pointing at /ads-tools/placement-review resolve to the
          same page a `pmax` slug renders. */}
      {(section === 'pmax' || section === 'placement-review') && (
        <PmaxPage tenantId={tenantId} />
      )}

      {section === 'campaign-settings' && (
        <CampaignSettingsPage tenantId={tenantId} />
      )}

      {(section === 'weekly-management' || section === 'client-dashboard') && (
        <div className="rounded border border-border bg-warm-bg p-3 text-xs text-muted-foreground">
          <RotateCcw className="inline h-3 w-3 mr-1" />
          Client Dashboard tables auto-refresh every 15s. The cron normally
          fires Monday 06:00 local. A client is swept only when their{' '}
          <code>weekly_mode</code> is set — leaving it <code>off</code> is what
          keeps a new account out.
        </div>
      )}
    </div>
  );
}
